import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import JSZip from "jszip";
import AudioStatusPill from "./AudioStatusPill";
import EchoBottomSheet from "./EchoBottomSheet";
import {
  ENGLISH_VOICE_TARGETS,
  FRENCH_VOICE_TARGETS,
  CHINESE_VOICE_TARGETS,
  buildVoiceOptions,
  getPreferredVoiceForLanguage
} from "./echoAudioProfiles";
import {
  createChapterAudioCacheKey,
  fetchEchoAudioConfig,
  requestChapterAudio,
  exportChapterAudio
} from "./echoAudioApi";
import { exportTranslateBlock, getLanguageLabel as getExportLanguageLabel } from "./echoExportTranslation";
import {
  SUPPORTED_CONVERSION_SOURCE_FORMATS,
  SUPPORTED_CONVERSION_TARGET_FORMATS,
  convertEchoDocument,
  saveConvertedEchoDocument
} from "./echoDocumentConversion";
import {
  TRANSLATION_PLAYBACK_MODES,
  buildPlaybackBlocks,
  getTranslationStatusForBlock,
  getTranslatedBlockForPlayback,
  mapOffsetAcrossTexts,
  normalizeTranslationPlaybackMode,
  warmTranslationCache
} from "./echoLocalTranslation";
import { importEchoDocument } from "./echoDocumentApi";
import { usePlaybackRecovery } from "./usePlaybackRecovery";
import { useReaderPanels } from "./useReaderPanels";
import { useReadingWindowState } from "./useReadingWindowState";
import { useSleepTimer } from "./useSleepTimer";
import speechFallbackHelpers from "./echoSpeechFallback";
import narrationCleanupHelpers from "./echoNarrationCleanup";
import UserDeclaration from "./UserDeclaration";

const {
  buildSpeechChunks,
  findSpeechChunkPosition,
  getFallbackContinuationDelayMs,
  mapSpeechChunkCharToCursor,
  resolveBrowserSpeechConfig,
  shouldSuppressAiAudioForSession
} = speechFallbackHelpers;
const {
  normalizeNarrationText,
  isSkippableStructuralText,
  sanitizeNarrationSourceText,
  shouldSkipMarkupNode
} = narrationCleanupHelpers;

const STORAGE_KEY = "wonderworld_reader_lab_state_v2";
const SAVED_BOOKS_KEY = "wonderworld_reader_lab_saved_books_v1";
const VOICE_PREFERENCES_KEY = "wonderworld_reader_lab_voice_preferences_v1";
const PERSONAL_API_KEY_SESSION_KEY = "wonderworld_reader_lab_openai_key_session_v1";
const USER_DECLARATION_ACCEPTED_KEY = "wonderworld_reader_lab_user_declaration_v1";
const DEV_DIAGNOSTICS_FLAG_KEY = "echo_debug";
const DEV_DIAGNOSTICS_QUERY_PARAM = "echoDebug";
const MAX_SAVED_BOOKS = 6;
const SHELF_VISIBLE_COUNT = 2;
const MAX_COVER_DATA_URL_LENGTH = 220000;
const RATE_OPTIONS = [0.5, 1.0, 1.2, 1.5];
const TIMER_OPTIONS = [10, 30, 60];
const PREFERRED_READING_LANGUAGES = {
  ZH: "zh",
  EN: "en"
};
const BACKGROUND_PREFETCH_AHEAD = 2;
const NEAR_END_PREFETCH_SECONDS = 45;
const RECOVERY_RETRY_DELAYS_MS = [180, 800, 1800];
const PLAYBACK_SNAPSHOT_PERSIST_INTERVAL_MS = 2500;
const PLAYBACK_SNAPSHOT_PERSIST_DELTA_SECONDS = 1.5;
const PLAYBACK_SNAPSHOT_VERSION = 2;
const PLAYBACK_SNAPSHOT_MAX_AGE_MS = 1000 * 60 * 60 * 12;
const SNAPSHOT_AUDIO_DRIFT_TOLERANCE_SECONDS = 1.2;
const CHAPTER_CACHE_KEEP_BEHIND = 1;
const CHAPTER_CACHE_KEEP_AHEAD = BACKGROUND_PREFETCH_AHEAD + 1;
const IMPORTED_DOCUMENT_CHAPTER_TARGET_CHARS = 8500;
const IMPORTED_DOCUMENT_CHAPTER_MAX_BLOCKS = 16;
const APPROX_LOCAL_STORAGE_BUDGET_BYTES = 5 * 1024 * 1024;
const DRAG_THRESHOLD_PX = 6;
const VIEWPORT_EDGE_GAP_PX = 12;
const READING_WINDOW_DRAG_LIMIT_PX = 72;
const READING_WINDOW_AUTO_SHIFT_PX = 28;
const READING_WINDOW_AUTO_RESET_DELAY_MS = 220;
const IMPORTABLE_FILE_ACCEPT =
  ".txt,.text,.md,.log,.epub,.mobi,.docx,.doc,.pdf,text/plain,text/markdown,application/epub+zip,application/x-mobipocket-ebook,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const FREE_BROWSER_SPEECH_ONLY_MODE = true;
const BROWSER_SPEECH_LOOKAHEAD_COUNT = 3;
const BROWSER_SPEECH_CHUNK_OPTIONS = {
  minChars: 360,
  targetChars: 860,
  maxChars: 1360,
  maxBlocks: 5
};
const TRANSLATED_BROWSER_SPEECH_CHUNK_OPTIONS = {
  minChars: 920,
  targetChars: 1760,
  maxChars: 2800,
  maxBlocks: 10
};
const TRANSLATED_BROWSER_SPEECH_AWAIT_LOOKAHEAD_COUNT = 2;
const TRANSLATED_BROWSER_SPEECH_BACKGROUND_LOOKAHEAD_COUNT = 5;

function getClosestRateOptionIndex(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || !RATE_OPTIONS.length) {
    return 0;
  }

  let closestIndex = 0;
  let closestDistance = Math.abs(RATE_OPTIONS[0] - numericValue);

  for (let index = 1; index < RATE_OPTIONS.length; index += 1) {
    const distance = Math.abs(RATE_OPTIONS[index] - numericValue);

    if (distance < closestDistance) {
      closestIndex = index;
      closestDistance = distance;
    }
  }

  return closestIndex;
}

function getRateOptionIndex(value) {
  const exactIndex = RATE_OPTIONS.findIndex((option) => option === value);
  return exactIndex >= 0 ? exactIndex : getClosestRateOptionIndex(value);
}

function formatClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }

  const wholeSeconds = Math.floor(seconds);
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const remainder = wholeSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
  }

  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function formatExcerpt(text, maxLength = 220) {
  if (typeof text !== "string") {
    return "";
  }

  const normalized = text.replace(/\s+/g, " ").trim();

  if (!normalized) {
    return "";
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength).trim()}...`;
}

function formatReadingWindowText(text) {
  if (typeof text !== "string") {
    return "";
  }

  return text.replace(/\s+/g, " ").trim();
}

const READING_WINDOW_HARD_BOUNDARY_PATTERN = /[.!?。！？\n]/;
const READING_WINDOW_SOFT_BOUNDARY_PATTERN = /[,;:，；：、]/;
const READING_WINDOW_OFFSET_UPDATE_MIN_DELTA = 8;
const SHOW_BROWSER_SPEECH_RESUME_PROMPT = false;

function trimRangeToContent(text, start, end) {
  let nextStart = Math.max(0, Number(start) || 0);
  let nextEnd = Math.min(text.length, Number(end) || 0);

  while (nextStart < nextEnd && /\s/.test(text[nextStart])) {
    nextStart += 1;
  }

  while (nextEnd > nextStart && /\s/.test(text[nextEnd - 1])) {
    nextEnd -= 1;
  }

  return {
    start: nextStart,
    end: nextEnd
  };
}

function findReadingWindowHighlightRange(text, offsetChars = 0) {
  if (typeof text !== "string" || !text.trim()) {
    return null;
  }

  const safeOffset = clampNumber(Number(offsetChars) || 0, 0, Math.max(text.length - 1, 0));
  let start = safeOffset;
  let end = safeOffset;

  while (start > 0 && !READING_WINDOW_HARD_BOUNDARY_PATTERN.test(text[start - 1])) {
    start -= 1;
  }

  while (end < text.length && !READING_WINDOW_HARD_BOUNDARY_PATTERN.test(text[end])) {
    end += 1;
  }

  if (end < text.length) {
    end += 1;
  }

  if (safeOffset - start > 120) {
    let softStart = safeOffset;
    while (softStart > start && !READING_WINDOW_SOFT_BOUNDARY_PATTERN.test(text[softStart - 1])) {
      softStart -= 1;
    }
    start = Math.max(start, softStart);
  }

  if (end - safeOffset > 160) {
    let softEnd = safeOffset;
    while (softEnd < end && !READING_WINDOW_SOFT_BOUNDARY_PATTERN.test(text[softEnd])) {
      softEnd += 1;
    }
    if (softEnd < end) {
      end = softEnd + 1;
    }
  }

  const trimmed = trimRangeToContent(text, start, end);

  if (trimmed.end <= trimmed.start) {
    return null;
  }

  return trimmed;
}

function buildReadingWindowTextSegments(text, offsetChars = 0) {
  if (typeof text !== "string" || !text) {
    return [];
  }

  const range = findReadingWindowHighlightRange(text, offsetChars);

  if (!range) {
    return [
      {
        key: "full",
        text,
        active: false
      }
    ];
  }

  return [
    {
      key: "before",
      text: text.slice(0, range.start),
      active: false
    },
    {
      key: "active",
      text: text.slice(range.start, range.end),
      active: true
    },
    {
      key: "after",
      text: text.slice(range.end),
      active: false
    }
  ].filter((segment) => segment.text);
}

function buildVisibleExcerptContext(blocks, currentIndex) {
  const normalizedBlocks = Array.isArray(blocks) ? blocks : [];
  const safeIndex = clampIndex(Number(currentIndex) || 0, normalizedBlocks.length);
  const previousBlock = safeIndex > 0 ? normalizedBlocks[safeIndex - 1] : null;
  const currentBlock = normalizedBlocks[safeIndex] || null;
  const nextBlock = safeIndex + 1 < normalizedBlocks.length ? normalizedBlocks[safeIndex + 1] : null;

  return {
    previous: formatExcerpt(previousBlock?.text || "", 180),
    current: formatExcerpt(currentBlock?.text || "", 260),
    next: formatExcerpt(nextBlock?.text || "", 180)
  };
}

function normalizePersonalApiKey(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, "")
    .trim();
}

function isLikelyOpenAiApiKey(value) {
  const normalized = normalizePersonalApiKey(value);
  return /^sk-[A-Za-z0-9._-]{20,}$/.test(normalized);
}

function shouldPreferDirectAiAudioStream() {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgent = String(navigator.userAgent || "");
  const platform = String(navigator.platform || "");
  const touchPoints = Number(navigator.maxTouchPoints || 0);

  return (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    /iPhone|iPad|iPod/i.test(platform) ||
    (/Macintosh/i.test(userAgent) && touchPoints > 1)
  );
}

function normalizeEchoAudioStreamUrl(value, expectedCacheKey = "") {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  try {
    const baseOrigin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://echo.local";
    const parsedUrl = new URL(value, baseOrigin);

    if (parsedUrl.pathname !== "/api/echo/chapter") {
      return "";
    }

    const cacheKey = String(parsedUrl.searchParams.get("cacheKey") || "").trim();

    if (!cacheKey) {
      return "";
    }

    if (expectedCacheKey && cacheKey !== expectedCacheKey) {
      return "";
    }

    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch (error) {
    return "";
  }
}

function getInitialPersonalApiKey() {
  if (typeof window === "undefined") {
    return "";
  }

  const storedValue = window.sessionStorage.getItem(PERSONAL_API_KEY_SESSION_KEY) || "";
  return isLikelyOpenAiApiKey(storedValue) ? normalizePersonalApiKey(storedValue) : "";
}

function maskPersonalApiKey(value) {
  const normalized = normalizePersonalApiKey(value);

  if (!normalized) {
    return "";
  }

  if (normalized.length <= 12) {
    return normalized;
  }

  return `${normalized.slice(0, 6)}…${normalized.slice(-4)}`;
}

function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  const tagName = target.tagName?.toLowerCase?.() || "";
  return tagName === "input" || tagName === "textarea" || tagName === "select";
}

const LAB_CONTENT = {
  en: {
    badge: "PageTone",
    title: "A separate page for the iPhone bilingual listening app",
    subtitle:
      "This is the web preview for the listening app now being built alongside Wonder World.",
    heroEyebrow: "Current build focus",
    heroBullets: ["TXT / EPUB / MOBI / DOCX / DOC / PDF import", "Chinese + English voice switching", "Resume where you stopped"],
    sandboxTitle: "Try the reader prototype",
    sandboxIntro:
      "Paste text or import a TXT, EPUB, MOBI, DOCX, DOC, or text-based PDF file, then test bilingual paragraph playback right in the browser.",
    dropzoneTitle: "Upload a file and start reading",
    dropzoneHint: "Drop a TXT, EPUB, MOBI, DOCX, DOC, or text-based PDF file here, or click to choose one.",
    dropzoneActive: "Release to import this file.",
    lastFileLabel: "Last file",
    fileTypeLabel: "Format",
    fileTypeTxt: "TXT",
    fileTypeEpub: "EPUB",
    fileTypeMobi: "MOBI",
    fileTypeDocx: "DOCX",
    fileTypeDoc: "DOC",
    fileTypePdf: "PDF",
    convertTitle: "Convert file",
    convertHint: "Use the current uploaded file and export it as TXT or EPUB.",
    convertSourceLabel: "From",
    convertTargetLabel: "To",
    convertArrowAria: "Convert file",
    convertSupportedTargets:
      "Current conversion MVP: TXT, EPUB, MOBI, DOCX, DOC, and text-based PDF can export to TXT or EPUB.",
    workspaceTitle: "PageTone",
    featureIntroTitle: "What PageTone is for",
    featureIntroBody:
      "Your book, your language. Import, read and listen with real-time translation, then export as an MP3.",
    back_home: "Back Home",
    closePanel: "Close",
    pasteText: "Paste text",
    currentBookTitle: "Continue listening",
    currentBookHint: "Pick up the current book right where you left off.",
    shelfNowPlaying: "Now playing",
    shelfUpNext: "Up next",
    chapterListTitle: "Chapters",
    chapterListHint: "Jump directly to any chapter.",
    chapterPickerOpen: "Choose chapter",
    noChapters: "No chapters loaded yet.",
    shelfTitle: "Saved Books",
    shelfHint: "Recent local library. New imports are kept here automatically.",
    shelfStorageBooks: "Books",
    shelfStorageTotal: "Total",
    shelfStorageUsed: "Used",
    shelfStorageLeft: "Left",
    shelfStorageNote: "Approximate browser-local storage for PageTone on this device.",
    shelfEmpty: "No saved books yet.",
    shelfOpen: "Open",
    shelfDelete: "Delete",
    shelfLimit: "Up to 6 recent books are kept here.",
    shelfPreviousWindow: "Show previous books",
    shelfNextWindow: "Show more books",
    shelfWindowLabel: "{start}-{end} of {total}",
    importedTextTitle: "Imported Text",
    demoChapterTitle: "Demo Chapter",
    chapterLabel: "Chapter",
    previousChapter: "Prev Chapter",
    nextChapter: "Next Chapter",
    noNextChapter: "Last Chapter",
    voiceTuningTitle: "Voice Tuning",
    voiceTuningHint:
      "PageTone is using browser speech in this mode. It works best while the Safari tab stays active, and gives you a clear Resume path when you return.",
    audioSettingsTitle: "Audio settings",
    audioSettingsSubtitle: "Choose voices when you want to tune the browser reading experience.",
    understandInLabel: "Understand in",
    understandInHint: "Choose your preferred reading language. We’ll handle translation automatically.",
    understandInChinese: "中文",
    understandInEnglish: "English",
    pureReadingLabel: "Pure reading",
    pureReadingExit: "Exit pure reading",
    pureReadingHint: "Pause first, then browse paragraph by paragraph with arrow keys or scrolling.",
    pureReadingActiveHint: "Use ↑ ↓ ← → or scroll to move through paragraphs.",
    exportMp3Label: "Export MP3",
    exportMp3OriginalLabel: "Export MP3 (Original)",
    exportMp3ChineseLabel: "Export MP3 (中文)",
    exportEpubOriginalLabel: "Export EPUB",
    exportEpubChineseLabel: "Export EPUB (中文)",
    exportMp3Hint: "Export the current book as one MP3 file.",
    exportMp3ProgressInline: "{done}/{total} chapters",
    exportEpubProgressInline: "{done}/{total} chapters",
    pureReadingPending: "Preparing translation…",
    statusExportingMp3: "Preparing MP3 export…",
    statusExportingMp3Progress: "Exporting… {done}/{total} chapters",
    statusExportingOriginalEpub: "Preparing EPUB export…",
    statusExportingOriginalEpubProgress: "Building EPUB… {done}/{total} chapters",
    statusExportingChineseEpub: "Preparing Chinese EPUB export…",
    statusExportingChineseEpubProgress: "Building Chinese EPUB… {done}/{total} chapters",
    statusExportedMp3: "MP3 export complete.",
    statusExportMp3Cancelled: "MP3 export cancelled.",
    statusExportMp3Failed: "MP3 export failed.",
    statusPureReadingEnabled: "Pure reading mode is on.",
    statusPureReadingDisabled: "Pure reading mode is off.",
    statusPureReadingNeedsPause: "Pause playback first to open pure reading.",
    translationPlaybackTitle: "Translation playback",
    translationPlaybackHint:
      "Quality-first EN ⇄ 中文 mode. PageTone can wait a moment for a cleaner free translation, then keeps warming the next block while reading.",
    translationPlaybackOff: "Original only",
    translationPlaybackAuto: "Auto EN ⇄ 中文",
    translationPlaybackEnglishToChinese: "EN → 中文",
    translationPlaybackChineseToEnglish: "中文 → EN",
    translationPlaybackChipPrefix: "Translate",
    translatedLineLabel: "Translation",
    translationPlaybackNowReading: "Now reading in translation",
    translationPlaybackCurrentLabel: "Current translated line",
    translationPlaybackPending: "Translating…",
    translationPlaybackFallbackNotice:
      "Using the original line for now.",
    audioSettingsShow: "Show",
    audioSettingsHide: "Hide",
    audioKeyCardTitle: "AI audio key",
    audioKeyCardSubtitle:
      "Add your own OpenAI API key if you want PageTone to generate smoother audio in this browser session.",
    audioKeyPromptTitle: "Use your own OpenAI API key for better AI audio",
    audioKeyPromptBody: "Add your key if you want PageTone to generate smoother chapter audio with more natural voices.",
    audioKeyPromptAction: "See how",
    audioKeyGuideTitle: "How to add your key",
    audioKeyGuideIntro: "You can keep using PageTone without a key. Adding your own key simply unlocks better AI audio in this browser session.",
    audioKeyGuideStepOne: "Open the AI audio key card, paste your OpenAI API key into the field, then choose Confirm key.",
    audioKeyGuideStepTwo: "Keep it in this tab or browser session while you listen.",
    audioKeyGuideStepThree: "If you do not add a key, PageTone will keep using local/browser speech fallback.",
    audioKeyGuideSessionLabel: "Session only",
    englishVoice: "English voice",
    frenchVoice: "French voice",
    chineseVoice: "Chinese voice",
    voiceAuto: "Auto (recommended)",
    previewVoice: "Preview",
    currentVoice: "Current voice",
    noVoiceAvailable: "No matching voice found in this browser.",
    audioEngineTitle: "Browser speech",
    audioEngineChecking: "Checking browser speech...",
    audioEngineServerKey: "Server key available",
    audioEnginePersonalKeyActive: "Personal API key connected",
    audioEngineFallback:
      "Server key is still missing, so PageTone is using browser speech as a fallback for now. It works for testing, but lock-screen playback will be less reliable until AI audio is enabled.",
    audioEngineFallbackWithServer:
      "Server key is connected, but this session is currently using browser speech as a fallback because AI chapter audio is unavailable. Testing can continue, but lock-screen playback will stay less reliable until AI audio recovers.",
    audioEngineFallbackWithPersonal:
      "Personal API key is connected, but this session is currently using browser speech as a fallback because AI chapter audio is unavailable. Testing can continue, but lock-screen playback will stay less reliable until AI audio recovers.",
    audioEngineNeedsKey:
      "AI audio in PageTone now uses your own OpenAI API key. Without one, PageTone falls back to browser speech on this device.",
    audioModeTitle: "Playback mode",
    audioModeServer: "Server AI chapter audio",
    audioModePersonal: "Personal-key AI chapter audio",
    audioModeFallback: "Browser speech fallback",
    audioModeUnavailable: "No audio engine available",
    audioModeServerHint:
      "PageTone is using chapter audio generated on the server. This is the mode meant for the most reliable background and lock-screen playback.",
    audioModePersonalHint:
      "PageTone is using chapter audio generated with your personal API key in this browser session. Background and lock-screen playback should behave like the server AI mode while this key remains available.",
    audioModeFallbackServerHint:
      "PageTone has a server key connected, but this session has temporarily fallen back to browser speech because AI chapter audio is currently unavailable.",
    audioModeFallbackPersonalHint:
      "PageTone has a personal API key connected, but this session has temporarily fallen back to browser speech because AI chapter audio is currently unavailable.",
    audioModeFallbackHint:
      "PageTone is still using browser speech only. This is fine for testing, but iPhone and iPad lock-screen playback will not be reliable in this mode.",
    audioModeUnavailableHint:
      "Neither AI chapter audio nor browser speech is available in this browser right now.",
    audioModeLockscreenReady: "Lock-screen ready",
    audioModeLockscreenLimited: "Lock-screen limited",
    audioModeLockscreenUnavailable: "No background audio",
    audioEnginePersonalKeyLabel: "Personal OpenAI API key",
    audioEnginePersonalKeyPlaceholder: "sk-...",
    audioEnginePersonalKeyHint:
      "Your API key stays in this browser session and is used only to generate PageTone audio. We do not store or collect it.",
    audioEngineSaveKey: "Confirm key",
    audioEngineEditKey: "Edit",
    audioEngineDeleteKey: "Delete",
    audioEngineSavedKey: "Saved for this session",
    audioEnginePersonalKeyAdded: "Personal API key added",
    audioEnginePersonalKeyInvalid: "Enter a valid OpenAI API key starting with sk-.",
    audioEngineModelLabel: "Model",
    audioEngineGenerating: "Generating audio",
    previewEnglishSample: "Hello, this is the current English voice preview.",
    previewFrenchSample: "Bonjour, ceci est l'aperçu de la voix française actuelle.",
    previewChineseSample: "你好，这里是当前中文声音的试听。",
    loadDemo: "Load Demo Text",
    importTxt: "Upload File",
    clearText: "Clear",
    editorLabel: "Reader text",
    editorPlaceholder: "Paste a bilingual chapter here...",
    browserSpeechReady: "Browser speech",
    browserFallbackReady: "Browser speech",
    browserFallbackDegraded: "Resume when you return",
    browserSpeechSupportNote: "Best in browser while this tab stays active.",
    browserSpeechResumeHint: "Resume when you return. Lock-screen playback can still pause in Safari.",
    browserSpeechMissing: "Browser speech unavailable",
    paragraphCount: "Paragraphs",
    currentBlock: "Current block",
    detectedLanguage: "Detected language",
    voiceCount: "Voices",
    readingWindow: "Reading window",
    justRead: "Just read",
    upNext: "Up next",
    speed: "Speed",
    autoStopTitle: "Auto stop",
    autoStopOff: "Off",
    autoStopChapter: "After chapter",
    timerTitle: "Timer",
    play: "Play",
    pause: "Pause",
    resume: "Resume",
    stop: "Stop",
    previous: "Prev Para",
    next: "Next Para",
    restartBlock: "Replay Para",
    noBlocks: "Add some text first to test playback.",
    queueTitle: "Paragraph queue",
    queueShow: "Show list",
    queueHide: "Hide list",
    queueSummary: "Optional jump list for paragraph-level navigation.",
    currentParagraph: "Now reading",
    clickToJump: "Click any paragraph to jump there.",
    progressNote: "Text, speed, and current paragraph are saved locally on this device.",
    progressLabel: "Progress",
    skipBackFifteen: "-15s",
    skipForwardFifteen: "+15s",
    skipBackAria: "Back 15 seconds",
    skipForwardAria: "Forward 15 seconds",
    statusReady: "Prototype ready.",
    statusImported: "TXT imported.",
    statusImportedEpub: "EPUB imported.",
    statusImportedMobi: "MOBI imported.",
    statusImportedDocx: "DOCX imported.",
    statusImportedDoc: "DOC imported.",
    statusImportedPdf: "PDF imported.",
    statusAutoStarted: "File imported and playback started.",
    statusOpenedSavedBook: "Opened saved book.",
    statusDeletedSavedBook: "Deleted saved book.",
    statusChapterChanged: "Moved to next chapter.",
    statusMovedPreviousChapter: "Moved to previous chapter.",
    statusNoNextChapter: "You are already on the last chapter.",
    statusNoPreviousChapter: "You are already on the first chapter.",
    statusCleared: "Reader text cleared.",
    statusNoSpeech:
      "No playback engine is available in this browser right now.",
    statusPaused: "Playback paused.",
    statusResumed: "Playback resumed.",
    statusStopped: "Playback stopped.",
    statusFinished: "Playback finished.",
    statusError: "Playback could not start.",
    statusPreviewingEnglish: "Previewing English voice.",
    statusPreviewingFrench: "Previewing French voice.",
    statusPreviewingChinese: "Previewing Chinese voice.",
    statusLoadedDemo: "Demo text loaded.",
    statusQueueJump: "Jumped to selected paragraph.",
    statusVoiceWait: "Generating chapter audio...",
    statusBuffering: "Buffering playback...",
    statusAiFallback:
      "AI chapter audio is temporarily unavailable. Switched to browser speech fallback.",
    statusVoiceUpdated: "Voice updated. Playback will continue from the current position.",
    statusTranslationPlaybackUpdated: "Reading language updated.",
    statusResumeReady: "Resume is ready when you return to PageTone.",
    statusResumeFailed: "Browser speech paused in the background. Tap Resume to continue.",
    statusRestarted: "Restarted current paragraph.",
    statusMovedNext: "Moved to next paragraph.",
    statusMovedPrevious: "Moved to previous paragraph.",
    statusNoNextParagraph: "You are already on the last paragraph of this chapter.",
    statusNoPreviousParagraph: "You are already on the first paragraph of this chapter.",
    statusRateChanged: "Playback speed updated.",
    statusSleepCleared: "Auto stop cleared.",
    statusSleepChapter: "Playback will stop after this chapter.",
    statusSleepScheduled: "Auto stop set for {minutes} minutes.",
    statusSleepTriggered: "Auto stop reached. Playback stopped.",
    statusSleepChapterTriggered: "Stopped at the end of this chapter.",
    resumePromptTitle: "Resume when you return",
    resumePromptBody:
      "Browser speech paused while PageTone was in the background. Pick up this chapter with one tap.",
    uploadHint:
      "TXT, EPUB, MOBI, DOCX, DOC, and text-based PDF import are live now. TXT works best with UTF-8 or common Chinese encodings.",
    statusUnsupported:
      "Only TXT, EPUB, MOBI, DOCX, DOC, and text-based PDF files are supported in this prototype.",
    statusExtractingEpub: "Extracting EPUB text...",
    statusExtractingDocument: "Extracting document text...",
    statusImportFailed: "File import failed.",
    statusImportEmpty: "No readable text was found in that file.",
    statusImportTooLarge: "That file is too large to import here right now.",
    statusConvertNeedFile: "Upload a file first to convert it.",
    statusConvertMismatch: "Current file is {current}. Upload a {expected} file to use this source selection.",
    statusConvertSameFormat: "Choose a different target format.",
    statusConverting: "Converting file...",
    statusConverted: "{format} file is ready.",
    statusConvertCancelled: "File conversion cancelled.",
    statusConvertFailed: "File conversion failed.",
    statusUnsupportedScannedPdf:
      "This PDF appears to be scanned or image-only. PageTone currently supports text-based PDFs only.",
    statusSaveLimited: "Local shelf is full, so only the 6 most recent books are kept.",
    statusSaveFailed: "This book is too large to save locally, but you can still read it now.",
    statusTitle: "What is already started",
    statusItems: [
      "A Swift core starter has been set up for TXT import, paragraph splitting, language detection, and reading progress.",
      "A SwiftUI starter screen flow exists for library, reader, speech controls, and settings.",
      "The main verification path already passes as a smoke check in the repo."
    ],
    featureTitle: "MVP features",
    features: [
      {
        icon: "📄",
        title: "TXT first",
        body: "Start with the easiest format so the full reading pipeline works before EPUB, MOBI, and PDF."
      },
      {
        icon: "🌐",
        title: "Bilingual detection",
        body: "Split text by paragraph, detect Chinese or English, and hand each block to the right voice."
      },
      {
        icon: "🔊",
        title: "System speech",
        body: "Use Apple voices first so we can make the app stable before worrying about premium TTS."
      },
      {
        icon: "⏯",
        title: "Resume playback",
        body: "Keep the last block position so it can reopen close to where you left off."
      }
    ],
    roadmapTitle: "Next steps",
    roadmap: [
      "Turn the starter into a full Xcode iPhone project.",
      "Add Readium and bring in EPUB support.",
      "Add normal PDF text extraction.",
      "Add background audio session handling."
    ],
    note:
      "This page is the website preview. The actual native iPhone starter files are also in this repo and can keep growing from here.",
    primaryCta: "Start Wonder World",
    secondaryCta: "Back Home",
    demoText:
      "Hello and welcome to PageTone. This small prototype lets us test paragraph playback before the native iPhone app is finished.\n\n你好，欢迎来到 PageTone。这个小原型先帮助我们测试分段朗读、中英切换和断点续播。\n\nWhen the browser sees English, it should try an English voice. When it sees Chinese, it should switch to a Chinese voice if one is available.\n\n如果这个方向对，我们下一步就继续把 EPUB、MOBI、PDF 和更完整的 iPhone 工程接上。"
  },
  fr: {
    badge: "PageTone",
    title: "Une page dédiée pour l'app iPhone d'écoute bilingue",
    subtitle:
      "Voici l'aperçu web de l'app d'écoute en cours de création à côté de Wonder World.",
    heroEyebrow: "Priorité actuelle",
    heroBullets: ["Import TXT / EPUB / MOBI / DOCX / DOC / PDF", "Bascule vocale chinois + anglais", "Reprise à la dernière position"],
    sandboxTitle: "Essayer le prototype lecteur",
    sandboxIntro:
      "Collez du texte ou importez un fichier TXT, EPUB, MOBI, DOCX, DOC ou PDF textuel, puis testez la lecture bilingue par paragraphes directement dans le navigateur.",
    dropzoneTitle: "Téléverser un fichier et commencer la lecture",
    dropzoneHint: "Déposez un fichier TXT, EPUB, MOBI, DOCX, DOC ou PDF textuel ici, ou cliquez pour le choisir.",
    dropzoneActive: "Relâchez pour importer ce fichier.",
    lastFileLabel: "Dernier fichier",
    fileTypeLabel: "Format",
    fileTypeTxt: "TXT",
    fileTypeEpub: "EPUB",
    fileTypeMobi: "MOBI",
    fileTypeDocx: "DOCX",
    fileTypeDoc: "DOC",
    fileTypePdf: "PDF",
    convertTitle: "Convertir le fichier",
    convertHint: "Utilisez le fichier importé actuel et exportez-le en TXT ou EPUB.",
    convertSourceLabel: "Depuis",
    convertTargetLabel: "Vers",
    convertArrowAria: "Convertir le fichier",
    convertSupportedTargets:
      "Conversion MVP actuelle : TXT, EPUB, MOBI, DOCX, DOC et PDF textuel peuvent être exportés en TXT ou EPUB.",
    workspaceTitle: "PageTone",
    featureIntroTitle: "À quoi sert PageTone",
    featureIntroBody:
      "Importez un livre, écoutez-le dans le navigateur, choisissez la langue que vous voulez comprendre, puis reprenez votre lecture là où vous vous êtes arrêté.",
    back_home: "Retour",
    closePanel: "Fermer",
    pasteText: "Coller du texte",
    currentBookTitle: "Continuer l'écoute",
    currentBookHint: "Reprenez le livre actuel exactement là où vous vous êtes arrêté.",
    shelfNowPlaying: "Lecture actuelle",
    shelfUpNext: "À suivre",
    chapterListTitle: "Chapitres",
    chapterListHint: "Allez directement à n'importe quel chapitre.",
    chapterPickerOpen: "Choisir un chapitre",
    noChapters: "Aucun chapitre chargé pour le moment.",
    shelfTitle: "Livres enregistrés",
    shelfHint: "Bibliothèque locale récente. Les nouveaux imports sont gardés ici automatiquement.",
    shelfStorageBooks: "Livres",
    shelfStorageTotal: "Total",
    shelfStorageUsed: "Utilisé",
    shelfStorageLeft: "Restant",
    shelfStorageNote: "Estimation du stockage local du navigateur pour PageTone sur cet appareil.",
    shelfEmpty: "Aucun livre enregistré pour le moment.",
    shelfOpen: "Ouvrir",
    shelfDelete: "Supprimer",
    shelfLimit: "Les 6 livres les plus récents sont conservés ici.",
    shelfPreviousWindow: "Voir les livres précédents",
    shelfNextWindow: "Voir plus de livres",
    shelfWindowLabel: "{start}-{end} sur {total}",
    importedTextTitle: "Texte importé",
    demoChapterTitle: "Chapitre démo",
    chapterLabel: "Chapitre",
    previousChapter: "Chapitre préc.",
    nextChapter: "Chapitre suivant",
    noNextChapter: "Dernier chapitre",
    voiceTuningTitle: "Réglage des voix",
    voiceTuningHint:
      "PageTone utilise ici la voix du navigateur. L'expérience est meilleure tant que l'onglet Safari reste actif, puis une reprise claire est proposée à votre retour.",
    audioSettingsTitle: "Réglages audio",
    audioSettingsSubtitle: "Choisissez les voix si vous voulez affiner l'expérience de lecture dans le navigateur.",
    understandInLabel: "Comprendre en",
    understandInHint: "Choisissez votre langue de lecture préférée. Nous gérons la traduction automatiquement.",
    understandInChinese: "中文",
    understandInEnglish: "English",
    pureReadingLabel: "Lecture pure",
    pureReadingExit: "Quitter la lecture pure",
    pureReadingHint: "Mettez d’abord en pause, puis parcourez paragraphe par paragraphe avec les flèches ou le défilement.",
    pureReadingActiveHint: "Utilisez ↑ ↓ ← → ou le défilement pour changer de paragraphe.",
    exportMp3Label: "Exporter MP3",
    exportMp3OriginalLabel: "Exporter MP3 (original)",
    exportMp3ChineseLabel: "Exporter MP3 (中文)",
    exportEpubOriginalLabel: "Exporter EPUB",
    exportEpubChineseLabel: "Exporter EPUB (中文)",
    exportMp3Hint: "Exporter le livre actuel en un seul fichier MP3.",
    exportMp3ProgressInline: "{done}/{total} chapitres",
    exportEpubProgressInline: "{done}/{total} chapitres",
    pureReadingPending: "Préparation de la traduction…",
    statusExportingMp3: "Préparation de l’export MP3…",
    statusExportingMp3Progress: "Export… {done}/{total} chapitres",
    statusExportingOriginalEpub: "Préparation de l’export EPUB…",
    statusExportingOriginalEpubProgress: "Création de l’EPUB… {done}/{total} chapitres",
    statusExportingChineseEpub: "Préparation de l’EPUB chinois…",
    statusExportingChineseEpubProgress: "Création de l’EPUB chinois… {done}/{total} chapitres",
    statusExportedMp3: "Export MP3 terminé.",
    statusExportMp3Cancelled: "Export MP3 annulé.",
    statusExportMp3Failed: "Échec de l’export MP3.",
    statusPureReadingEnabled: "Le mode lecture pure est activé.",
    statusPureReadingDisabled: "Le mode lecture pure est désactivé.",
    statusPureReadingNeedsPause: "Mettez la lecture en pause avant d’ouvrir la lecture pure.",
    translationPlaybackTitle: "Lecture traduite",
    translationPlaybackHint:
      "Mode EN ⇄ 中文 priorisant la qualité. PageTone peut attendre un instant pour une meilleure traduction gratuite, puis préparer la suivante pendant la lecture.",
    translationPlaybackOff: "Texte original",
    translationPlaybackAuto: "Auto EN ⇄ 中文",
    translationPlaybackEnglishToChinese: "EN → 中文",
    translationPlaybackChineseToEnglish: "中文 → EN",
    translationPlaybackChipPrefix: "Traduction",
    translatedLineLabel: "Traduction",
    translationPlaybackNowReading: "Lecture traduite en cours",
    translationPlaybackCurrentLabel: "Ligne traduite en cours",
    translationPlaybackPending: "Traduction en cours…",
    translationPlaybackFallbackNotice:
      "Texte original conservé pour le moment.",
    audioSettingsShow: "Afficher",
    audioSettingsHide: "Masquer",
    audioKeyCardTitle: "Clé audio IA",
    audioKeyCardSubtitle:
      "Ajoutez votre propre clé API OpenAI si vous voulez qu'PageTone génère un son plus fluide dans cette session du navigateur.",
    audioKeyPromptTitle: "Ajoutez votre propre clé API OpenAI pour une meilleure voix IA",
    audioKeyPromptBody: "Ajoutez votre clé si vous voulez qu'PageTone génère un audio de chapitre plus fluide et plus naturel.",
    audioKeyPromptAction: "Voir comment",
    audioKeyGuideTitle: "Comment ajouter votre clé",
    audioKeyGuideIntro: "Vous pouvez continuer à utiliser PageTone sans clé. Ajouter votre propre clé active simplement une meilleure voix IA dans cette session du navigateur.",
    audioKeyGuideStepOne: "Ouvrez la carte de clé audio IA, collez votre clé API OpenAI dans le champ, puis choisissez Confirmer la clé.",
    audioKeyGuideStepTwo: "Gardez-la dans cet onglet ou cette session de navigateur pendant l'écoute.",
    audioKeyGuideStepThree: "Si vous n'ajoutez pas de clé, PageTone continuera à utiliser la voix locale / du navigateur.",
    audioKeyGuideSessionLabel: "Session uniquement",
    englishVoice: "Voix anglaise",
    frenchVoice: "Voix française",
    chineseVoice: "Voix chinoise",
    voiceAuto: "Auto (recommandé)",
    previewVoice: "Écouter",
    currentVoice: "Voix active",
    noVoiceAvailable: "Aucune voix compatible trouvée dans ce navigateur.",
    audioEngineTitle: "Voix du navigateur",
    audioEngineChecking: "Vérification de la lecture du navigateur...",
    audioEngineServerKey: "Clé serveur disponible",
    audioEnginePersonalKeyActive: "Clé API personnelle connectée",
    audioEngineFallback:
      "La clé serveur manque encore, donc PageTone utilise pour l'instant la synthèse vocale du navigateur en secours. C'est suffisant pour tester, mais la lecture écran verrouillé restera moins fiable tant que l'audio IA n'est pas activé.",
    audioEngineFallbackWithServer:
      "La clé serveur est connectée, mais cette session utilise actuellement la voix du navigateur en secours parce que l'audio IA par chapitre n'est pas disponible.",
    audioEngineFallbackWithPersonal:
      "La clé API personnelle est connectée, mais cette session utilise actuellement la voix du navigateur en secours parce que l'audio IA par chapitre n'est pas disponible.",
    audioEngineNeedsKey:
      "L'audio IA d'PageTone utilise maintenant votre propre clé API OpenAI. Sans clé, PageTone repasse à la voix du navigateur sur cet appareil.",
    audioModeTitle: "Mode de lecture",
    audioModeServer: "Audio IA serveur par chapitre",
    audioModePersonal: "Audio IA par clé personnelle",
    audioModeFallback: "Voix du navigateur uniquement",
    audioModeUnavailable: "Aucun moteur audio disponible",
    audioModeServerHint:
      "PageTone utilise l'audio de chapitre généré côté serveur. C'est le mode prévu pour la lecture en arrière-plan et écran verrouillé la plus fiable.",
    audioModePersonalHint:
      "PageTone utilise l'audio de chapitre généré avec votre clé API personnelle dans cette session navigateur. La lecture en arrière-plan et écran verrouillé devrait alors se comporter comme le mode IA serveur.",
    audioModeFallbackServerHint:
      "PageTone a bien une clé serveur connectée, mais cette session est temporairement repassée sur la voix du navigateur parce que l'audio IA par chapitre n'est pas disponible.",
    audioModeFallbackPersonalHint:
      "PageTone a bien une clé API personnelle connectée, mais cette session est temporairement repassée sur la voix du navigateur parce que l'audio IA par chapitre n'est pas disponible.",
    audioModeFallbackHint:
      "PageTone utilise encore uniquement la voix du navigateur. C'est suffisant pour tester, mais la lecture écran verrouillé sur iPhone et iPad ne sera pas fiable dans ce mode.",
    audioModeUnavailableHint:
      "Ni l'audio IA par chapitre ni la voix du navigateur ne sont disponibles dans ce navigateur pour le moment.",
    audioModeLockscreenReady: "Écran verrouillé prêt",
    audioModeLockscreenLimited: "Écran verrouillé limité",
    audioModeLockscreenUnavailable: "Pas d'audio arrière-plan",
    audioEnginePersonalKeyLabel: "Clé API OpenAI personnelle",
    audioEnginePersonalKeyPlaceholder: "sk-...",
    audioEnginePersonalKeyHint:
      "Votre clé API reste uniquement dans cette session du navigateur et sert seulement à générer l'audio PageTone. Nous ne la stockons ni ne la collectons.",
    audioEngineSaveKey: "Confirmer la clé",
    audioEngineEditKey: "Modifier",
    audioEngineDeleteKey: "Supprimer",
    audioEngineSavedKey: "Enregistrée pour cette session",
    audioEnginePersonalKeyAdded: "Clé API personnelle ajoutée",
    audioEnginePersonalKeyInvalid: "Saisissez une clé API OpenAI valide commençant par sk-.",
    audioEngineModelLabel: "Modèle",
    audioEngineGenerating: "Génération audio",
    previewEnglishSample: "Hello, this is the current English voice preview.",
    previewFrenchSample: "Bonjour, ceci est l'aperçu de la voix française actuelle.",
    previewChineseSample: "你好，这里是当前中文声音的试听。",
    loadDemo: "Charger le texte démo",
    importTxt: "Téléverser un fichier",
    clearText: "Effacer",
    editorLabel: "Texte du lecteur",
    editorPlaceholder: "Collez ici un chapitre bilingue...",
    browserSpeechReady: "Voix du navigateur",
    browserFallbackReady: "Voix du navigateur",
    browserFallbackDegraded: "Reprenez à votre retour",
    browserSpeechSupportNote: "Le meilleur résultat reste dans le navigateur tant que cet onglet reste actif.",
    browserSpeechResumeHint: "Reprenez à votre retour. La lecture écran verrouillé peut encore s'arrêter dans Safari.",
    browserSpeechMissing: "Voix du navigateur indisponible",
    paragraphCount: "Paragraphes",
    currentBlock: "Paragraphe actuel",
    detectedLanguage: "Langue détectée",
    voiceCount: "Voix",
    readingWindow: "Fenêtre de lecture",
    justRead: "Juste avant",
    upNext: "À suivre",
    speed: "Vitesse",
    autoStopTitle: "Arrêt auto",
    autoStopOff: "Off",
    autoStopChapter: "Fin du chapitre",
    timerTitle: "Minuteur",
    play: "Lecture",
    pause: "Pause",
    resume: "Reprendre",
    stop: "Stop",
    previous: "Paragr. préc.",
    next: "Paragr. suiv.",
    restartBlock: "Relire",
    noBlocks: "Ajoutez d'abord du texte pour tester la lecture.",
    queueTitle: "File des paragraphes",
    queueShow: "Afficher",
    queueHide: "Masquer",
    queueSummary: "Liste facultative pour sauter paragraphe par paragraphe.",
    currentParagraph: "Lecture en cours",
    clickToJump: "Cliquez sur un paragraphe pour sauter à cet endroit.",
    progressNote: "Le texte, la vitesse et le paragraphe actuel sont enregistrés localement sur cet appareil.",
    progressLabel: "Progression",
    skipBackFifteen: "-15s",
    skipForwardFifteen: "+15s",
    skipBackAria: "Reculer de 15 secondes",
    skipForwardAria: "Avancer de 15 secondes",
    statusReady: "Prototype prêt.",
    statusImported: "TXT importé.",
    statusImportedEpub: "EPUB importé.",
    statusImportedMobi: "MOBI importé.",
    statusImportedDocx: "DOCX importé.",
    statusImportedDoc: "DOC importé.",
    statusImportedPdf: "PDF importé.",
    statusAutoStarted: "Fichier importé et lecture démarrée.",
    statusOpenedSavedBook: "Livre enregistré ouvert.",
    statusDeletedSavedBook: "Livre enregistré supprimé.",
    statusChapterChanged: "Passage au chapitre suivant.",
    statusMovedPreviousChapter: "Retour au chapitre précédent.",
    statusNoNextChapter: "Vous êtes déjà au dernier chapitre.",
    statusNoPreviousChapter: "Vous êtes déjà au premier chapitre.",
    statusCleared: "Texte du lecteur effacé.",
    statusNoSpeech:
      "Aucun moteur de lecture n'est disponible dans ce navigateur pour le moment.",
    statusPaused: "Lecture mise en pause.",
    statusResumed: "Lecture reprise.",
    statusStopped: "Lecture arrêtée.",
    statusFinished: "Lecture terminée.",
    statusError: "La lecture n'a pas pu démarrer.",
    statusPreviewingEnglish: "Aperçu de la voix anglaise.",
    statusPreviewingFrench: "Aperçu de la voix française.",
    statusPreviewingChinese: "Aperçu de la voix chinoise.",
    statusLoadedDemo: "Texte démo chargé.",
    statusQueueJump: "Saut vers le paragraphe sélectionné.",
    statusVoiceWait: "Génération de l'audio du chapitre...",
    statusBuffering: "Mise en mémoire tampon de la lecture...",
    statusAiFallback:
      "L'audio IA du chapitre est temporairement indisponible. Bascule vers la lecture vocale du navigateur.",
    statusVoiceUpdated: "Voix mise à jour. La lecture reprend à la position actuelle.",
    statusTranslationPlaybackUpdated: "La langue de lecture a été mise à jour.",
    statusResumeReady: "La reprise est prête quand vous revenez dans PageTone.",
    statusResumeFailed: "La voix du navigateur s'est arrêtée en arrière-plan. Touchez Reprendre pour continuer.",
    statusRestarted: "Paragraphe relancé.",
    statusMovedNext: "Passage au paragraphe suivant.",
    statusMovedPrevious: "Retour au paragraphe précédent.",
    statusNoNextParagraph: "Vous êtes déjà au dernier paragraphe de ce chapitre.",
    statusNoPreviousParagraph: "Vous êtes déjà au premier paragraphe de ce chapitre.",
    statusRateChanged: "La vitesse de lecture a été mise à jour.",
    statusSleepCleared: "L'arrêt automatique a été désactivé.",
    statusSleepChapter: "La lecture s'arrêtera à la fin de ce chapitre.",
    statusSleepScheduled: "Arrêt automatique dans {minutes} minutes.",
    statusSleepTriggered: "Arrêt automatique atteint. Lecture stoppée.",
    statusSleepChapterTriggered: "Lecture arrêtée à la fin du chapitre.",
    resumePromptTitle: "Reprendre à votre retour",
    resumePromptBody:
      "La voix du navigateur s'est arrêtée pendant qu'PageTone était en arrière-plan. Reprenez ce chapitre en un geste.",
    uploadHint:
      "L'import TXT, EPUB, MOBI, DOCX, DOC et PDF textuel est actif. Pour TXT, UTF-8 et les encodages chinois courants devraient bien fonctionner.",
    statusUnsupported:
      "Seuls TXT, EPUB, MOBI, DOCX, DOC et les PDF textuels sont pris en charge dans ce prototype.",
    statusExtractingEpub: "Extraction du texte EPUB...",
    statusExtractingDocument: "Extraction du texte du document...",
    statusImportFailed: "L'import du fichier a échoué.",
    statusImportEmpty: "Aucun texte exploitable n'a été trouvé dans ce fichier.",
    statusImportTooLarge: "Ce fichier est trop volumineux pour être importé ici pour l'instant.",
    statusConvertNeedFile: "Importez d'abord un fichier pour le convertir.",
    statusConvertMismatch: "Le fichier actuel est en {current}. Importez un fichier {expected} pour utiliser cette source.",
    statusConvertSameFormat: "Choisissez un format cible différent.",
    statusConverting: "Conversion du fichier...",
    statusConverted: "Le fichier {format} est prêt.",
    statusConvertCancelled: "Conversion annulée.",
    statusConvertFailed: "La conversion du fichier a échoué.",
    statusUnsupportedScannedPdf:
      "Ce PDF semble scanné ou uniquement composé d'images. PageTone prend actuellement en charge uniquement les PDF contenant du texte.",
    statusSaveLimited: "La bibliothèque locale est pleine, donc seuls les 6 livres les plus récents sont gardés.",
    statusSaveFailed: "Ce livre est trop volumineux pour être enregistré localement, mais vous pouvez quand même le lire maintenant.",
    statusTitle: "Ce qui est déjà lancé",
    statusItems: [
      "Un starter Swift a déjà été préparé pour l'import TXT, le découpage en paragraphes, la détection de langue et la sauvegarde de progression.",
      "Un starter SwiftUI existe déjà pour la bibliothèque, le lecteur, les contrôles audio et les réglages.",
      "Le chemin principal de vérification passe déjà en smoke check dans le dépôt."
    ],
    featureTitle: "Fonctions MVP",
    features: [
      {
        icon: "📄",
        title: "TXT d'abord",
        body: "On commence par le format le plus simple afin de valider toute la chaîne avant EPUB, MOBI et PDF."
      },
      {
        icon: "🌐",
        title: "Détection bilingue",
        body: "Le texte est découpé par paragraphe, reconnu en chinois ou en anglais, puis envoyé à la bonne voix."
      },
      {
        icon: "🔊",
        title: "Voix système",
        body: "On utilise d'abord les voix Apple pour stabiliser l'app avant de penser à un TTS premium."
      },
      {
        icon: "⏯",
        title: "Reprise de lecture",
        body: "La dernière position est mémorisée pour reprendre près de l'endroit quitté."
      }
    ],
    roadmapTitle: "Étapes suivantes",
    roadmap: [
      "Transformer le starter en projet Xcode iPhone complet.",
      "Ajouter Readium et le support EPUB.",
      "Ajouter l'extraction de texte PDF classique.",
      "Ajouter la gestion audio en arrière-plan."
    ],
    note:
      "Cette page est l'aperçu du site. Les vrais fichiers natifs iPhone sont aussi dans ce dépôt et peuvent continuer à évoluer ici.",
    primaryCta: "Lancer Wonder World",
    secondaryCta: "Retour accueil",
    demoText:
      "Hello and welcome to PageTone. This small prototype lets us test paragraph playback before the native iPhone app is finished.\n\n你好，欢迎来到 PageTone。这个小原型先帮助我们测试分段朗读、中英切换和断点续播。\n\nWhen the browser sees English, it should try an English voice. When it sees Chinese, it should switch to a Chinese voice if one is available.\n\n如果这个方向对，我们下一步就继续把 EPUB、MOBI、PDF 和更完整的 iPhone 工程接上。"
  },
  zh: {
    badge: "PageTone",
    back_home: "返回主页",
    closePanel: "关闭",
    pasteText: "粘贴文本",
    currentBookTitle: "继续当前图书",
    currentBookHint: "从你上次停下的位置继续播放当前这本书。",
    chapterListTitle: "章节列表",
    chapterListHint: "可直接跳转到任意章节。",
    chapterPickerOpen: "选择章节",
    noChapters: "当前还没有可用章节。",
    dropzoneTitle: "上传文件并开始阅读",
    dropzoneHint: "把 TXT、EPUB、MOBI、DOCX、DOC 或文本型 PDF 拖到这里，或点击选择文件。",
    dropzoneActive: "松开即可导入这个文件。",
    lastFileLabel: "最近文件",
    fileTypeLabel: "格式",
    fileTypeTxt: "TXT",
    fileTypeEpub: "EPUB",
    fileTypeMobi: "MOBI",
    fileTypeDocx: "DOCX",
    fileTypeDoc: "DOC",
    fileTypePdf: "PDF",
    convertTitle: "转换文件",
    convertHint: "使用当前已上传的文件，导出为 TXT 或 EPUB。",
    convertSourceLabel: "从",
    convertTargetLabel: "到",
    convertArrowAria: "转换文件",
    convertSupportedTargets:
      "当前可用的转换：TXT、EPUB、MOBI、DOCX、DOC 和文本型 PDF 可导出为 TXT 或 EPUB。",
    workspaceTitle: "PageTone",
    featureIntroTitle: "PageTone 可以做什么",
    featureIntroBody:
      "你的书，你的语言。导入书籍，实时翻译边读边听，还可以导出成 MP3。",
    shelfTitle: "已保存书籍",
    shelfHint: "最近导入的本地书架，新文件会自动保存在这里。",
    shelfNowPlaying: "正在播放",
    shelfUpNext: "下一本",
    shelfStorageBooks: "书籍",
    shelfStorageTotal: "总量",
    shelfStorageUsed: "已用",
    shelfStorageLeft: "剩余",
    shelfStorageNote: "这里显示的是这台设备上 PageTone 浏览器本地存储的近似值。",
    shelfEmpty: "还没有保存的书。",
    shelfOpen: "打开",
    shelfDelete: "删除",
    shelfLimit: "这里最多保留最近 6 本书。",
    shelfPreviousWindow: "查看前面的书",
    shelfNextWindow: "查看更多书",
    shelfWindowLabel: "{start}-{end} / {total}",
    importedTextTitle: "导入文本",
    demoChapterTitle: "演示章节",
    chapterLabel: "章节",
    previousChapter: "上一章",
    nextChapter: "下一章",
    noNextChapter: "最后一章",
    voiceTuningTitle: "语音设置",
    voiceTuningHint:
      "这一模式下 PageTone 使用浏览器朗读。Safari 标签页保持激活时体验最好，回来后也会给你明确的继续入口。",
    audioSettingsTitle: "音频设置",
    audioSettingsSubtitle: "只在需要时微调浏览器里的朗读声音。",
    understandInLabel: "理解为",
    understandInHint: "选择你更想读懂成哪种语言，剩下的交给我们自动处理。",
    understandInChinese: "中文",
    understandInEnglish: "English",
    pureReadingLabel: "Pure reading",
    pureReadingExit: "退出纯阅读",
    pureReadingHint: "先暂停，再用方向键或滚动按段落阅读。",
    pureReadingActiveHint: "可用 ↑ ↓ ← → 或滚动切换段落。",
    exportMp3Label: "导出 MP3",
    exportMp3OriginalLabel: "导出 MP3（原声）",
    exportMp3ChineseLabel: "导出 MP3（中文）",
    exportEpubOriginalLabel: "导出 EPUB",
    exportEpubChineseLabel: "导出 EPUB（中文）",
    exportMp3Hint: "将当前整本书导出为一个 MP3 文件。",
    exportMp3ProgressInline: "{done}/{total} 章",
    exportEpubProgressInline: "第 {done}/{total} 章",
    pureReadingPending: "正在准备译文…",
    statusExportingMp3: "正在准备 MP3 导出…",
    statusExportingMp3Progress: "正在导出… 第 {done}/{total} 章",
    statusExportingOriginalEpub: "正在准备 EPUB 导出…",
    statusExportingOriginalEpubProgress: "正在生成 EPUB… 第 {done}/{total} 章",
    statusExportingChineseEpub: "正在准备中文 EPUB 导出…",
    statusExportingChineseEpubProgress: "正在生成中文 EPUB… 第 {done}/{total} 章",
    statusExportedMp3: "MP3 导出完成。",
    statusExportMp3Cancelled: "已取消 MP3 导出。",
    statusExportMp3Failed: "MP3 导出失败。",
    statusPureReadingEnabled: "已进入纯阅读模式。",
    statusPureReadingDisabled: "已退出纯阅读模式。",
    statusPureReadingNeedsPause: "请先暂停，再进入纯阅读。",
    translationPlaybackTitle: "翻译朗读",
    translationPlaybackHint:
      "质量优先的免费中英互译模式。PageTone 可以稍等一下拿到更稳的译文，同时继续预热后面的段落。",
    translationPlaybackOff: "只读原文",
    translationPlaybackAuto: "自动中英互译",
    translationPlaybackEnglishToChinese: "英 → 中",
    translationPlaybackChineseToEnglish: "中 → 英",
    translationPlaybackChipPrefix: "翻译",
    translatedLineLabel: "译文",
    translationPlaybackNowReading: "当前正在译文朗读",
    translationPlaybackCurrentLabel: "当前译文",
    translationPlaybackPending: "翻译中…",
    translationPlaybackFallbackNotice:
      "这句暂时先保留原文。",
    audioSettingsShow: "展开",
    audioSettingsHide: "收起",
    audioKeyCardTitle: "AI 音频 Key",
    audioKeyCardSubtitle:
      "如果你希望 PageTone 在当前浏览器会话里生成更顺滑的音频，请添加你自己的 OpenAI API key。",
    audioKeyPromptTitle: "添加你自己的 OpenAI API key，可获得更好的 AI 声音",
    audioKeyPromptBody: "如果你希望 PageTone 生成更自然、更顺滑的章节音频，可以在这里添加你自己的 key。",
    audioKeyPromptAction: "查看方法",
    audioKeyGuideTitle: "如何添加 key",
    audioKeyGuideIntro: "没有 key 也可以继续使用 PageTone。添加你自己的 key，只是让这个浏览器会话里可以启用更好的 AI 音频。",
    audioKeyGuideStepOne: "打开 AI 音频 Key 卡片，把你的 OpenAI API key 粘贴到输入框中，然后点确认 Key。",
    audioKeyGuideStepTwo: "在收听期间保留这个标签页或浏览器会话。",
    audioKeyGuideStepThree: "如果不添加 key，PageTone 会继续使用本地 / 浏览器朗读回退。",
    audioKeyGuideSessionLabel: "仅当前会话",
    englishVoice: "英语语音",
    frenchVoice: "法语语音",
    chineseVoice: "中文语音",
    voiceAuto: "自动（推荐）",
    previewVoice: "试听",
    currentVoice: "当前语音",
    noVoiceAvailable: "当前浏览器没有找到匹配的语音。",
    audioEngineTitle: "浏览器朗读",
    audioEngineChecking: "正在检查浏览器朗读……",
    audioEngineServerKey: "服务器密钥可用",
    audioEnginePersonalKeyActive: "个人 API Key 已连接",
    audioEngineFallback:
      "服务器 key 还没配好，所以 PageTone 现在先用浏览器朗读做回退。测试没有问题，但锁屏播放还是会比 AI 音频弱一些。",
    audioEngineFallbackWithServer:
      "服务器 key 已连接，但当前这个会话因为 AI 章节音频暂时不可用，所以正在回退到浏览器朗读。",
    audioEngineFallbackWithPersonal:
      "个人 API Key 已连接，但当前这个会话因为 AI 章节音频暂时不可用，所以正在回退到浏览器朗读。",
    audioEngineNeedsKey:
      "PageTone 的 AI 音频现在使用你自己的 OpenAI API key。没有 key 时，会自动回退到当前设备上的浏览器朗读。",
    audioModeTitle: "播放模式",
    audioModeServer: "服务器 AI 章节音频",
    audioModePersonal: "个人 Key AI 章节音频",
    audioModeFallback: "仅浏览器朗读回退",
    audioModeUnavailable: "当前没有可用音频引擎",
    audioModeServerHint:
      "PageTone 正在使用服务器生成的章节音频。这才是锁屏和后台播放最可靠的模式。",
    audioModePersonalHint:
      "PageTone 正在使用你当前会话里的个人 API Key 生成章节音频。只要这个 key 可用，后台和锁屏播放体验会接近服务器 AI 模式。",
    audioModeFallbackServerHint:
      "服务器 key 已连接，但当前这个会话因为 AI 章节音频暂时不可用，所以临时回退到了浏览器朗读。",
    audioModeFallbackPersonalHint:
      "个人 API Key 已连接，但当前这个会话因为 AI 章节音频暂时不可用，所以临时回退到了浏览器朗读。",
    audioModeFallbackHint:
      "PageTone 现在仍然只是浏览器朗读回退模式。测试可以，但在 iPhone 和 iPad 上，锁屏播放在这个模式下不会稳定。",
    audioModeUnavailableHint:
      "当前浏览器既没有 AI 章节音频，也没有可用的浏览器朗读。",
    audioModeLockscreenReady: "锁屏可用",
    audioModeLockscreenLimited: "锁屏受限",
    audioModeLockscreenUnavailable: "无后台音频",
    audioEnginePersonalKeyLabel: "个人 OpenAI API Key",
    audioEnginePersonalKeyPlaceholder: "sk-...",
    audioEnginePersonalKeyHint:
      "你的 API key 只保留在当前浏览器会话中，仅用于生成 PageTone 音频。我们不会存储或收集它。",
    audioEngineSaveKey: "确认 Key",
    audioEngineEditKey: "修改",
    audioEngineDeleteKey: "删除",
    audioEngineSavedKey: "已保存于本次会话",
    audioEnginePersonalKeyAdded: "个人 API Key 已添加",
    audioEnginePersonalKeyInvalid: "请输入有效的 OpenAI API key，并以 sk- 开头。",
    audioEngineModelLabel: "模型",
    audioEngineGenerating: "正在生成音频",
    previewEnglishSample: "Hello, this is the current English voice preview.",
    previewFrenchSample: "Bonjour, ceci est l'aperçu de la voix française actuelle.",
    previewChineseSample: "你好，这里是当前中文声音的试听。",
    loadDemo: "载入示例文本",
    importTxt: "上传文件",
    clearText: "清空",
    editorLabel: "阅读文本",
    editorPlaceholder: "把双语章节粘贴到这里……",
    browserSpeechReady: "浏览器朗读",
    browserFallbackReady: "浏览器朗读",
    browserFallbackDegraded: "回来后可继续",
    browserSpeechSupportNote: "保持此标签页开启时，浏览器内体验最好。",
    browserSpeechResumeHint: "回来后可继续。Safari 锁屏时仍可能暂停。",
    browserSpeechMissing: "浏览器朗读不可用",
    paragraphCount: "段落数",
    currentBlock: "当前段落",
    detectedLanguage: "识别语言",
    voiceCount: "语音数",
    readingWindow: "阅读窗口",
    justRead: "刚读过",
    upNext: "即将读到",
    speed: "速度",
    autoStopTitle: "自动停止",
    autoStopOff: "关闭",
    autoStopChapter: "本章结束",
    timerTitle: "定时",
    play: "播放",
    pause: "暂停",
    resume: "继续",
    stop: "停止",
    previous: "上一段",
    next: "下一段",
    restartBlock: "重读本段",
    noBlocks: "先加入一点文本再测试朗读。",
    queueTitle: "段落队列",
    queueShow: "显示列表",
    queueHide: "隐藏列表",
    queueSummary: "这是可选的跳转列表，只在你想按段落定位时使用。",
    currentParagraph: "当前朗读",
    clickToJump: "点击任意段落即可跳转。",
    progressNote: "文本、速度和当前段落都会保存在本机。",
    progressLabel: "进度",
    skipBackFifteen: "-15秒",
    skipForwardFifteen: "+15秒",
    skipBackAria: "后退 15 秒",
    skipForwardAria: "前进 15 秒",
    statusReady: "原型已就绪。",
    statusImported: "TXT 已导入。",
    statusImportedEpub: "EPUB 已导入。",
    statusImportedMobi: "MOBI 已导入。",
    statusImportedDocx: "DOCX 已导入。",
    statusImportedDoc: "DOC 已导入。",
    statusImportedPdf: "PDF 已导入。",
    statusAutoStarted: "文件已导入并开始朗读。",
    statusOpenedSavedBook: "已打开保存的书。",
    statusDeletedSavedBook: "已删除保存的书。",
    statusChapterChanged: "已切到下一章。",
    statusMovedPreviousChapter: "已切到上一章。",
    statusNoNextChapter: "已经是最后一章了。",
    statusNoPreviousChapter: "已经是第一章了。",
    statusCleared: "阅读文本已清空。",
    statusNoSpeech:
      "当前浏览器暂时没有可用的播放引擎。",
    statusPaused: "已暂停朗读。",
    statusResumed: "已继续朗读。",
    statusStopped: "已停止朗读。",
    statusFinished: "朗读完成。",
    statusError: "朗读启动失败。",
    statusPreviewingEnglish: "正在试听英语语音。",
    statusPreviewingFrench: "正在试听法语语音。",
    statusPreviewingChinese: "正在试听中文语音。",
    statusLoadedDemo: "示例文本已载入。",
    statusQueueJump: "已跳到所选段落。",
    statusVoiceWait: "正在生成章节音频……",
    statusBuffering: "正在缓冲播放……",
    statusAiFallback:
      "AI 章节音频暂时不可用，已切换到浏览器朗读。",
    statusVoiceUpdated: "语音已更新，将从当前位置继续播放。",
    statusTranslationPlaybackUpdated: "理解语言已更新。",
    statusResumeReady: "回到 PageTone 后可以继续播放。",
    statusResumeFailed: "浏览器朗读在后台暂停了。点“继续”即可恢复。",
    statusRestarted: "已重读当前段落。",
    statusMovedNext: "已切到下一段。",
    statusMovedPrevious: "已切到上一段。",
    statusNoNextParagraph: "已经是本章最后一段了。",
    statusNoPreviousParagraph: "已经是本章第一段了。",
    statusRateChanged: "朗读速度已更新。",
    statusSleepCleared: "自动停止已关闭。",
    statusSleepChapter: "将在本章结束后停止朗读。",
    statusSleepScheduled: "{minutes} 分钟后自动停止。",
    statusSleepTriggered: "已到自动停止时间，朗读已停止。",
    statusSleepChapterTriggered: "已在本章结束时停止。",
    resumePromptTitle: "回来后继续",
    resumePromptBody:
      "PageTone 在后台时，浏览器朗读已经暂停。点一下就能从当前章节继续。",
    uploadHint:
      "现在支持 TXT、EPUB、MOBI、DOCX、DOC 和文本型 PDF 导入。TXT 最适合 UTF-8 或常见中文编码。",
    statusUnsupported:
      "这个原型目前只支持 TXT、EPUB、MOBI、DOCX、DOC 和文本型 PDF。",
    statusExtractingEpub: "正在提取 EPUB 文本……",
    statusExtractingDocument: "正在提取文档文本……",
    statusImportFailed: "文件导入失败。",
    statusImportEmpty: "这个文件里没有找到可读取的文本。",
    statusImportTooLarge: "这个文件现在太大，暂时无法在这里导入。",
    statusConvertNeedFile: "请先上传一个文件再转换。",
    statusConvertMismatch: "当前文件是 {current}。如需使用这个来源选项，请先上传 {expected} 文件。",
    statusConvertSameFormat: "请选择不同的目标格式。",
    statusConverting: "正在转换文件……",
    statusConverted: "{format} 文件已准备好。",
    statusConvertCancelled: "文件转换已取消。",
    statusConvertFailed: "文件转换失败。",
    statusUnsupportedScannedPdf:
      "这个 PDF 看起来是扫描版或纯图片版。PageTone 目前只支持可提取文本的 PDF。",
    statusSaveLimited: "本地书架已满，只保留最近 6 本书。",
    statusSaveFailed: "这本书太大，无法保存在本地，但你现在仍然可以阅读。",
    secondaryCta: "回到首页",
    demoText:
      "Hello and welcome to PageTone. This small prototype lets us test paragraph playback before the native iPhone app is finished.\n\n你好，欢迎来到 PageTone。这个小原型先帮助我们测试分段朗读、中英法切换和断点续播。\n\nWhen the browser sees English, it should try an English voice. When it sees French, it should try a French voice. When it sees Chinese, it should switch to a Chinese voice if one is available.\n\n如果这个方向对，我们下一步就继续把 EPUB、MOBI、PDF 和更完整的 iPhone 工程接上。"
  }
};

function normalizeText(rawText) {
  return sanitizeNarrationSourceText(rawText);
}

function splitLongNarrationBlock(blockText, maxChars = 900) {
  const normalized = normalizeNarrationText(blockText);

  if (!normalized) {
    return [];
  }

  if (normalized.length <= maxChars) {
    return [normalized];
  }

  const sentenceLikeSegments = normalized
    .split(/(?<=[。！？!?])|(?<=[.!?])\s+/)
    .map((segment) => normalizeNarrationText(segment))
    .filter(Boolean);

  const segments = sentenceLikeSegments.length ? sentenceLikeSegments : [normalized];
  const chunks = [];
  let currentChunk = "";

  const flushCurrentChunk = () => {
    const trimmed = normalizeNarrationText(currentChunk);
    if (trimmed) {
      chunks.push(trimmed);
    }
    currentChunk = "";
  };

  segments.forEach((segment) => {
    if (segment.length > maxChars) {
      const softParts = segment
        .split(/(?<=[，；：,;:])\s*|\n+/)
        .map((part) => normalizeNarrationText(part))
        .filter(Boolean);

      if (softParts.length > 1) {
        softParts.forEach((part) => {
          const candidate = currentChunk ? `${currentChunk} ${part}` : part;
          if (candidate.length > maxChars && currentChunk) {
            flushCurrentChunk();
            currentChunk = part;
          } else {
            currentChunk = candidate;
          }
        });
        return;
      }

      const wordParts = segment.match(/.{1,900}(?:\s+|$)/g) || [segment];
      wordParts.forEach((part) => {
        const cleanedPart = normalizeNarrationText(part);
        if (!cleanedPart) {
          return;
        }
        if (currentChunk) {
          flushCurrentChunk();
        }
        currentChunk = cleanedPart;
        flushCurrentChunk();
      });
      return;
    }

    const joiner = /[A-Za-z0-9]$/.test(currentChunk) && /^[A-Za-z0-9]/.test(segment) ? " " : "";
    const candidate = currentChunk ? `${currentChunk}${joiner}${segment}` : segment;

    if (candidate.length > maxChars && currentChunk) {
      flushCurrentChunk();
      currentChunk = segment;
      return;
    }

    currentChunk = candidate;
  });

  if (currentChunk) {
    flushCurrentChunk();
  }

  return chunks.length ? chunks : [normalized];
}

function splitIntoBlocks(rawText) {
  return normalizeText(rawText)
    .split(/\n\s*\n/g)
    .map((block) => normalizeNarrationText(block))
    .filter((block) => block && !isSkippableStructuralText(block))
    .flatMap((block) => splitLongNarrationBlock(block))
    .map((text, index) => ({
      id: `block-${index}`,
      index,
      text,
      language: detectLanguage(text)
    }));
}

function buildImportedDocumentChapters(rawText, chapterLabel, fallbackTitle) {
  const normalized = normalizeText(String(rawText || "")).trim();

  if (!normalized) {
    return [];
  }

  const blocks = splitIntoBlocks(normalized);

  if (
    !blocks.length ||
    (blocks.length <= IMPORTED_DOCUMENT_CHAPTER_MAX_BLOCKS &&
      normalized.length <= IMPORTED_DOCUMENT_CHAPTER_TARGET_CHARS)
  ) {
    return [
      {
        id: "chapter-0",
        title: fallbackTitle,
        text: normalized
      }
    ];
  }

  const chapters = [];
  let currentBlocks = [];
  let currentChars = 0;

  function flushChapter() {
    if (!currentBlocks.length) {
      return;
    }

    chapters.push({
      id: `chapter-${chapters.length}`,
      title: `${chapterLabel} ${chapters.length + 1}`,
      text: currentBlocks.map((block) => block.text).join("\n\n")
    });
    currentBlocks = [];
    currentChars = 0;
  }

  blocks.forEach((block) => {
    const candidateLength = currentChars + block.text.length;
    const shouldSplit =
      currentBlocks.length &&
      (candidateLength > IMPORTED_DOCUMENT_CHAPTER_TARGET_CHARS ||
        currentBlocks.length >= IMPORTED_DOCUMENT_CHAPTER_MAX_BLOCKS);

    if (shouldSplit) {
      flushChapter();
    }

    currentBlocks.push(block);
    currentChars += block.text.length;
  });

  flushChapter();

  return chapters.length
    ? chapters
    : [
        {
          id: "chapter-0",
          title: fallbackTitle,
          text: normalized
        }
      ];
}

function getDocumentImportKind(fileName, mimeType = "") {
  const normalizedName = String(fileName || "").toLowerCase();
  const normalizedMimeType = String(mimeType || "").toLowerCase();

  if (
    normalizedName.endsWith(".mobi") ||
    normalizedMimeType === "application/x-mobipocket-ebook"
  ) {
    return "mobi";
  }

  if (
    normalizedName.endsWith(".docx") ||
    normalizedMimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return "docx";
  }

  if (
    normalizedName.endsWith(".doc") ||
    normalizedMimeType === "application/msword" ||
    normalizedMimeType === "application/vnd.ms-word"
  ) {
    return "doc";
  }

  if (normalizedName.endsWith(".pdf") || normalizedMimeType === "application/pdf") {
    return "pdf";
  }

  return "";
}

function getImportedStatusMessage(fileType, content) {
  if (fileType === content.fileTypeEpub) {
    return content.statusImportedEpub;
  }

  if (fileType === content.fileTypeMobi) {
    return content.statusImportedMobi;
  }

  if (fileType === content.fileTypeDocx) {
    return content.statusImportedDocx;
  }

  if (fileType === content.fileTypeDoc) {
    return content.statusImportedDoc;
  }

  if (fileType === content.fileTypePdf) {
    return content.statusImportedPdf;
  }

  return content.statusImported;
}

function getFileTypeLabel(fileType, content) {
  if (fileType === "EPUB" || fileType === content.fileTypeEpub) {
    return content.fileTypeEpub;
  }

  if (fileType === "MOBI" || fileType === content.fileTypeMobi) {
    return content.fileTypeMobi;
  }

  if (fileType === "DOCX" || fileType === content.fileTypeDocx) {
    return content.fileTypeDocx;
  }

  if (fileType === "DOC" || fileType === content.fileTypeDoc) {
    return content.fileTypeDoc;
  }

  if (fileType === "PDF" || fileType === content.fileTypePdf) {
    return content.fileTypePdf;
  }

  return content.fileTypeTxt;
}

function isSupportedConversionSourceFormat(fileType, content) {
  return SUPPORTED_CONVERSION_SOURCE_FORMATS.includes(getFileTypeLabel(fileType, content));
}

function getImportErrorStatus(error, content) {
  const errorCode = String(error?.code || "");
  const errorMessage = String(error?.message || "").toLowerCase();

  if (errorCode === "unsupported-scanned-pdf") {
    return content.statusUnsupportedScannedPdf;
  }

  if (errorCode === "file-too-large" || /too large/.test(errorMessage)) {
    return content.statusImportTooLarge;
  }

  if (errorCode === "empty-document" || /empty|no readable text/.test(errorMessage)) {
    return content.statusImportEmpty;
  }

  if (errorCode === "unsupported-format") {
    return content.statusUnsupported;
  }

  return content.statusImportFailed;
}

function isChineseLanguage(language) {
  const normalized = (language || "").toLowerCase();
  return normalized.startsWith("zh") || normalized.startsWith("cmn") || normalized.startsWith("yue");
}

function isJapaneseLanguage(language) {
  return (language || "").toLowerCase().startsWith("ja");
}

function isFrenchLanguage(language) {
  return (language || "").toLowerCase().startsWith("fr");
}

function detectLanguage(text) {
  const rawText = String(text || "");

  if (/[\u3040-\u30ff\u31f0-\u31ff\u30fc「」『』・]/.test(rawText)) {
    return "ja-JP";
  }

  if (/(です|ます|でした|ません|する|して|した|ない|ある|いる|から|まで|こと|もの|よう|さん|ちゃん|くん)/.test(rawText)) {
    return "ja-JP";
  }

  if (/[\u3400-\u9fff\uf900-\ufaff]/.test(rawText)) {
    return "zh-CN";
  }

  const normalized = ` ${rawText.toLowerCase()} `;
  let frenchScore = 0;

  if (/[àâæçéèêëîïôœùûüÿ]/i.test(normalized)) {
    frenchScore += 3;
  }

  const frenchWordMatches = normalized.match(
    /\b(?:bonjour|merci|avec|pour|dans|mais|donc|comme|vous|nous|elles|ils|elle|est|sont|pas|plus|tout|tous|une|des|les|qui|que|sur|chez|sans|encore|livre|chapitre|histoire|lecture|fran[cç]ais)\b/g
  );
  frenchScore += frenchWordMatches ? frenchWordMatches.length : 0;

  const frenchApostropheMatches = normalized.match(
    /\b(?:l|d|c|j|n|m|t|s|qu)'[a-zàâæçéèêëîïôœùûüÿ]+/g
  );
  frenchScore += frenchApostropheMatches ? frenchApostropheMatches.length : 0;

  const englishMatches = normalized.match(
    /\b(?:the|and|with|for|this|that|you|your|from|into|when|where|have|not|are|is|was|were|book|chapter|story|reading|hello|welcome)\b/g
  );
  const englishScore = englishMatches ? englishMatches.length : 0;

  if (frenchScore >= 3 && frenchScore >= englishScore) {
    return "fr-FR";
  }

  return "en-US";
}

function getLanguageLabel(language) {
  if (isChineseLanguage(language)) {
    return "中文";
  }

  if (isJapaneseLanguage(language)) {
    return "日本語";
  }

  if (isFrenchLanguage(language)) {
    return "Français";
  }

  return "English";
}

function getSupportedReadingLanguageBase(language) {
  const normalized = String(language || "").trim().toLowerCase();

  if (normalized.startsWith("fr")) {
    return "fr";
  }

  if (normalized.startsWith("ja")) {
    return "ja";
  }

  if (
    normalized.startsWith("zh") ||
    normalized.startsWith("cmn") ||
    normalized.startsWith("yue")
  ) {
    return PREFERRED_READING_LANGUAGES.ZH;
  }

  if (normalized.startsWith("en")) {
    return PREFERRED_READING_LANGUAGES.EN;
  }

  return "other";
}

function inferPreferredReadingLanguage(mode, fallbackLanguage = PREFERRED_READING_LANGUAGES.EN) {
  const normalizedMode = normalizeTranslationPlaybackMode(mode);

  if (
    normalizedMode === TRANSLATION_PLAYBACK_MODES.ZH_TO_EN ||
    normalizedMode === TRANSLATION_PLAYBACK_MODES.PREFER_EN
  ) {
    return PREFERRED_READING_LANGUAGES.EN;
  }

  if (
    normalizedMode === TRANSLATION_PLAYBACK_MODES.EN_TO_ZH ||
    normalizedMode === TRANSLATION_PLAYBACK_MODES.PREFER_ZH
  ) {
    return PREFERRED_READING_LANGUAGES.ZH;
  }

  return fallbackLanguage;
}

function sanitizePreferredReadingLanguage(value, fallbackLanguage = PREFERRED_READING_LANGUAGES.EN) {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === PREFERRED_READING_LANGUAGES.ZH) {
    return PREFERRED_READING_LANGUAGES.ZH;
  }

  if (normalized === PREFERRED_READING_LANGUAGES.EN) {
    return PREFERRED_READING_LANGUAGES.EN;
  }

  return fallbackLanguage;
}

function getTranslationPlaybackModeForPreferredLanguage(preferredReadingLanguage) {
  return sanitizePreferredReadingLanguage(preferredReadingLanguage) === PREFERRED_READING_LANGUAGES.ZH
    ? TRANSLATION_PLAYBACK_MODES.PREFER_ZH
    : TRANSLATION_PLAYBACK_MODES.PREFER_EN;
}

function getPreferredReadingLanguageLabel(preferredReadingLanguage, content) {
  return sanitizePreferredReadingLanguage(preferredReadingLanguage) === PREFERRED_READING_LANGUAGES.ZH
    ? content.understandInChinese
    : content.understandInEnglish;
}

function collectBlocksFromChunkWindow(blocks, chunkPlan, startChunkIndex, chunkCount) {
  if (!Array.isArray(blocks) || !Array.isArray(chunkPlan) || chunkCount <= 0) {
    return [];
  }

  const collectedBlocks = [];
  const seenIndexes = new Set();

  chunkPlan
    .slice(startChunkIndex, startChunkIndex + chunkCount)
    .forEach((chunk) => {
      for (
        let blockIndex = chunk.startBlockIndex;
        blockIndex <= chunk.endBlockIndex;
        blockIndex += 1
      ) {
        if (seenIndexes.has(blockIndex) || !blocks[blockIndex]) {
          continue;
        }

        seenIndexes.add(blockIndex);
        collectedBlocks.push(blocks[blockIndex]);
      }
    });

  return collectedBlocks;
}

function shouldPreferUnderstandingLanguage(blockLanguage, preferredReadingLanguage) {
  const sourceLanguageBase = getSupportedReadingLanguageBase(blockLanguage);
  const preferredBase = sanitizePreferredReadingLanguage(preferredReadingLanguage);

  return (
    (
      sourceLanguageBase === PREFERRED_READING_LANGUAGES.EN ||
      sourceLanguageBase === "fr" ||
      sourceLanguageBase === PREFERRED_READING_LANGUAGES.ZH ||
      sourceLanguageBase === "ja"
    ) &&
    sourceLanguageBase !== preferredBase
  );
}

function getMatchingBrowserSpeechConfig(language, selectedVoiceURI = "") {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    return {
      voice: null,
      utteranceLang: language || "en-US"
    };
  }

  const voices = window.speechSynthesis.getVoices() || [];

  if (!voices.length) {
    return {
      voice: null,
      utteranceLang: language || "en-US"
    };
  }

  return resolveBrowserSpeechConfig({
    availableVoices: voices,
    contentLanguage: language,
    selectedVoiceURI
  });
}

function sanitizeCoverImage(value) {
  return typeof value === "string" && value.startsWith("data:image/")
    ? value
    : "";
}

function escapeSvgText(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function splitTitleIntoLines(title, maxLines = 3) {
  const words = String(title || "PageTone")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);

  if (!words.length) {
    return ["PageTone"];
  }

  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (nextLine.length > 14 && currentLine) {
      if (lines.length < maxLines) {
        lines.push(currentLine);
      }
      currentLine = word;
      return;
    }

    currentLine = nextLine;
  });

  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }

  return lines.slice(0, maxLines);
}

function createGeneratedCoverDataUrl(title = "PageTone") {
  const safeTitle = String(title || "PageTone").trim() || "PageTone";
  let hue = 0;

  for (let index = 0; index < safeTitle.length; index += 1) {
    hue = (hue * 31 + safeTitle.charCodeAt(index)) % 360;
  }

  const accentHue = (hue + 48) % 360;
  const initial = escapeSvgText(safeTitle.charAt(0).toUpperCase());
  const titleLines = splitTitleIntoLines(safeTitle)
    .map((line, index) => {
      const y = 258 + index * 36;
      return `<text x="42" y="${y}" fill="#f8fffe" font-family="Nunito, Arial, sans-serif" font-size="26" font-weight="800">${escapeSvgText(line)}</text>`;
    })
    .join("");
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="320" height="480" viewBox="0 0 320 480">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="hsl(${hue} 74% 26%)"/>
          <stop offset="100%" stop-color="hsl(${accentHue} 78% 44%)"/>
        </linearGradient>
        <radialGradient id="glow" cx="78%" cy="18%" r="58%">
          <stop offset="0%" stop-color="rgba(255,255,255,0.42)"/>
          <stop offset="100%" stop-color="rgba(255,255,255,0)"/>
        </radialGradient>
      </defs>
      <rect width="320" height="480" rx="34" fill="url(#bg)"/>
      <rect x="22" y="22" width="276" height="436" rx="26" fill="rgba(7,27,36,0.14)" stroke="rgba(255,255,255,0.28)"/>
      <circle cx="236" cy="96" r="96" fill="url(#glow)"/>
      <rect x="40" y="42" width="110" height="110" rx="24" fill="rgba(255,255,255,0.14)" stroke="rgba(255,255,255,0.18)"/>
      <text x="95" y="114" text-anchor="middle" fill="#ffffff" font-family="Orbitron, Arial, sans-serif" font-size="54" font-weight="700">${initial}</text>
      <text x="42" y="198" fill="rgba(240,255,253,0.76)" font-family="Nunito, Arial, sans-serif" font-size="13" font-weight="900" letter-spacing="2.2">ECHO LIBRARY</text>
      ${titleLines}
      <rect x="42" y="374" width="152" height="10" rx="999" fill="rgba(255,255,255,0.18)"/>
      <rect x="42" y="374" width="94" height="10" rx="999" fill="rgba(255,255,255,0.78)"/>
      <text x="42" y="420" fill="rgba(240,255,253,0.8)" font-family="Nunito, Arial, sans-serif" font-size="14" font-weight="700">Roots &amp; Bridge</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function fileNameWithoutExtension(fileName) {
  return fileName.replace(/\.[^.]+$/, "").trim();
}

function getCoverSeedFromFileName(fileName, fallbackTitle = "PageTone") {
  const normalizedFileName = fileNameWithoutExtension(String(fileName || "").trim());
  const fallbackSeed = String(fallbackTitle || "PageTone").trim() || "PageTone";
  const source = normalizedFileName || fallbackSeed;
  const chineseMatch = source.match(/[\u3400-\u9fff]+/);

  if (chineseMatch?.[0]) {
    return chineseMatch[0].slice(0, 4);
  }

  return source.split(/[\s._-]+/).find(Boolean) || fallbackSeed;
}

function createGeneratedFileCoverDataUrl(fileName, fallbackTitle = "PageTone") {
  return createGeneratedCoverDataUrl(getCoverSeedFromFileName(fileName, fallbackTitle));
}

function createBookId(seed = "book") {
  const normalizedSeed = seed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);

  const randomPart = Math.random().toString(36).slice(2, 8);
  return `${normalizedSeed || "book"}-${Date.now().toString(36)}-${randomPart}`;
}

function normalizeExternalSelectionText(text) {
  if (typeof text !== "string") {
    return "";
  }

  return text
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function clampIndex(index, length) {
  if (!length) {
    return 0;
  }

  return Math.min(Math.max(index, 0), length - 1);
}

function fillTemplate(template, replacements) {
  return Object.entries(replacements).reduce(
    (result, [key, value]) => result.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)),
    template
  );
}

function measureLocalStorageBytes(value) {
  return String(value || "").length * 2;
}

function getOriginLocalStorageUsageBytes() {
  if (typeof window === "undefined") {
    return 0;
  }

  try {
    let totalBytes = 0;

    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);

      if (!key) {
        continue;
      }

      totalBytes += measureLocalStorageBytes(key);
      totalBytes += measureLocalStorageBytes(window.localStorage.getItem(key) || "");
    }

    return totalBytes;
  } catch (error) {
    return 0;
  }
}

function buildShelfStorageStats(books) {
  const serializedBooks = JSON.stringify(Array.isArray(books) ? books : []);
  const bookBytes = measureLocalStorageBytes(serializedBooks);
  const originUsageBytes = getOriginLocalStorageUsageBytes();
  const totalBudgetBytes = APPROX_LOCAL_STORAGE_BUDGET_BYTES;
  const usedBytes = Math.max(originUsageBytes, bookBytes);

  return {
    totalBudgetBytes,
    usedBytes,
    remainingBytes: Math.max(totalBudgetBytes - usedBytes, 0),
    bookBytes
  };
}

function formatStorageMegabytes(bytes) {
  return (Math.max(0, Number(bytes) || 0) / (1024 * 1024)).toFixed(1);
}

function clampNumber(value, min, max) {
  if (!Number.isFinite(value)) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

function isDevDiagnosticsEnabled() {
  if (process.env.NODE_ENV !== "production") {
    return true;
  }

  if (typeof window === "undefined") {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);

    if (params.get(DEV_DIAGNOSTICS_QUERY_PARAM) === "1") {
      return true;
    }

    return window.localStorage.getItem(DEV_DIAGNOSTICS_FLAG_KEY) === "1";
  } catch (error) {
    return false;
  }
}

function getNearEndPrefetchThreshold(totalSeconds) {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return NEAR_END_PREFETCH_SECONDS;
  }

  return Math.max(15, Math.min(75, totalSeconds * 0.3));
}

function formatPlaybackTime(seconds) {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function canUseBrowserSpeechFallback(error) {
  if (!error) {
    return true;
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
      ? error
      : JSON.stringify(error);

  return !/no readable text|segments are required|too many segments|too large to synthesize safely/i.test(
    message || ""
  );
}

function sanitizeVoicePreferences(rawValue) {
  return {
    selectedEnglishVoiceURI:
      typeof rawValue?.selectedEnglishVoiceURI === "string"
        ? rawValue.selectedEnglishVoiceURI
        : "",
    selectedFrenchVoiceURI:
      typeof rawValue?.selectedFrenchVoiceURI === "string"
        ? rawValue.selectedFrenchVoiceURI
        : "",
    selectedChineseVoiceURI:
      typeof rawValue?.selectedChineseVoiceURI === "string"
        ? rawValue.selectedChineseVoiceURI
        : ""
  };
}

function mergeVoicePreferences(primaryValue, fallbackValue) {
  const primary = sanitizeVoicePreferences(primaryValue);
  const fallback = sanitizeVoicePreferences(fallbackValue);

  return {
    selectedEnglishVoiceURI:
      primary.selectedEnglishVoiceURI || fallback.selectedEnglishVoiceURI || "",
    selectedFrenchVoiceURI:
      primary.selectedFrenchVoiceURI || fallback.selectedFrenchVoiceURI || "",
    selectedChineseVoiceURI:
      primary.selectedChineseVoiceURI || fallback.selectedChineseVoiceURI || ""
  };
}

function sanitizeTranslationPlaybackMode(value) {
  return normalizeTranslationPlaybackMode(value);
}

function getPersistedVoicePreferences() {
  if (typeof window === "undefined") {
    return sanitizeVoicePreferences(null);
  }

  try {
    const voicePreferences = sanitizeVoicePreferences(
      JSON.parse(window.localStorage.getItem(VOICE_PREFERENCES_KEY) || "{}")
    );

    const readerStatePreferences = sanitizeVoicePreferences(
      JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}")
    );

    return mergeVoicePreferences(voicePreferences, readerStatePreferences);
  } catch (error) {
    return sanitizeVoicePreferences(null);
  }
}

function estimateBlockDurationSeconds(text, language, rate) {
  const normalized = (text || "").replace(/\s+/g, " ").trim();

  if (!normalized) {
    return 0;
  }

  const cjkChars = (normalized.match(/[\u3400-\u9fff\uf900-\ufaff]/g) || []).length;
  const latinWords = (normalized.match(/[A-Za-z0-9]+(?:['’-][A-Za-z0-9]+)*/g) || []).length;
  const punctuationPauses = (normalized.match(/[.,!?;:，。！？；：、]/g) || []).length;
  const compactLength = normalized.replace(/\s+/g, "").length;
  const otherChars = Math.max(compactLength - cjkChars, 0);

  let estimatedSeconds =
    cjkChars * 0.26 +
    latinWords * 0.34 +
    punctuationPauses * 0.11 +
    otherChars * (isChineseLanguage(language) ? 0.015 : 0.01);

  if (estimatedSeconds < 1.2) {
    estimatedSeconds = 1.2;
  }

  return estimatedSeconds / Math.max(rate || 1, 0.5);
}

function buildChapterProgressModel(blocks, rate, durationOverrides = []) {
  let elapsedCursor = 0;

  const items = blocks.map((block, index) => {
    const overrideDuration = durationOverrides[index];
    const durationSeconds = Number.isFinite(overrideDuration) && overrideDuration > 0
      ? overrideDuration
      : estimateBlockDurationSeconds(
          block?.text || "",
          block?.language || detectLanguage(block?.text || ""),
          rate
        );
    const item = {
      index,
      startSeconds: elapsedCursor,
      durationSeconds,
      endSeconds: elapsedCursor + durationSeconds,
      textLength: (block?.text || "").length
    };

    elapsedCursor += durationSeconds;
    return item;
  });

  return {
    totalSeconds: elapsedCursor,
    items
  };
}

function getElapsedForBlockOffset(model, blocks, blockIndex, offsetChars = 0) {
  const item = model.items[blockIndex];

  if (!item) {
    return 0;
  }

  const textLength = blocks[blockIndex]?.text?.length || 0;
  const offsetRatio = textLength
    ? clampNumber(offsetChars / textLength, 0, 1)
    : 0;

  return item.startSeconds + item.durationSeconds * offsetRatio;
}

function getRemainingBlockDurationSeconds(model, blocks, blockIndex, offsetChars = 0) {
  const item = model.items[blockIndex];

  if (!item) {
    return 0;
  }

  const textLength = blocks[blockIndex]?.text?.length || 0;

  if (!textLength) {
    return item.durationSeconds;
  }

  const remainingRatio = clampNumber((textLength - offsetChars) / textLength, 0, 1);
  return item.durationSeconds * remainingRatio;
}

function findSeekTarget(model, blocks, targetSeconds) {
  if (!model.items.length) {
    return {
      blockIndex: 0,
      offsetChars: 0,
      elapsedSeconds: 0
    };
  }

  const safeTarget = clampNumber(targetSeconds, 0, model.totalSeconds);
  const lastItem = model.items[model.items.length - 1];
  const targetItem = model.items.find((item) => safeTarget <= item.endSeconds) || lastItem;
  const blockText = blocks[targetItem.index]?.text || "";
  const textLength = blockText.length;

  if (!textLength || !targetItem.durationSeconds) {
    return {
      blockIndex: targetItem.index,
      offsetChars: 0,
      elapsedSeconds: targetItem.startSeconds
    };
  }

  const secondsIntoBlock = clampNumber(
    safeTarget - targetItem.startSeconds,
    0,
    targetItem.durationSeconds
  );
  const ratio = secondsIntoBlock / targetItem.durationSeconds;
  let offsetChars = Math.round(textLength * ratio);

  if (offsetChars >= textLength) {
    offsetChars = Math.max(textLength - 1, 0);
  }

  return {
    blockIndex: targetItem.index,
    offsetChars,
    elapsedSeconds: safeTarget
  };
}

function getInitialReaderState(fallbackText, uiLanguage = "en") {
  const persistedVoicePreferences = getPersistedVoicePreferences();
  const defaultPreferredReadingLanguage = sanitizePreferredReadingLanguage(
    String(uiLanguage || "").toLowerCase().startsWith("zh")
      ? PREFERRED_READING_LANGUAGES.ZH
      : PREFERRED_READING_LANGUAGES.EN
  );

  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    const savedChapters = Array.isArray(saved.chapters)
      ? saved.chapters.filter(
          (chapter) =>
            chapter &&
            typeof chapter.title === "string" &&
            typeof chapter.text === "string"
        )
      : [];
    const normalizedChapters = savedChapters.length
      ? savedChapters
      : [
          {
            id: "chapter-0",
            title: "Demo",
            text: typeof saved.text === "string" && saved.text.trim() ? saved.text : fallbackText
          }
        ];

    return {
      activeBookId: typeof saved.activeBookId === "string" ? saved.activeBookId : "",
      bookTitle: typeof saved.bookTitle === "string" && saved.bookTitle.trim()
        ? saved.bookTitle
        : "PageTone",
      coverImage: sanitizeCoverImage(saved.coverImage),
      chapters: normalizedChapters,
      currentIndex: Number.isInteger(saved.currentIndex) ? saved.currentIndex : 0,
      currentChapterIndex: Number.isInteger(saved.currentChapterIndex) ? saved.currentChapterIndex : 0,
      rate: typeof saved.rate === "number" ? saved.rate : 1.0,
      preferredReadingLanguage: sanitizePreferredReadingLanguage(
        saved.preferredReadingLanguage,
        inferPreferredReadingLanguage(saved.translationPlaybackMode, defaultPreferredReadingLanguage)
      ),
      translationPlaybackMode: sanitizeTranslationPlaybackMode(saved.translationPlaybackMode),
      ...mergeVoicePreferences(saved, persistedVoicePreferences),
      lastImportedFileName:
        typeof saved.lastImportedFileName === "string" ? saved.lastImportedFileName : "",
      lastImportedFileType:
        typeof saved.lastImportedFileType === "string" ? saved.lastImportedFileType : "",
      playbackSnapshot: sanitizePlaybackSnapshot(saved.playbackSnapshot)
    };
  } catch (error) {
    return {
      activeBookId: "",
      bookTitle: "PageTone",
      coverImage: "",
      chapters: [
        {
          id: "chapter-0",
          title: "Demo",
          text: fallbackText
        }
      ],
      currentIndex: 0,
      currentChapterIndex: 0,
      rate: 1.0,
      preferredReadingLanguage: defaultPreferredReadingLanguage,
      translationPlaybackMode: TRANSLATION_PLAYBACK_MODES.OFF,
      ...persistedVoicePreferences,
      lastImportedFileName: "",
      lastImportedFileType: "",
      playbackSnapshot: null
    };
  }
}

function sanitizePlaybackSnapshot(rawSnapshot) {
  if (!rawSnapshot || typeof rawSnapshot !== "object") {
    return null;
  }

  const updatedAt = typeof rawSnapshot.updatedAt === "number" ? rawSnapshot.updatedAt : 0;

  if (!updatedAt || Date.now() - updatedAt > PLAYBACK_SNAPSHOT_MAX_AGE_MS) {
    return null;
  }

  const phase = typeof rawSnapshot.phase === "string" ? rawSnapshot.phase : "idle";
  const audioMode =
    rawSnapshot.audioMode === "ai-audio"
      ? "ai-audio"
      : rawSnapshot.audioMode === "browser-fallback"
      ? "browser-fallback"
      : "none";
  const pauseOrigin =
    rawSnapshot.pauseOrigin === "user"
      ? "user"
      : rawSnapshot.pauseOrigin === "system"
      ? "system"
      : "none";
  const visibleExcerptContext =
    rawSnapshot.visibleExcerptContext && typeof rawSnapshot.visibleExcerptContext === "object"
      ? {
          previous:
            typeof rawSnapshot.visibleExcerptContext.previous === "string"
              ? rawSnapshot.visibleExcerptContext.previous
              : "",
          current:
            typeof rawSnapshot.visibleExcerptContext.current === "string"
              ? rawSnapshot.visibleExcerptContext.current
              : "",
          next:
            typeof rawSnapshot.visibleExcerptContext.next === "string"
              ? rawSnapshot.visibleExcerptContext.next
              : ""
        }
      : {
          previous: "",
          current: "",
          next: ""
        };

  return {
    version: Number.isInteger(rawSnapshot.version)
      ? rawSnapshot.version
      : PLAYBACK_SNAPSHOT_VERSION,
    activeBookId:
      typeof rawSnapshot.activeBookId === "string" ? rawSnapshot.activeBookId : "",
    phase,
    audioMode,
    pauseOrigin,
    userPaused:
      typeof rawSnapshot.userPaused === "boolean"
        ? rawSnapshot.userPaused
        : pauseOrigin === "user",
    shouldResume: Boolean(rawSnapshot.shouldResume) && pauseOrigin !== "user",
    backgroundInterrupted: Boolean(rawSnapshot.backgroundInterrupted),
    chapterIndex: Number.isInteger(rawSnapshot.chapterIndex) ? rawSnapshot.chapterIndex : 0,
    blockIndex: Number.isInteger(rawSnapshot.blockIndex) ? rawSnapshot.blockIndex : 0,
    offsetChars: Number.isInteger(rawSnapshot.offsetChars) ? rawSnapshot.offsetChars : 0,
    speechChunkIndex:
      Number.isInteger(rawSnapshot.speechChunkIndex) ? rawSnapshot.speechChunkIndex : 0,
    audioCacheKey:
      typeof rawSnapshot.audioCacheKey === "string" ? rawSnapshot.audioCacheKey : "",
    audioStreamUrl: normalizeEchoAudioStreamUrl(
      typeof rawSnapshot.audioStreamUrl === "string" ? rawSnapshot.audioStreamUrl : "",
      typeof rawSnapshot.audioCacheKey === "string" ? rawSnapshot.audioCacheKey : ""
    ),
    elapsedSeconds:
      typeof rawSnapshot.elapsedSeconds === "number" && Number.isFinite(rawSnapshot.elapsedSeconds)
        ? Math.max(0, rawSnapshot.elapsedSeconds)
        : 0,
    totalSeconds:
      typeof rawSnapshot.totalSeconds === "number" && Number.isFinite(rawSnapshot.totalSeconds)
        ? Math.max(0, rawSnapshot.totalSeconds)
        : 0,
    visibleExcerptContext,
    updatedAt
  };
}

function sanitizeChapters(rawChapters) {
  return Array.isArray(rawChapters)
    ? rawChapters
        .filter(
          (chapter) =>
            chapter &&
            typeof chapter.title === "string" &&
            typeof chapter.text === "string"
        )
        .map((chapter, index) => ({
          id: chapter.id || `chapter-${index}`,
          title: chapter.title,
          text: chapter.text
        }))
    : [];
}

function normalizeChapterTitleForCompare(value) {
  return String(value || "")
    .replace(/\.[^.]+$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function getDisplayChapterTitle(chapter, index, options = {}) {
  const chapterLabel = options.chapterLabel || "Chapter";
  const rawTitle = typeof chapter?.title === "string" ? chapter.title.trim() : "";
  const fallbackTitle = `${chapterLabel} ${index + 1}`;

  if (!rawTitle) {
    return fallbackTitle;
  }

  const normalizedTitle = normalizeChapterTitleForCompare(rawTitle);
  const normalizedBookTitle = normalizeChapterTitleForCompare(options.bookTitle);
  const normalizedFileName = normalizeChapterTitleForCompare(options.fileName);
  const normalizedWorkspaceTitle = normalizeChapterTitleForCompare(options.workspaceTitle);
  const shouldTreatAsBookTitle =
    (normalizedBookTitle && normalizedTitle === normalizedBookTitle) ||
    (normalizedFileName && normalizedTitle === normalizedFileName) ||
    (normalizedWorkspaceTitle && normalizedTitle === normalizedWorkspaceTitle);

  return shouldTreatAsBookTitle ? fallbackTitle : rawTitle;
}

function sanitizeSavedBook(rawBook) {
  if (!rawBook || typeof rawBook !== "object") {
    return null;
  }

  const chapters = sanitizeChapters(rawBook.chapters);

  if (!chapters.length) {
    return null;
  }

  const fileType = typeof rawBook.fileType === "string" ? rawBook.fileType : "";
  const lastImportedFileName =
    typeof rawBook.lastImportedFileName === "string" ? rawBook.lastImportedFileName : "";
  const bookTitle =
    typeof rawBook.bookTitle === "string" && rawBook.bookTitle.trim()
      ? rawBook.bookTitle
      : "PageTone";
  const sanitizedCoverImage = sanitizeCoverImage(rawBook.coverImage);
  const coverImage =
    fileType === "EPUB"
      ? sanitizedCoverImage || createGeneratedFileCoverDataUrl(lastImportedFileName, bookTitle)
      : createGeneratedFileCoverDataUrl(lastImportedFileName, bookTitle);

  return {
    id: typeof rawBook.id === "string" ? rawBook.id : `book-${Date.now()}`,
    bookTitle,
    coverImage,
    fileType,
    lastImportedFileName,
    currentChapterIndex: Number.isInteger(rawBook.currentChapterIndex) ? rawBook.currentChapterIndex : 0,
    currentIndex: Number.isInteger(rawBook.currentIndex) ? rawBook.currentIndex : 0,
    rate: typeof rawBook.rate === "number" ? rawBook.rate : 1.0,
    translationPlaybackMode: sanitizeTranslationPlaybackMode(rawBook.translationPlaybackMode),
    selectedEnglishVoiceURI:
      typeof rawBook.selectedEnglishVoiceURI === "string" ? rawBook.selectedEnglishVoiceURI : "",
    selectedFrenchVoiceURI:
      typeof rawBook.selectedFrenchVoiceURI === "string" ? rawBook.selectedFrenchVoiceURI : "",
    selectedChineseVoiceURI:
      typeof rawBook.selectedChineseVoiceURI === "string" ? rawBook.selectedChineseVoiceURI : "",
    updatedAt: typeof rawBook.updatedAt === "number" ? rawBook.updatedAt : Date.now(),
    chapters
  };
}

function getInitialSavedBooks() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(SAVED_BOOKS_KEY) || "[]");
    return Array.isArray(parsed)
      ? parsed
          .map(sanitizeSavedBook)
          .filter(Boolean)
          .sort((a, b) => b.updatedAt - a.updatedAt)
          .slice(0, MAX_SAVED_BOOKS)
      : [];
  } catch (error) {
    return [];
  }
}

function persistSavedBooks(books) {
  const sorted = [...books]
    .map(sanitizeSavedBook)
    .filter(Boolean)
    .sort((a, b) => b.updatedAt - a.updatedAt);

  if (!sorted.length) {
    window.localStorage.setItem(SAVED_BOOKS_KEY, JSON.stringify([]));
    return {
      books: [],
      trimmed: false,
      saved: true
    };
  }

  let limited = sorted.slice(0, MAX_SAVED_BOOKS);

  while (limited.length > 0) {
    try {
      window.localStorage.setItem(SAVED_BOOKS_KEY, JSON.stringify(limited));
      return {
        books: limited,
        trimmed: sorted.length > limited.length,
        saved: true
      };
    } catch (error) {
      if (!limited.length) {
        break;
      }
      limited = limited.slice(0, -1);
    }
  }

  const fallbackBooks = getInitialSavedBooks();
  return {
    books: fallbackBooks,
    trimmed: sorted.length > MAX_SAVED_BOOKS,
    saved: false
  };
}

function countReplacementCharacters(text) {
  return (text.match(/\uFFFD/g) || []).length;
}

function decodeBuffer(buffer, encoding) {
  return new TextDecoder(encoding, { fatal: false }).decode(buffer);
}

function normalizeZipPath(path) {
  return path.replace(/^\/+/, "").replace(/\\/g, "/");
}

function getMimeTypeFromPath(path) {
  const normalizedPath = normalizeZipPath(path).toLowerCase();

  if (normalizedPath.endsWith(".png")) {
    return "image/png";
  }
  if (normalizedPath.endsWith(".jpg") || normalizedPath.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (normalizedPath.endsWith(".webp")) {
    return "image/webp";
  }
  if (normalizedPath.endsWith(".gif")) {
    return "image/gif";
  }
  if (normalizedPath.endsWith(".svg")) {
    return "image/svg+xml";
  }

  return "image/jpeg";
}

function getDirectoryPath(path) {
  const normalized = normalizeZipPath(path);
  const lastSlash = normalized.lastIndexOf("/");
  return lastSlash >= 0 ? normalized.slice(0, lastSlash + 1) : "";
}

function resolveZipPath(basePath, relativePath) {
  const normalizedBase = getDirectoryPath(basePath);
  return normalizeZipPath(new URL(relativePath, `https://reader.lab/${normalizedBase}`).pathname);
}

function parseMarkup(source, mimeType = "application/xml") {
  return new window.DOMParser().parseFromString(source, mimeType);
}

function getFirstElementTextByNames(root, names) {
  const lowered = names.map((name) => name.toLowerCase());
  const allElements = Array.from(root.getElementsByTagName("*"));
  const match = allElements.find((element) => {
    const localName = (element.localName || element.nodeName || "").toLowerCase();
    return lowered.includes(localName);
  });

  return match?.textContent?.replace(/\s+/g, " ").trim() || "";
}

function findZipEntry(zip, path) {
  const normalized = normalizeZipPath(path);
  const decoded = decodeURIComponent(normalized);
  const encoded = normalized
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  return zip.file(normalized) || zip.file(decoded) || zip.file(encoded) || null;
}

async function readZipEntryAsText(zip, path) {
  const entry = findZipEntry(zip, path);

  if (!entry) {
    throw new Error(`Missing EPUB entry: ${path}`);
  }

  return entry.async("string");
}

async function readZipEntryAsDataUrl(zip, path, mediaType = "") {
  const entry = findZipEntry(zip, path);

  if (!entry) {
    return "";
  }

  const base64 = await entry.async("base64");
  const mimeType = mediaType || getMimeTypeFromPath(path);
  const dataUrl = `data:${mimeType};base64,${base64}`;

  return dataUrl.length <= MAX_COVER_DATA_URL_LENGTH ? dataUrl : "";
}

async function extractEpubCover(zip, rootfilePath, packageDoc, manifestMap, packageTitle) {
  const manifestItems = Array.from(manifestMap.values());
  const coverMetaId = Array.from(packageDoc.querySelectorAll("metadata > meta")).find((item) =>
    (item.getAttribute("name") || "").toLowerCase() === "cover"
  )?.getAttribute("content");
  const epub3CoverItem = manifestItems.find((item) =>
    (item.properties || "").split(/\s+/).includes("cover-image")
  );
  const coverMetaItem = coverMetaId ? manifestMap.get(coverMetaId) : null;
  const namedCoverItem = manifestItems.find(
    (item) =>
      /image\//i.test(item.mediaType || "") &&
      /cover/i.test(`${item.id || ""} ${item.href || ""}`)
  );
  const directCoverItem = epub3CoverItem || coverMetaItem || namedCoverItem || null;

  if (directCoverItem?.href) {
    const coverPath = resolveZipPath(rootfilePath, directCoverItem.href);
    const coverDataUrl = await readZipEntryAsDataUrl(zip, coverPath, directCoverItem.mediaType);

    if (coverDataUrl) {
      return coverDataUrl;
    }
  }

  const guideCoverHref = Array.from(packageDoc.querySelectorAll("guide > reference")).find((item) =>
    /cover/i.test(item.getAttribute("type") || "")
  )?.getAttribute("href");

  if (guideCoverHref) {
    const guidePath = resolveZipPath(rootfilePath, guideCoverHref);
    const coverDoc = parseMarkup(await readZipEntryAsText(zip, guidePath), "application/xhtml+xml");
    const imageSource =
      coverDoc.querySelector("img")?.getAttribute("src") ||
      coverDoc.querySelector("image")?.getAttribute("href") ||
      coverDoc.querySelector("image")?.getAttribute("xlink:href");

    if (imageSource) {
      const imagePath = resolveZipPath(guidePath, imageSource);
      const coverDataUrl = await readZipEntryAsDataUrl(zip, imagePath, getMimeTypeFromPath(imagePath));

      if (coverDataUrl) {
        return coverDataUrl;
      }
    }
  }

  return createGeneratedCoverDataUrl(packageTitle);
}

function extractMarkupText(markup) {
  const doc = parseMarkup(markup, "application/xhtml+xml");
  const parserError = doc.querySelector("parsererror");
  const htmlDoc = parserError ? parseMarkup(markup, "text/html") : doc;
  const body = htmlDoc.body || htmlDoc.querySelector("body");

  if (!body) {
    return "";
  }

  const selectors = [
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "blockquote",
    "li",
    "figcaption",
    "pre",
    "td",
    "th"
  ];

  function extractNodeText(node) {
    if (!node || shouldSkipMarkupNode(node)) {
      return "";
    }

    const clone =
      typeof node.cloneNode === "function"
        ? node.cloneNode(true)
        : null;

    if (clone && typeof clone.querySelectorAll === "function") {
      Array.from(clone.querySelectorAll("*")).forEach((child) => {
        if (shouldSkipMarkupNode(child)) {
          child.remove();
        }
      });
    }

    const sourceNode = clone || node;
    const normalized = normalizeNarrationText(sourceNode.textContent || "");

    return isSkippableStructuralText(normalized) ? "" : normalized;
  }

  const segments = Array.from(body.querySelectorAll(selectors.join(",")))
    .map((node) => extractNodeText(node))
    .filter(Boolean);

  if (segments.length) {
    return segments.join("\n\n");
  }

  const fallbackText = sanitizeNarrationSourceText(body.textContent || "");
  return isSkippableStructuralText(fallbackText) ? "" : fallbackText;
}

async function extractEpubBook(file, content) {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const containerXML = await readZipEntryAsText(zip, "META-INF/container.xml");
  const containerDoc = parseMarkup(containerXML, "application/xml");
  const rootfilePath = containerDoc.querySelector("rootfile")?.getAttribute("full-path");

  if (!rootfilePath) {
    throw new Error("EPUB package file not found.");
  }

  const packageXML = await readZipEntryAsText(zip, rootfilePath);
  const packageDoc = parseMarkup(packageXML, "application/xml");
  const manifestMap = new Map(
    Array.from(packageDoc.querySelectorAll("manifest > item")).map((item) => [
      item.getAttribute("id"),
      {
        id: item.getAttribute("id") || "",
        href: item.getAttribute("href") || "",
        mediaType: item.getAttribute("media-type") || "",
        properties: item.getAttribute("properties") || ""
      }
    ])
  );
  const spineIds = Array.from(packageDoc.querySelectorAll("spine > itemref"))
    .map((item) => item.getAttribute("idref"))
    .filter(Boolean);

  const packageTitle =
    getFirstElementTextByNames(packageDoc, ["title", "dc:title"]) ||
    fileNameWithoutExtension(file.name || "") ||
    "EPUB";

  const chapters = [];

  for (let index = 0; index < spineIds.length; index += 1) {
    const manifestItem = manifestMap.get(spineIds[index]);

    if (!manifestItem || !manifestItem.href) {
      continue;
    }

    if (!/xhtml|html|xml/i.test(manifestItem.mediaType)) {
      continue;
    }

    const contentPath = resolveZipPath(rootfilePath, manifestItem.href);
    const markup = await readZipEntryAsText(zip, contentPath);
    const text = extractMarkupText(markup);

    if (!text) {
      continue;
    }

    const doc = parseMarkup(markup, "application/xhtml+xml");
    const chapterTitle =
      getFirstElementTextByNames(doc, ["h1", "h2", "h3", "title"]) ||
      `${content.chapterLabel} ${chapters.length + 1}`;

    chapters.push({
      id: `chapter-${chapters.length}`,
      title: chapterTitle,
      text
    });
  }

  if (!chapters.length) {
    throw new Error("No readable EPUB text was found.");
  }

  return {
    title: packageTitle,
    chapters,
    coverImage: await extractEpubCover(zip, rootfilePath, packageDoc, manifestMap, packageTitle)
  };
}

async function decodeUploadedText(file) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const preferredEncodings = [];

  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    preferredEncodings.push("utf-16le");
  } else if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    preferredEncodings.push("utf-16be");
  } else if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    preferredEncodings.push("utf-8");
  }

  ["utf-8", "gb18030", "utf-16le", "utf-16be"].forEach((encoding) => {
    if (!preferredEncodings.includes(encoding)) {
      preferredEncodings.push(encoding);
    }
  });

  let bestText = "";
  let bestScore = Number.POSITIVE_INFINITY;

  preferredEncodings.forEach((encoding) => {
    try {
      const decoded = decodeBuffer(buffer, encoding);
      const trimmed = decoded.trim();
      const replacements = countReplacementCharacters(decoded);
      const penalty = trimmed ? 0 : 1000;
      const score = replacements * 100 + penalty;

      if (score < bestScore) {
        bestScore = score;
        bestText = decoded;
      }
    } catch (error) {
      // Ignore unsupported decoders in the current browser.
    }
  });

  return bestText;
}

const ReaderLab = forwardRef(function ReaderLab(
  {
    lang,
    isActive = true,
    visible,
    onClose,
    onPlayerStateChange,
    onUiStateChange,
    onRegisterActions
  },
  ref
) {
  const content = useMemo(
    () => ({
      ...LAB_CONTENT.en,
      ...(LAB_CONTENT[lang] || {})
    }),
    [lang]
  );
  const supportsAudio =
    typeof window !== "undefined" &&
    typeof window.Audio !== "undefined";
  const supportsSpeechFallback =
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    "SpeechSynthesisUtterance" in window;
  const isVisible = typeof visible === "boolean" ? visible : isActive;

  const [initialState] = useState(() => getInitialReaderState(content.demoText, lang));
  const [activeBookId, setActiveBookId] = useState(initialState.activeBookId);
  const [bookTitle, setBookTitle] = useState(initialState.bookTitle);
  const [bookCoverImage, setBookCoverImage] = useState(
    initialState.coverImage || createGeneratedCoverDataUrl(initialState.bookTitle)
  );
  const [chapters, setChapters] = useState(initialState.chapters);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(initialState.currentChapterIndex);
  const [currentIndex, setCurrentIndex] = useState(initialState.currentIndex);
  const [rate, setRate] = useState(initialState.rate);
  const [preferredReadingLanguage, setPreferredReadingLanguage] = useState(
    initialState.preferredReadingLanguage
  );
  const [, setTranslationPlaybackVersion] = useState(0);
  const [selectedEnglishVoiceURI, setSelectedEnglishVoiceURI] = useState(initialState.selectedEnglishVoiceURI);
  const [selectedFrenchVoiceURI, setSelectedFrenchVoiceURI] = useState(initialState.selectedFrenchVoiceURI);
  const [selectedChineseVoiceURI, setSelectedChineseVoiceURI] = useState(initialState.selectedChineseVoiceURI);
  const [audioConfig, setAudioConfig] = useState({
    checked: FREE_BROWSER_SPEECH_ONLY_MODE,
    hasServerKey: false,
    personalKeyRequired: !FREE_BROWSER_SPEECH_ONLY_MODE,
    publicAiAudioMode: FREE_BROWSER_SPEECH_ONLY_MODE
      ? "browser-speech-only"
      : "personal-key-only",
    model: "",
    chapterAudioCache: null
  });
  const [aiPlaybackSuppressed, setAiPlaybackSuppressed] = useState(false);
  const [personalApiKey, setPersonalApiKey] = useState(() => getInitialPersonalApiKey());
  const [personalApiKeyDraft, setPersonalApiKeyDraft] = useState(() => getInitialPersonalApiKey());
  const [isEditingPersonalApiKey, setIsEditingPersonalApiKey] = useState(
    () => !getInitialPersonalApiKey()
  );
  const [personalApiKeyError, setPersonalApiKeyError] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [playbackPhase, setPlaybackPhase] = useState(
    initialState.playbackSnapshot?.phase || "idle"
  );
  const [status, setStatus] = useState(content.statusReady);
  const [hasAcceptedUserDeclaration, setHasAcceptedUserDeclaration] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return window.localStorage.getItem(USER_DECLARATION_ACCEPTED_KEY) === "true";
    } catch (error) {
      return false;
    }
  });
  const [dragActive, setDragActive] = useState(false);
  const [isTopbarImportDragActive, setIsTopbarImportDragActive] = useState(false);
  const [, setBrowserSpeechResumeCard] = useState(null);
  const [isCompactLayout, setIsCompactLayout] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 980 : false
  );
  const {
    chapterPopoverOffsetY,
    setChapterPopoverOffsetY,
    isChapterPopoverDragging,
    setIsChapterPopoverDragging,
    readingWindowUserOffsetY,
    setReadingWindowUserOffsetY,
    readingWindowAutoOffsetY,
    setReadingWindowAutoOffsetY,
    isReadingWindowDragging,
    setIsReadingWindowDragging,
    isPureReadingMode,
    setIsPureReadingMode,
    pureReadingIndex,
    setPureReadingIndex,
    pureReadingDisplaySnapshot,
    setPureReadingDisplaySnapshot,
    readingWindowPlaybackCursor,
    setReadingWindowPlaybackCursor
  } = useReadingWindowState(initialState);
  const [lastImportedFileName, setLastImportedFileName] = useState(initialState.lastImportedFileName);
  const [lastImportedFileType, setLastImportedFileType] = useState(initialState.lastImportedFileType);
  const [conversionSourceFormat, setConversionSourceFormat] = useState(() =>
    isSupportedConversionSourceFormat(initialState.lastImportedFileType, content)
      ? getFileTypeLabel(initialState.lastImportedFileType, content)
      : ""
  );
  const [conversionTargetFormat, setConversionTargetFormat] = useState(
    getFileTypeLabel(initialState.lastImportedFileType, content) === content.fileTypeTxt
      ? content.fileTypeEpub
      : content.fileTypeTxt
  );
  const [isConvertingDocument, setIsConvertingDocument] = useState(false);
  const [isExportingMp3, setIsExportingMp3] = useState(false);
  const [exportProgress, setExportProgress] = useState({ done: 0, total: 0 });
  const [isExportingPlainEpub, setIsExportingPlainEpub] = useState(false);
  const [plainEpubProgress, setPlainEpubProgress] = useState({ done: 0, total: 0 });
  const [isExportingChineseEpub, setIsExportingChineseEpub] = useState(false);
  const [chineseEpubProgress, setChineseEpubProgress] = useState({ done: 0, total: 0 });
  const [readerTextValue, setReaderTextValue] = useState(
    initialState.chapters[initialState.currentChapterIndex]?.text || ""
  );
  const [savedBooks, setSavedBooks] = useState(() => getInitialSavedBooks());
  const [shelfStorageStats, setShelfStorageStats] = useState(() =>
    buildShelfStorageStats(getInitialSavedBooks())
  );
  const [shelfStartIndex, setShelfStartIndex] = useState(0);
  const [isSeekInteracting, setIsSeekInteracting] = useState(false);
  const [seekPreviewPercent, setSeekPreviewPercent] = useState(0);
  const [progressState, setProgressState] = useState({
    elapsedSeconds: 0,
    totalSeconds: 0,
    progressPercent: 0,
    progressText: "0:00 / 0:00"
  });
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [prefersDirectAiAudioStream] = useState(() => shouldPreferDirectAiAudioStream());

  const apiKeySectionRef = useRef(null);
  const voiceSectionRef = useRef(null);
  const importPanelRef = useRef(null);
  const personalApiKeyInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const inlineQueueRef = useRef(null);
  const sourceDetailsRef = useRef(null);
  const sourceTextareaRef = useRef(null);
  const progressWrapRef = useRef(null);
  const progressInputRef = useRef(null);
  const seekDragRef = useRef(null);
  const chapterPickerRef = useRef(null);
  const understandLanguageMenuRef = useRef(null);
  const timerMenuRef = useRef(null);
  const timerTriggerRef = useRef(null);
  const chapterPopoverDragRef = useRef(null);
  const readingWindowRef = useRef(null);
  const readingWindowCurrentPaneRef = useRef(null);
  const readingWindowDragRef = useRef(null);
  const readingWindowAutoResetTimeoutRef = useRef(null);
  const lastReadingWindowCursorRef = useRef(null);
  const navigatePureReadingRef = useRef(() => {});
  const pureReadingWheelLockRef = useRef(0);
  const readingWindowPlaybackCursorRef = useRef({
    chapterIndex: initialState.currentChapterIndex,
    blockIndex: initialState.currentIndex,
    offsetChars: clampNumber(initialState.playbackSnapshot?.offsetChars || 0, 0, 1000000)
  });
  const audioRef = useRef(null);
  const previewAudioRef = useRef(null);
  const speechUtteranceRef = useRef(null);
  const browserSpeechChunksRef = useRef([]);
  const browserSpeechLookaheadRef = useRef([]);
  const browserSpeechWasHiddenRef = useRef(false);
  const playTokenRef = useRef(0);
  const currentIndexRef = useRef(initialState.currentIndex);
  const blocksRef = useRef([]);
  const chaptersRef = useRef(initialState.chapters);
  const rateRef = useRef(initialState.rate);
  const pendingAutoPlayRef = useRef(false);
  const pendingAutoPlayTargetRef = useRef(null);
  const speakFromIndexRef = useRef(() => {});
  const skipBackActionRef = useRef(() => {});
  const skipForwardActionRef = useRef(() => {});
  const previousChapterActionRef = useRef(() => {});
  const nextChapterActionRef = useRef(() => {});
  const freezeProgressTrackingRef = useRef(() => {});
  const resumeProgressTrackingRef = useRef(() => {});
  const syncProgressToCursorRef = useRef(() => {});
  const handlePlayToggleRef = useRef(() => {});
  const currentChapterIndexRef = useRef(initialState.currentChapterIndex);
  const isPlayingRef = useRef(false);
  const isPausedRef = useRef(false);
  const playbackPhaseRef = useRef(initialState.playbackSnapshot?.phase || "idle");
  const playbackIntentRef = useRef(false);
  const pauseOriginRef = useRef(initialState.playbackSnapshot?.pauseOrigin || "none");
  const savedBooksRef = useRef(savedBooks);
  const autoSaveEnabledRef = useRef(
    Boolean(
      initialState.activeBookId ||
      initialState.lastImportedFileName ||
      initialState.lastImportedFileType
    )
  );
  const shelfNoticeRef = useRef({ trimmed: false, failed: false });
  const progressIntervalRef = useRef(null);
  const playbackProgressRef = useRef({
    elapsedSeconds: 0,
    totalSeconds: 0,
    segmentStartTimeMs: 0,
    segmentStartSeconds: 0
  });
  const chapterTimingRef = useRef({
    key: "",
    durations: [],
    totalSeconds: 0
  });
  const recoveryAttemptRef = useRef(0);
  const recoveryInFlightRef = useRef(false);
  const shouldRecoverPlaybackRef = useRef(false);
  const audioPauseGuardCountRef = useRef(0);
  const manualSeekGuardUntilRef = useRef(0);
  const manualSeekShouldResumeRef = useRef(false);
  const nearEndPrefetchKeyRef = useRef("");
  const chapterStartPrefetchKeyRef = useRef("");
  const wakeLockRef = useRef(null);
  const chapterAudioCacheRef = useRef(new Map());
  const chapterAudioRequestsRef = useRef(new Map());
  const chapterAudioGenerationRef = useRef(0);
  const queuedOffsetRef = useRef(0);
  const restorePlaybackSnapshotRef = useRef(initialState.playbackSnapshot);
  const restoreAppliedRef = useRef(false);
  const restoreWarmupDoneRef = useRef(false);
  const setPlaybackPhaseStateRef = useRef(() => {});
  const persistReaderStateRef = useRef(() => null);

  const {
    sleepMode,
    sleepModeRef,
    sleepCountdownSeconds,
    handleSelectSleepTimer,
    cancelSleepTimer,
    clearSleepTimeout
  } = useSleepTimer({
    onSleepTriggered: () => stopPlayback(),
    content
  });

  const {
    showVoiceTuning,
    setShowVoiceTuning,
    showAudioKeyGuide,
    setShowAudioKeyGuide,
    showTimerMenu,
    setShowTimerMenu,
    showChapterPicker,
    setShowChapterPicker,
    showUnderstandLanguageMenu,
    setShowUnderstandLanguageMenu,
    showQueuePanel,
    setShowQueuePanel,
    timerMenuDirection,
    setTimerMenuDirection,
    activeUtilitySheet,
    setActiveUtilitySheet
  } = useReaderPanels({
    isVisible,
    isCompactLayout
  });

  const currentConversionSourceFormat = useMemo(() => {
    if (!isSupportedConversionSourceFormat(lastImportedFileType, content)) {
      return "";
    }

    return getFileTypeLabel(lastImportedFileType, content);
  }, [content, lastImportedFileType]);
  const translationPlaybackMode =
    getTranslationPlaybackModeForPreferredLanguage(preferredReadingLanguage);

  const conversionSourceMismatch = Boolean(
    conversionSourceFormat &&
      currentConversionSourceFormat &&
      conversionSourceFormat !== currentConversionSourceFormat
  );

  const canConvertCurrentDocument = Boolean(
    lastImportedFileName &&
      currentConversionSourceFormat &&
      conversionSourceFormat === currentConversionSourceFormat &&
      conversionTargetFormat &&
      conversionTargetFormat !== conversionSourceFormat &&
      !isConvertingDocument
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(
        USER_DECLARATION_ACCEPTED_KEY,
        hasAcceptedUserDeclaration ? "true" : "false"
      );
    } catch (error) {
      // Ignore localStorage write failures.
    }
  }, [hasAcceptedUserDeclaration]);

  useEffect(() => {
    if (!currentConversionSourceFormat) {
      setConversionSourceFormat("");
      setConversionTargetFormat(content.fileTypeTxt);
      return;
    }

    setConversionSourceFormat(currentConversionSourceFormat);
    setConversionTargetFormat((previous) => {
      if (!previous || previous === currentConversionSourceFormat) {
        return currentConversionSourceFormat === content.fileTypeTxt
          ? content.fileTypeEpub
          : content.fileTypeTxt;
      }

      return previous;
    });
  }, [content.fileTypeEpub, content.fileTypeTxt, currentConversionSourceFormat]);
  const lastPlaybackSnapshotPersistRef = useRef(
    initialState.playbackSnapshot
      ? {
          ...initialState.playbackSnapshot,
          updatedAt: initialState.playbackSnapshot.updatedAt || Date.now()
        }
      : null
  );
  const browserFallbackForcedRef = useRef(false);
  const voiceSelectionResumeRef = useRef(null);
  const seekToPercentRef = useRef(() => {});

  const currentChapter = chapters[currentChapterIndex] || null;
  const currentChapterDisplayTitle = getDisplayChapterTitle(currentChapter, currentChapterIndex, {
    chapterLabel: content.chapterLabel,
    bookTitle,
    fileName: lastImportedFileName,
    workspaceTitle: content.workspaceTitle
  });
  const currentChapterText = currentChapter?.text || "";
  const blocks = splitIntoBlocks(currentChapterText);
  const browserSpeechChunks = useMemo(
    () => buildSpeechChunks(blocks, BROWSER_SPEECH_CHUNK_OPTIONS),
    [blocks]
  );
  const previousBlock = currentIndex > 0 ? blocks[currentIndex - 1] : null;
  const currentBlock = blocks[currentIndex] || null;
  const nextBlock = currentIndex + 1 < blocks.length ? blocks[currentIndex + 1] : null;
  const previousTranslatedBlock = getTranslatedBlockForPlayback(
    previousBlock,
    translationPlaybackMode
  );
  const currentTranslatedBlock = getTranslatedBlockForPlayback(
    currentBlock,
    translationPlaybackMode
  );
  const nextTranslatedBlock = getTranslatedBlockForPlayback(
    nextBlock,
    translationPlaybackMode
  );
  const englishVoices = buildVoiceOptions(ENGLISH_VOICE_TARGETS, lang);
  const frenchVoices = buildVoiceOptions(FRENCH_VOICE_TARGETS, lang);
  const chineseVoices = buildVoiceOptions(CHINESE_VOICE_TARGETS, lang);
  const orderedSavedBooks = useMemo(
    () =>
      activeBookId
        ? [
            ...savedBooks.filter((book) => book.id === activeBookId),
            ...savedBooks.filter((book) => book.id !== activeBookId)
          ]
        : savedBooks,
    [activeBookId, savedBooks]
  );
  const currentChapterAudioKey = createChapterAudioCacheKey({
    chapterTitle: currentChapter?.title || "",
    chapterText: currentChapterText,
    selectedEnglishVoiceURI,
    selectedFrenchVoiceURI,
    selectedChineseVoiceURI
  });
  const personalAiAudioEnabled = FREE_BROWSER_SPEECH_ONLY_MODE
    ? false
    : Boolean(personalApiKey.trim());
  const audioGenerationReady = FREE_BROWSER_SPEECH_ONLY_MODE
    ? false
    : personalAiAudioEnabled;
  const aiAudioAvailable = FREE_BROWSER_SPEECH_ONLY_MODE
    ? false
    : audioGenerationReady && !aiPlaybackSuppressed;
  const {
    cancelAll: cancelAllRecoveryTimers,
    schedulePlaybackRecovery,
    startManualSeekGuard,
    isManualSeekActive,
    clearManualSeekGuard,
    scheduleSeekResume,
    makeFallbackContinuationGuard,
    clearRecoveryTimeout,
    clearFallbackContinuationTimeout,
    clearSeekResumeTimeout
  } = usePlaybackRecovery({
    audioRef,
    playTokenRef,
    playbackIntentRef,
    isPausedRef,
    isPlayingRef,
    currentChapterIndexRef,
    currentIndexRef,
    queuedOffsetRef,
    pauseOriginRef,
    aiAudioAvailable,
    supportsAudio,
    supportsSpeechFallback,
    recoveryAttemptRef,
    recoveryInFlightRef,
    shouldRecoverPlaybackRef,
    manualSeekGuardUntilRef,
    manualSeekShouldResumeRef,
    browserFallbackForcedRef,
    speechUtteranceRef,
    startAiChapterPlayback,
    speakFromIndexRef,
    suppressAiPlaybackForSession,
    resumeProgressTrackingRef,
    freezeProgressTrackingRef,
    setPlaybackPhaseState,
    setIsPlaying,
    setIsPaused,
    setStatus,
    content,
    canUseBrowserSpeechFallback
  });
  const maxShelfStartIndex = Math.max(orderedSavedBooks.length - SHELF_VISIBLE_COUNT, 0);
  const visibleShelfBooks = orderedSavedBooks.slice(
    shelfStartIndex,
    shelfStartIndex + SHELF_VISIBLE_COUNT
  );
  const selectedRateIndex = getRateOptionIndex(rate);
  const rateProgressPercent =
    RATE_OPTIONS.length > 1
      ? (selectedRateIndex / (RATE_OPTIONS.length - 1)) * 100
      : 0;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const updateCompactLayout = () => {
      setIsCompactLayout(window.innerWidth <= 980);
    };

    updateCompactLayout();
    window.addEventListener("resize", updateCompactLayout);

    return () => {
      window.removeEventListener("resize", updateCompactLayout);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !showTimerMenu) {
      return undefined;
    }

    const updateTimerMenuDirection = () => {
      const triggerRect = timerTriggerRef.current?.getBoundingClientRect?.();

      if (!triggerRect) {
        setTimerMenuDirection("down");
        return;
      }

      const estimatedMenuHeight = TIMER_OPTIONS.length * 48 + 64;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      setTimerMenuDirection(
        spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow ? "up" : "down"
      );
    };

    updateTimerMenuDirection();
    window.addEventListener("resize", updateTimerMenuDirection);
    window.addEventListener("scroll", updateTimerMenuDirection, true);

    return () => {
      window.removeEventListener("resize", updateTimerMenuDirection);
      window.removeEventListener("scroll", updateTimerMenuDirection, true);
    };
  }, [setTimerMenuDirection, showTimerMenu]);

  useEffect(() => {
    if (!showChapterPicker) {
      setChapterPopoverOffsetY(0);
      setIsChapterPopoverDragging(false);
      chapterPopoverDragRef.current = null;
    }
  }, [setChapterPopoverOffsetY, setIsChapterPopoverDragging, showChapterPicker]);

  useEffect(() => {
    if (typeof document === "undefined" || isCompactLayout || !showChapterPicker) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!chapterPickerRef.current?.contains(event.target)) {
        setShowChapterPicker(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowChapterPicker(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCompactLayout, setShowChapterPicker, showChapterPicker]);

  useEffect(() => {
    if (typeof window === "undefined" || !showChapterPicker) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const activeChapterLink = chapterPickerRef.current?.querySelector?.(
        ".echoChapterListLink.active"
      );

      activeChapterLink?.scrollIntoView?.({
        block: "nearest",
        behavior: "auto"
      });
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [currentChapterIndex, showChapterPicker]);

  useEffect(() => {
    if (typeof document === "undefined" || !showTimerMenu) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!timerMenuRef.current?.contains(event.target)) {
        setShowTimerMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowTimerMenu(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowTimerMenu, showTimerMenu]);

  useEffect(() => {
    if (typeof document === "undefined" || !showUnderstandLanguageMenu) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!understandLanguageMenuRef.current?.contains(event.target)) {
        setShowUnderstandLanguageMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowUnderstandLanguageMenu(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowUnderstandLanguageMenu, showUnderstandLanguageMenu]);

  useEffect(() => {
    const currentPicker = chapterPickerRef.current;
    const currentReadingWindow = readingWindowRef.current;

    return () => {
      if (chapterPopoverDragRef.current?.pointerId != null) {
        try {
          currentPicker?.releasePointerCapture?.(chapterPopoverDragRef.current.pointerId);
        } catch (error) {
          // Ignore pointer-capture cleanup failures during unmount.
        }
      }

      if (readingWindowDragRef.current?.pointerId != null) {
        try {
          currentReadingWindow?.releasePointerCapture?.(readingWindowDragRef.current.pointerId);
        } catch (error) {
          // Ignore pointer-capture cleanup failures during unmount.
        }
      }

      if (readingWindowAutoResetTimeoutRef.current != null) {
        window.clearTimeout(readingWindowAutoResetTimeoutRef.current);
        readingWindowAutoResetTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || !blocks.length) {
      return undefined;
    }

    const handlePlaybackShortcut = (event) => {
      if (event.defaultPrevented || isEditableTarget(event.target)) {
        return;
      }

      const key = event.key || "";
      const code = event.code || "";
      const isMediaKey =
        key === "MediaPlayPause" ||
        key === "MediaTrackNext" ||
        key === "MediaTrackPrevious" ||
        key === "MediaNextTrack" ||
        key === "MediaPreviousTrack";

      if (!isMediaKey && (!isVisible || event.metaKey || event.ctrlKey || event.altKey)) {
        return;
      }

      if (isPureReadingMode) {
        switch (key) {
          case "ArrowLeft":
          case "ArrowUp":
          case "PageUp":
            event.preventDefault();
            navigatePureReadingRef.current(-1);
            return;
          case "ArrowRight":
          case "ArrowDown":
          case "PageDown":
            event.preventDefault();
            navigatePureReadingRef.current(1);
            return;
          case "Escape":
            event.preventDefault();
            setIsPureReadingMode(false);
            setStatus(content.statusPureReadingDisabled);
            return;
          default:
            break;
        }
      }

      if (key === "MediaPlayPause") {
        event.preventDefault();
        handlePlayToggleRef.current();
        return;
      }

      if (key === "MediaTrackNext" || key === "MediaNextTrack") {
        event.preventDefault();
        nextChapterActionRef.current();
        return;
      }

      if (key === "MediaTrackPrevious" || key === "MediaPreviousTrack") {
        event.preventDefault();
        previousChapterActionRef.current();
        return;
      }

      if (code === "Space" || key.toLowerCase() === "k") {
        event.preventDefault();
        handlePlayToggleRef.current();
        return;
      }

      if (key.toLowerCase() === "j") {
        event.preventDefault();
        skipBackActionRef.current();
        return;
      }

      if (key.toLowerCase() === "l") {
        event.preventDefault();
        skipForwardActionRef.current();
      }
    };

    document.addEventListener("keydown", handlePlaybackShortcut);

    return () => {
      document.removeEventListener("keydown", handlePlaybackShortcut);
    };
  }, [
    blocks.length,
    content.statusPureReadingDisabled,
    isPureReadingMode,
    isVisible,
    setIsPureReadingMode
  ]);

  function debugLog(event, details = {}) {
    if (!isDevDiagnosticsEnabled()) {
      return;
    }

    console.info("[PageToneDebug]", event, details);
  }

  const clearBrowserSpeechResumeCard = useCallback(() => {
    setBrowserSpeechResumeCard(null);
  }, []);

  const offerBrowserSpeechResume = useCallback((source, snapshot = null) => {
    if (!SHOW_BROWSER_SPEECH_RESUME_PROMPT) {
      setBrowserSpeechResumeCard(null);
      return;
    }

    const resumeSnapshot =
      snapshot && typeof snapshot === "object"
        ? snapshot
        : readPersistedPlaybackSnapshot();

    if (
      !resumeSnapshot ||
      resumeSnapshot.audioMode !== "browser-fallback" ||
      !resumeSnapshot.shouldResume ||
      resumeSnapshot.pauseOrigin === "user"
    ) {
      return;
    }

    const chapterIndex = clampIndex(resumeSnapshot.chapterIndex, chaptersRef.current.length);
    const chapter = chaptersRef.current[chapterIndex] || null;

    setBrowserSpeechResumeCard({
      chapterIndex,
      blockIndex: clampIndex(
        resumeSnapshot.blockIndex,
        splitIntoBlocks(chapter?.text || "").length
      ),
      offsetChars: Math.max(0, Number(resumeSnapshot.offsetChars) || 0),
      bookTitle: bookTitle || content.workspaceTitle,
      chapterTitle:
        getDisplayChapterTitle(chapter, chapterIndex, {
          chapterLabel: content.chapterLabel,
          bookTitle,
          fileName: lastImportedFileName,
          workspaceTitle: content.workspaceTitle
        }) || content.importedTextTitle,
      previousExcerpt:
        resumeSnapshot.visibleExcerptContext?.previous ||
        buildVisibleExcerptContext(
          splitIntoBlocks(chapter?.text || ""),
          resumeSnapshot.blockIndex
        ).previous,
      currentExcerpt:
        resumeSnapshot.visibleExcerptContext?.current ||
        buildVisibleExcerptContext(
          splitIntoBlocks(chapter?.text || ""),
          resumeSnapshot.blockIndex
        ).current
    });
    setStatus(content.statusResumeReady);
    debugLog("speech-resume-offered", {
      source,
      chapterIndex,
      blockIndex: resumeSnapshot.blockIndex
    });
  }, [
    bookTitle,
    content.chapterLabel,
    content.importedTextTitle,
    content.statusResumeReady,
    content.workspaceTitle,
    lastImportedFileName
  ]);

  function setPlaybackPhaseState(nextPhase, details = {}) {
    playbackPhaseRef.current = nextPhase;
    setPlaybackPhase(nextPhase);
    debugLog("phase", {
      phase: nextPhase,
      ...details
    });
  }

  function getCurrentAudioModeId() {
    if (FREE_BROWSER_SPEECH_ONLY_MODE) {
      return supportsSpeechFallback ? "browser-fallback" : "none";
    }

    if (aiAudioAvailable || Boolean(audioRef.current?.dataset.cacheKey)) {
      return "ai-audio";
    }

    if (supportsSpeechFallback) {
      return "browser-fallback";
    }

    return "none";
  }

  function buildPlaybackSnapshot(overrides = {}) {
    const chapterIndex = clampIndex(
      Number.isInteger(overrides.chapterIndex)
        ? overrides.chapterIndex
        : currentChapterIndexRef.current,
      chaptersRef.current.length
    );
    const chapterBlocks = splitIntoBlocks(chaptersRef.current[chapterIndex]?.text || "");
    const blockIndex = clampIndex(
      Number.isInteger(overrides.blockIndex) ? overrides.blockIndex : currentIndexRef.current,
      chapterBlocks.length
    );
    const blockTextLength = chapterBlocks[blockIndex]?.text?.length || 0;
    const offsetChars = clampNumber(
      Number.isInteger(overrides.offsetChars) ? overrides.offsetChars : queuedOffsetRef.current,
      0,
      blockTextLength
    );
    const model = buildChapterProgressModel(
      chapterBlocks,
      rateRef.current,
      chapterIndex === currentChapterIndexRef.current ? chapterTimingRef.current.durations : []
    );
    const totalSeconds =
      typeof overrides.totalSeconds === "number" && Number.isFinite(overrides.totalSeconds)
        ? Math.max(0, overrides.totalSeconds)
        : playbackProgressRef.current.totalSeconds || model.totalSeconds;
    const rawElapsed =
      typeof overrides.elapsedSeconds === "number" && Number.isFinite(overrides.elapsedSeconds)
        ? overrides.elapsedSeconds
        : getCurrentEstimatedElapsedSeconds();
    const elapsedSeconds = clampNumber(rawElapsed, 0, totalSeconds || Math.max(rawElapsed, 0));
    const pauseOrigin = overrides.pauseOrigin || pauseOriginRef.current;
    const chapterVisibleContext = buildVisibleExcerptContext(chapterBlocks, blockIndex);
    const speechChunkPosition = findSpeechChunkPosition(
      chapterIndex === currentChapterIndexRef.current
        ? browserSpeechChunksRef.current
        : buildSpeechChunks(chapterBlocks, BROWSER_SPEECH_CHUNK_OPTIONS),
      blockIndex,
      offsetChars
    );
    const activeAudio = audioRef.current;
    const audioCacheKey =
      typeof overrides.audioCacheKey === "string"
        ? overrides.audioCacheKey
        : String(activeAudio?.dataset.cacheKey || "");
    const audioStreamUrl =
      typeof overrides.audioStreamUrl === "string"
        ? normalizeEchoAudioStreamUrl(overrides.audioStreamUrl, audioCacheKey)
        : getPersistableAudioStreamUrl(activeAudio, audioCacheKey);

    return {
      version: PLAYBACK_SNAPSHOT_VERSION,
      activeBookId: typeof activeBookId === "string" ? activeBookId : "",
      phase: overrides.phase || playbackPhaseRef.current,
      audioMode: overrides.audioMode || getCurrentAudioModeId(),
      pauseOrigin,
      userPaused:
        typeof overrides.userPaused === "boolean"
          ? overrides.userPaused
          : pauseOrigin === "user",
      shouldResume:
        typeof overrides.shouldResume === "boolean"
          ? overrides.shouldResume
          : Boolean(playbackIntentRef.current && pauseOrigin !== "user"),
      backgroundInterrupted:
        typeof overrides.backgroundInterrupted === "boolean"
          ? overrides.backgroundInterrupted
          : false,
      chapterIndex,
      blockIndex,
      offsetChars,
      speechChunkIndex:
        Number.isInteger(overrides.speechChunkIndex)
          ? overrides.speechChunkIndex
          : speechChunkPosition.chunkIndex,
      audioCacheKey,
      audioStreamUrl,
      elapsedSeconds,
      totalSeconds,
      visibleExcerptContext:
        overrides.visibleExcerptContext && typeof overrides.visibleExcerptContext === "object"
          ? overrides.visibleExcerptContext
          : chapterVisibleContext,
      updatedAt: Date.now()
    };
  }

  function persistReaderState(overrides = {}) {
    if (typeof window === "undefined") {
      return null;
    }

    const playbackSnapshot = buildPlaybackSnapshot(overrides.playbackSnapshot || {});

    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          activeBookId,
          bookTitle,
          coverImage: bookCoverImage,
          chapters,
          currentIndex,
          currentChapterIndex,
          rate,
          preferredReadingLanguage,
          translationPlaybackMode,
          selectedEnglishVoiceURI,
          selectedFrenchVoiceURI,
          selectedChineseVoiceURI,
          lastImportedFileName,
          lastImportedFileType,
          playbackSnapshot
        })
      );
      lastPlaybackSnapshotPersistRef.current = playbackSnapshot;
      return playbackSnapshot;
    } catch (error) {
      debugLog("persist-failed", {
        message: error instanceof Error ? error.message : "Unknown local persistence error."
      });
      return null;
    }
  }

  setPlaybackPhaseStateRef.current = setPlaybackPhaseState;
  persistReaderStateRef.current = persistReaderState;

  function persistPlaybackSnapshotIfNeeded(source, playbackOverrides = {}, options = {}) {
    const nextSnapshot = buildPlaybackSnapshot(playbackOverrides);
    const previousSnapshot = lastPlaybackSnapshotPersistRef.current;
    const force = Boolean(options.force);

    const shouldPersist =
      force ||
      !previousSnapshot ||
      nextSnapshot.phase !== previousSnapshot.phase ||
      nextSnapshot.audioMode !== previousSnapshot.audioMode ||
      nextSnapshot.pauseOrigin !== previousSnapshot.pauseOrigin ||
      nextSnapshot.shouldResume !== previousSnapshot.shouldResume ||
      nextSnapshot.backgroundInterrupted !== previousSnapshot.backgroundInterrupted ||
      nextSnapshot.chapterIndex !== previousSnapshot.chapterIndex ||
      nextSnapshot.blockIndex !== previousSnapshot.blockIndex ||
      nextSnapshot.speechChunkIndex !== previousSnapshot.speechChunkIndex ||
      Math.abs(nextSnapshot.offsetChars - (previousSnapshot.offsetChars || 0)) >= 24 ||
      Math.abs(nextSnapshot.totalSeconds - (previousSnapshot.totalSeconds || 0)) >= 0.5 ||
      (
        Date.now() - (previousSnapshot.updatedAt || 0) >=
          PLAYBACK_SNAPSHOT_PERSIST_INTERVAL_MS &&
        Math.abs(nextSnapshot.elapsedSeconds - previousSnapshot.elapsedSeconds) >=
          PLAYBACK_SNAPSHOT_PERSIST_DELTA_SECONDS
      );

    if (!shouldPersist) {
      return previousSnapshot;
    }

    const persisted = persistReaderState({
      playbackSnapshot: nextSnapshot
    });

    if (persisted) {
      debugLog("snapshot-persisted", {
        source,
        phase: persisted.phase,
        chapterIndex: persisted.chapterIndex,
        blockIndex: persisted.blockIndex,
        elapsedSeconds: persisted.elapsedSeconds,
        pauseOrigin: persisted.pauseOrigin,
        shouldResume: persisted.shouldResume
      });
    }

    return persisted;
  }

  function getSelectedVoiceUriForLanguage(language) {
    if (isChineseLanguage(language)) {
      return selectedChineseVoiceURI;
    }

    if (isFrenchLanguage(language)) {
      return selectedFrenchVoiceURI;
    }

    return selectedEnglishVoiceURI;
  }

  function getCurrentBrowserSpeechConfig(language) {
    return getMatchingBrowserSpeechConfig(
      language,
      getSelectedVoiceUriForLanguage(language)
    );
  }

  function suppressAiPlaybackForSession(error, source = "ai-error") {
    if (!shouldSuppressAiAudioForSession(error)) {
      return;
    }

    browserFallbackForcedRef.current = true;
    setAiPlaybackSuppressed(true);
    debugLog("ai-playback-suppressed", {
      source,
      message: error instanceof Error ? error.message : "Unknown AI availability error."
    });
  }

  function clearAiPlaybackSuppression(source = "ai-success") {
    if (!browserFallbackForcedRef.current && !aiPlaybackSuppressed) {
      return;
    }

    browserFallbackForcedRef.current = false;
    setAiPlaybackSuppressed(false);
    debugLog("ai-playback-restored", {
      source
    });
  }

  function pruneGeneratedChapterCache(anchorChapterIndex = currentChapterIndexRef.current) {
    const activeAudioKey = audioRef.current?.dataset.cacheKey || "";
    const keepKeys = new Set();
    const startIndex = Math.max(0, anchorChapterIndex - CHAPTER_CACHE_KEEP_BEHIND);
    const endIndex = Math.min(
      chaptersRef.current.length - 1,
      anchorChapterIndex + CHAPTER_CACHE_KEEP_AHEAD
    );

    for (let chapterIndex = startIndex; chapterIndex <= endIndex; chapterIndex += 1) {
      const cacheKey = getAudioCacheKeyForChapter(chapterIndex);

      if (cacheKey) {
        keepKeys.add(cacheKey);
      }
    }

    chapterAudioCacheRef.current.forEach((entry, cacheKey) => {
      if (cacheKey === activeAudioKey || keepKeys.has(cacheKey)) {
        return;
      }

      revokeCachedChapterAudio(entry);
      chapterAudioCacheRef.current.delete(cacheKey);
      debugLog("cache-pruned", {
        chapterIndex: entry?.chapterIndex,
        cacheKey
      });
    });
  }

  function commitSavedBooks(nextBooks) {
    const result = persistSavedBooks(nextBooks);
    savedBooksRef.current = result.books;
    setSavedBooks(result.books);
    return result;
  }

  function findExistingBookId(nextBookTitle, nextFileName, nextFileType) {
    const normalizedFileName = (nextFileName || "").trim().toLowerCase();
    const normalizedTitle = (nextBookTitle || "").trim().toLowerCase();

    const fileMatch = savedBooksRef.current.find(
      (book) => (book.lastImportedFileName || "").trim().toLowerCase() === normalizedFileName
    );

    if (fileMatch) {
      return fileMatch.id;
    }

    const titleMatch = savedBooksRef.current.find(
      (book) =>
        (book.bookTitle || "").trim().toLowerCase() === normalizedTitle &&
        (book.fileType || "") === nextFileType
    );

    return titleMatch?.id || "";
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!chapters.length) {
      setCurrentChapterIndex(0);
      return;
    }

    if (currentChapterIndex >= chapters.length) {
      setCurrentChapterIndex(chapters.length - 1);
    }
  }, [chapters, currentChapterIndex]);

  useEffect(() => {
    blocksRef.current = blocks;

    if (!blocks.length) {
      setCurrentIndex(0);
      currentIndexRef.current = 0;
      return;
    }

    if (currentIndex >= blocks.length) {
      const nextIndex = blocks.length - 1;
      setCurrentIndex(nextIndex);
      currentIndexRef.current = nextIndex;
    }
  }, [blocks, currentIndex]);

  useEffect(() => {
    browserSpeechChunksRef.current = browserSpeechChunks;
    browserSpeechLookaheadRef.current = browserSpeechChunks.slice(1, 1 + BROWSER_SPEECH_LOOKAHEAD_COUNT);
  }, [browserSpeechChunks]);

  useEffect(() => {
    const normalizedTranslationMode = normalizeTranslationPlaybackMode(translationPlaybackMode);

    if (
      normalizedTranslationMode === TRANSLATION_PLAYBACK_MODES.OFF ||
      !blocks.length
    ) {
      return;
    }

    const safeIndex = clampIndex(currentIndex, blocks.length);
    const chunkPosition = findSpeechChunkPosition(browserSpeechChunks, safeIndex, 0);
    const indexesToWarm = new Set([safeIndex]);
    const lookAheadChunkCount = BROWSER_SPEECH_LOOKAHEAD_COUNT + 2;

    if (safeIndex > 0) {
      indexesToWarm.add(safeIndex - 1);
    }

    if (safeIndex + 1 < blocks.length) {
      indexesToWarm.add(safeIndex + 1);
    }

    if (chunkPosition.chunkIndex >= 0) {
      browserSpeechChunks
        .slice(
          chunkPosition.chunkIndex,
          chunkPosition.chunkIndex + 1 + lookAheadChunkCount
        )
        .forEach((chunk) => {
          for (
            let blockIndex = chunk.startBlockIndex;
            blockIndex <= chunk.endBlockIndex;
            blockIndex += 1
          ) {
            indexesToWarm.add(blockIndex);
          }
        });
    }

    void warmTranslationCache(
      Array.from(indexesToWarm)
        .sort((a, b) => a - b)
        .map((blockIndex) => blocks[blockIndex])
        .filter(Boolean),
      normalizedTranslationMode,
      {
        preferRemote: true,
        onRemoteUpdate: () => {
          setTranslationPlaybackVersion((currentValue) => currentValue + 1);
        }
      }
    );
  }, [blocks, browserSpeechChunks, currentIndex, translationPlaybackMode]);

  useEffect(() => {
    if (!pendingAutoPlayRef.current) {
      return;
    }

    if (!blocks.length) {
      pendingAutoPlayRef.current = false;
      return;
    }

    pendingAutoPlayRef.current = false;
    const nextTarget = pendingAutoPlayTargetRef.current;
    pendingAutoPlayTargetRef.current = null;
    const nextIndex = clampIndex(nextTarget?.index || 0, blocks.length);
    const nextOffsetChars = Math.max(0, Number(nextTarget?.offsetChars) || 0);
    currentIndexRef.current = nextIndex;
    queuedOffsetRef.current = nextOffsetChars;

    if (aiAudioAvailable || supportsSpeechFallback) {
      setStatus(content.statusAutoStarted);
      window.setTimeout(
        () => speakFromIndexRef.current(nextIndex, { offsetChars: nextOffsetChars }),
        50
      );
      return;
    }

    setStatus(content.statusNoSpeech);
  }, [
    blocks,
    content.statusAutoStarted,
    content.statusNoSpeech,
    aiAudioAvailable,
    supportsSpeechFallback
  ]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    setReaderTextValue(currentChapterText);
  }, [currentChapterIndex, currentChapterText]);

  useEffect(() => {
    chaptersRef.current = chapters;
  }, [chapters]);

  useEffect(() => {
    currentChapterIndexRef.current = currentChapterIndex;
  }, [currentChapterIndex]);

  useEffect(() => {
    persistReaderStateRef.current();
  }, [preferredReadingLanguage]);

  useEffect(() => {
    const pendingResume = voiceSelectionResumeRef.current;

    if (!pendingResume) {
      return;
    }

    const chapterBlocks = splitIntoBlocks(
      chaptersRef.current[pendingResume.chapterIndex]?.text || ""
    );

    if (!chapterBlocks.length) {
      voiceSelectionResumeRef.current = null;
      return;
    }

    const safeIndex = clampIndex(pendingResume.blockIndex, chapterBlocks.length);
    const safeOffsetChars = clampNumber(
      pendingResume.offsetChars,
      0,
      chapterBlocks[safeIndex]?.text?.length || 0
    );
    const nextAudioMode = aiAudioAvailable
      ? "ai-audio"
      : supportsSpeechFallback
      ? "browser-fallback"
      : "none";

    setCurrentChapterIndex(pendingResume.chapterIndex);
    currentChapterIndexRef.current = pendingResume.chapterIndex;
    setCurrentIndex(safeIndex);
    currentIndexRef.current = safeIndex;
    blocksRef.current = chapterBlocks;
    queuedOffsetRef.current = safeOffsetChars;
    syncProgressToCursorRef.current(safeIndex, safeOffsetChars);

    if (pendingResume.shouldStayPaused) {
      voiceSelectionResumeRef.current = null;
      playbackIntentRef.current = false;
      pauseOriginRef.current = "user";
      setIsPlaying(false);
      setIsPaused(true);
      setPlaybackPhaseStateRef.current("pausedByUser", {
        source: "voice-selection-paused",
        chapterIndex: pendingResume.chapterIndex,
        blockIndex: safeIndex
      });
      persistReaderStateRef.current({
        playbackSnapshot: {
          phase: "pausedByUser",
          audioMode: nextAudioMode,
          pauseOrigin: "user",
          shouldResume: false,
          chapterIndex: pendingResume.chapterIndex,
          blockIndex: safeIndex,
          offsetChars: safeOffsetChars,
          elapsedSeconds: pendingResume.elapsedSeconds,
          totalSeconds: pendingResume.totalSeconds
        }
      });
      return;
    }

    if (pendingResume.shouldResume && (aiAudioAvailable || supportsSpeechFallback)) {
      const pendingId = pendingResume.id;

      playbackIntentRef.current = true;
      pauseOriginRef.current = "none";
      setPlaybackPhaseStateRef.current(aiAudioAvailable ? "loading" : "fallback", {
        source: "voice-selection-resume-pending",
        chapterIndex: pendingResume.chapterIndex,
        blockIndex: safeIndex
      });
      setStatus(content.statusVoiceUpdated);

      window.setTimeout(() => {
        if (voiceSelectionResumeRef.current?.id !== pendingId) {
          return;
        }

        voiceSelectionResumeRef.current = null;
        speakFromIndexRef.current(safeIndex, {
          offsetChars: safeOffsetChars,
          forceBrowserFallback: !aiAudioAvailable,
          source: "voice-selection-resume"
        });
      }, aiAudioAvailable ? 90 : 40);
      return;
    }

    voiceSelectionResumeRef.current = null;
  }, [
    selectedEnglishVoiceURI,
    selectedFrenchVoiceURI,
    selectedChineseVoiceURI,
    aiAudioAvailable,
    supportsSpeechFallback,
    content.statusVoiceUpdated
  ]);

  rateRef.current = rate;
  isPlayingRef.current = isPlaying;
  isPausedRef.current = isPaused;
  playbackPhaseRef.current = playbackPhase;
  savedBooksRef.current = savedBooks;

  useEffect(() => {
    setShelfStorageStats(buildShelfStorageStats(savedBooks));
  }, [
    savedBooks,
    activeBookId,
    bookTitle,
    chapters,
    currentIndex,
    currentChapterIndex,
    rate,
    selectedEnglishVoiceURI,
    selectedFrenchVoiceURI,
    selectedChineseVoiceURI,
    lastImportedFileName,
    lastImportedFileType,
    isPlaying,
    isPaused,
    playbackPhase
  ]);

  useEffect(() => {
    setShelfStartIndex((previous) => Math.min(previous, maxShelfStartIndex));
  }, [maxShelfStartIndex]);

  useEffect(() => {
    setShelfStartIndex(0);
  }, [activeBookId, offerBrowserSpeechResume]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const container = inlineQueueRef.current;
    const activeItem = container?.querySelector(".readerLabQueueItem.active");

    if (!container || !activeItem) {
      return;
    }

    const padding = 12;
    const nextTop = activeItem.offsetTop - container.clientHeight * 0.3;
    const itemTop = activeItem.offsetTop;
    const itemBottom = itemTop + activeItem.offsetHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;

    if (itemTop < viewTop + padding || itemBottom > viewBottom - padding) {
      container.scrollTo({
        top: Math.max(nextTop, 0),
        behavior: "smooth"
      });
    }
  }, [currentIndex, currentChapterIndex, isVisible]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (personalApiKey) {
      window.sessionStorage.setItem(PERSONAL_API_KEY_SESSION_KEY, personalApiKey);
      return;
    }

    window.sessionStorage.removeItem(PERSONAL_API_KEY_SESSION_KEY);
  }, [personalApiKey]);

  useEffect(() => {
    browserFallbackForcedRef.current = false;
    setAiPlaybackSuppressed(false);
  }, [audioConfig.personalKeyRequired, personalApiKey]);

  useEffect(() => {
    if (FREE_BROWSER_SPEECH_ONLY_MODE) {
      setAudioConfig({
        checked: true,
        hasServerKey: false,
        personalKeyRequired: false,
        publicAiAudioMode: "browser-speech-only",
        model: "",
        chapterAudioCache: null
      });
      return undefined;
    }

    let isMounted = true;

    fetchEchoAudioConfig()
      .then((config) => {
        if (!isMounted) {
          return;
        }

        setAudioConfig({
          checked: true,
          hasServerKey: Boolean(config?.hasServerKey),
          personalKeyRequired:
            typeof config?.personalKeyRequired === "boolean"
              ? config.personalKeyRequired
              : true,
          publicAiAudioMode:
            typeof config?.publicAiAudioMode === "string"
              ? config.publicAiAudioMode
              : "personal-key-only",
          model: typeof config?.model === "string" ? config.model : "",
          chapterAudioCache:
            config?.chapterAudioCache && typeof config.chapterAudioCache === "object"
              ? config.chapterAudioCache
              : null
        });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setAudioConfig({
          checked: true,
          hasServerKey: false,
          personalKeyRequired: true,
          publicAiAudioMode: "personal-key-only",
          model: "",
          chapterAudioCache: null
        });
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    persistReaderStateRef.current();
  }, [
    activeBookId,
    bookTitle,
    bookCoverImage,
    chapters,
    currentIndex,
    currentChapterIndex,
    playbackPhase,
    isPaused,
    isPlaying,
    rate,
    selectedEnglishVoiceURI,
    selectedFrenchVoiceURI,
    selectedChineseVoiceURI,
    lastImportedFileName,
    lastImportedFileType
  ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(
        VOICE_PREFERENCES_KEY,
        JSON.stringify({
          selectedEnglishVoiceURI,
          selectedFrenchVoiceURI,
          selectedChineseVoiceURI
        })
      );
    } catch (error) {
      debugLog("voice-preferences-persist-failed", {
        message: error instanceof Error ? error.message : "Unknown voice preference persistence error."
      });
    }
  }, [selectedEnglishVoiceURI, selectedFrenchVoiceURI, selectedChineseVoiceURI]);

  useEffect(() => {
    if (restoreAppliedRef.current) {
      return;
    }

    restoreAppliedRef.current = true;

    const snapshot = restorePlaybackSnapshotRef.current;

    if (!snapshot) {
      return;
    }

    if (
      snapshot.activeBookId &&
      activeBookId &&
      snapshot.activeBookId !== activeBookId
    ) {
      debugLog("restore-skipped", {
        reason: "book-mismatch",
        snapshotBookId: snapshot.activeBookId,
        activeBookId
      });
      return;
    }

    const nextChapterIndex = clampIndex(snapshot.chapterIndex, chaptersRef.current.length);
    const nextBlocks = splitIntoBlocks(chaptersRef.current[nextChapterIndex]?.text || "");

    if (!nextBlocks.length) {
      debugLog("restore-skipped", {
        reason: "missing-blocks",
        chapterIndex: nextChapterIndex
      });
      return;
    }

    const nextBlockIndex = clampIndex(snapshot.blockIndex, nextBlocks.length);
    const nextOffsetChars = clampNumber(
      snapshot.offsetChars,
      0,
      nextBlocks[nextBlockIndex]?.text?.length || 0
    );
    const nextModel = buildChapterProgressModel(nextBlocks, rateRef.current);
    const targetTotalSeconds = snapshot.totalSeconds || nextModel.totalSeconds;
    const targetElapsedSeconds = clampNumber(
      snapshot.elapsedSeconds,
      0,
      targetTotalSeconds
    );

    currentChapterIndexRef.current = nextChapterIndex;
    currentIndexRef.current = nextBlockIndex;
    blocksRef.current = nextBlocks;
    queuedOffsetRef.current = nextOffsetChars;
    pauseOriginRef.current = snapshot.pauseOrigin;
    playbackIntentRef.current = false;

    setCurrentChapterIndex(nextChapterIndex);
    setCurrentIndex(nextBlockIndex);
    setIsPlaying(false);
    setIsPaused(Boolean(snapshot.userPaused || snapshot.pauseOrigin === "user"));
    setPlaybackPhaseStateRef.current(
      snapshot.pauseOrigin === "user"
        ? "pausedByUser"
        : snapshot.shouldResume
        ? "pausedBySystem"
        : "ready",
      {
        source: "restore",
        chapterIndex: nextChapterIndex,
        blockIndex: nextBlockIndex
      }
    );

    playbackProgressRef.current = {
      elapsedSeconds: targetElapsedSeconds,
      totalSeconds: targetTotalSeconds,
      segmentStartTimeMs: 0,
      segmentStartSeconds: targetElapsedSeconds
    };
    updateProgressSnapshot(targetElapsedSeconds, targetTotalSeconds);

    debugLog("restore-applied", {
      chapterIndex: nextChapterIndex,
      blockIndex: nextBlockIndex,
      offsetChars: nextOffsetChars,
      elapsedSeconds: targetElapsedSeconds,
      shouldResume: snapshot.shouldResume,
      pauseOrigin: snapshot.pauseOrigin,
      audioMode: snapshot.audioMode
    });

    if (
      snapshot.audioMode === "browser-fallback" &&
      snapshot.shouldResume &&
      snapshot.pauseOrigin !== "user"
    ) {
      offerBrowserSpeechResume("restore", snapshot);
    }

    if (snapshot.audioMode !== "ai-audio" || !snapshot.shouldResume) {
      restorePlaybackSnapshotRef.current = null;
    }
  }, [activeBookId, offerBrowserSpeechResume]);

  useEffect(() => {
    const normalizedChapters = sanitizeChapters(chapters);
    const hasReadableText = normalizedChapters.some((chapter) => chapter.text.trim());
    const shouldPersist =
      autoSaveEnabledRef.current &&
      hasReadableText &&
      (
        Boolean(activeBookId) ||
        Boolean(lastImportedFileName) ||
        Boolean(lastImportedFileType) ||
        normalizedChapters.length > 1 ||
        bookTitle !== content.workspaceTitle
      );

    if (!shouldPersist) {
      return;
    }

    const snapshot = sanitizeSavedBook({
      id: activeBookId || createBookId(lastImportedFileName || bookTitle),
      bookTitle,
      coverImage: bookCoverImage,
      fileType: lastImportedFileType,
      lastImportedFileName,
      currentChapterIndex,
      currentIndex,
      rate,
      translationPlaybackMode,
      selectedEnglishVoiceURI,
      selectedFrenchVoiceURI,
      selectedChineseVoiceURI,
      updatedAt: Date.now(),
      chapters: normalizedChapters
    });

    if (!snapshot) {
      return;
    }

    const nextBooks = [
      snapshot,
      ...savedBooksRef.current.filter((book) => book.id !== snapshot.id)
    ];
    const result = commitSavedBooks(nextBooks);

    if (result.trimmed && !shelfNoticeRef.current.trimmed) {
      shelfNoticeRef.current.trimmed = true;
      setStatus(content.statusSaveLimited);
    } else if (!result.trimmed) {
      shelfNoticeRef.current.trimmed = false;
    }

    if (!result.saved && !shelfNoticeRef.current.failed) {
      shelfNoticeRef.current.failed = true;
      setStatus(content.statusSaveFailed);
    } else if (result.saved) {
      shelfNoticeRef.current.failed = false;
    }
  }, [
    activeBookId,
    bookTitle,
    bookCoverImage,
    chapters,
    currentIndex,
    currentChapterIndex,
    rate,
    translationPlaybackMode,
    selectedEnglishVoiceURI,
    selectedFrenchVoiceURI,
    selectedChineseVoiceURI,
    lastImportedFileName,
    lastImportedFileType,
    content.statusSaveFailed,
    content.statusSaveLimited,
    content.workspaceTitle
  ]);

  useEffect(() => {
    if (!isPlaying && !isPaused) {
      const cachedEntry = chapterAudioCacheRef.current.get(currentChapterAudioKey);

      chapterTimingRef.current = cachedEntry || {
        key: currentChapterAudioKey,
        durations: [],
        totalSeconds: 0
      };
      syncProgressToCursorRef.current(currentIndexRef.current, queuedOffsetRef.current);
    }
  }, [currentChapterAudioKey, currentIndex, isPlaying, isPaused]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.preload = "auto";
    audio.playsInline = true;
    audio.setAttribute("playsinline", "");
    audio.setAttribute("webkit-playsinline", "");
    audio.setAttribute("x-webkit-airplay", "allow");
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
    }
    cancelAllRecoveryTimers();
    clearSleepTimeout();
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.src = "";
      previewAudioRef.current = null;
    }
    if (supportsSpeechFallback) {
      window.speechSynthesis.cancel();
      speechUtteranceRef.current = null;
    }
    chapterAudioCacheRef.current.forEach((entry) => {
      window.URL.revokeObjectURL(entry.url);
    });
    chapterAudioCacheRef.current.clear();
    releaseWakeLock();
  }, [cancelAllRecoveryTimers, clearSleepTimeout, supportsSpeechFallback]);

  useEffect(() => {
    setStatus((previous) => {
      if (
        previous === LAB_CONTENT.en.statusNoSpeech ||
        previous === LAB_CONTENT.fr.statusNoSpeech ||
        previous === LAB_CONTENT.zh.statusNoSpeech ||
        previous === LAB_CONTENT.en.statusReady ||
        previous === LAB_CONTENT.fr.statusReady ||
        previous === LAB_CONTENT.zh.statusReady
      ) {
        if (!supportsAudio) {
          return supportsSpeechFallback ? content.statusReady : content.statusNoSpeech;
        }

        if (audioConfig.checked && !aiAudioAvailable && !supportsSpeechFallback) {
          return content.statusNoSpeech;
        }

        return content.statusReady;
      }

      return previous;
    });
  }, [
    audioConfig.checked,
    aiAudioAvailable,
    content.statusNoSpeech,
    content.statusReady,
    supportsAudio,
    supportsSpeechFallback
  ]);

  function updateProgressSnapshot(nextElapsedSeconds, nextTotalSeconds) {
    const safeTotalSeconds = Math.max(0, nextTotalSeconds || 0);
    const safeElapsedSeconds = clampNumber(nextElapsedSeconds || 0, 0, safeTotalSeconds);

    playbackProgressRef.current.elapsedSeconds = safeElapsedSeconds;
    playbackProgressRef.current.totalSeconds = safeTotalSeconds;

    setProgressState({
      elapsedSeconds: safeElapsedSeconds,
      totalSeconds: safeTotalSeconds,
      progressPercent: safeTotalSeconds ? (safeElapsedSeconds / safeTotalSeconds) * 100 : 0,
      progressText: `${formatPlaybackTime(safeElapsedSeconds)} / ${formatPlaybackTime(safeTotalSeconds)}`
    });
  }

  function updateReadingWindowPlaybackCursor(
    chapterIndex = currentChapterIndexRef.current,
    blockIndex = currentIndexRef.current,
    offsetChars = queuedOffsetRef.current,
    options = {}
  ) {
    const chapterBlocks = blocksRef.current;
    const safeBlockIndex = clampIndex(blockIndex, chapterBlocks.length);
    const maxOffsetChars = (chapterBlocks[safeBlockIndex]?.text || "").length;
    const safeOffsetChars = clampNumber(offsetChars || 0, 0, maxOffsetChars);
    const nextCursor = {
      chapterIndex: clampIndex(chapterIndex, chaptersRef.current.length),
      blockIndex: safeBlockIndex,
      offsetChars: safeOffsetChars
    };
    const previousCursor = readingWindowPlaybackCursorRef.current;
    const shouldSkip =
      !options.force &&
      previousCursor.chapterIndex === nextCursor.chapterIndex &&
      previousCursor.blockIndex === nextCursor.blockIndex &&
      Math.abs(previousCursor.offsetChars - nextCursor.offsetChars) < READING_WINDOW_OFFSET_UPDATE_MIN_DELTA;

    if (shouldSkip) {
      return;
    }

    readingWindowPlaybackCursorRef.current = nextCursor;
    setReadingWindowPlaybackCursor((currentValue) => {
      if (
        currentValue.chapterIndex === nextCursor.chapterIndex &&
        currentValue.blockIndex === nextCursor.blockIndex &&
        currentValue.offsetChars === nextCursor.offsetChars
      ) {
        return currentValue;
      }

      return nextCursor;
    });
  }

  function stopProgressTicker() {
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }

  async function settleManualSeek(source = "manual-seek") {
    if (!isManualSeekActive()) {
      return false;
    }

    const audio = audioRef.current;

    if (!audio || audio.ended) {
      clearManualSeekGuard();
      return false;
    }

    if (!manualSeekShouldResumeRef.current || !playbackIntentRef.current || isPausedRef.current) {
      clearManualSeekGuard();
      return false;
    }

    if (audio.paused) {
      try {
        const playPromise = audio.play();

        if (playPromise && typeof playPromise.then === "function") {
          await playPromise;
        }
      } catch (error) {
        debugLog("manual-seek-resume-failed", {
          source,
          message: error instanceof Error ? error.message : "Unknown seek resume error."
        });
        schedulePlaybackRecovery(80);
        return false;
      }
    }

    if (!audio.paused) {
      clearManualSeekGuard();
      return true;
    }

    return false;
  }

  function runWithAudioPauseGuard(callback) {
    audioPauseGuardCountRef.current += 1;

    try {
      callback();
    } finally {
      window.setTimeout(() => {
        audioPauseGuardCountRef.current = Math.max(audioPauseGuardCountRef.current - 1, 0);
      }, 0);
    }
  }

  function hasCurrentChapterLoadedAudio() {
    const audio = audioRef.current;

    return Boolean(
      audio &&
      audio.dataset.cacheKey &&
      audio.dataset.cacheKey === currentChapterAudioKey &&
      audio.src
    );
  }

  function getPersistableAudioStreamUrl(audio, expectedCacheKey = "") {
    if (!audio) {
      return "";
    }

    const activeCacheKey = String(expectedCacheKey || audio.dataset.cacheKey || "").trim();

    if (!activeCacheKey) {
      return "";
    }

    return normalizeEchoAudioStreamUrl(audio.currentSrc || audio.src || "", activeCacheKey);
  }

  function buildEstimatedChapterAudioEntry({
    chapterIndex,
    cacheKey,
    streamUrl,
    chapterBlocks,
    totalSeconds = 0,
    cacheStatus = "snapshot",
    cacheStore = "persisted",
    cacheDedupe = "none"
  }) {
    const model = buildChapterProgressModel(chapterBlocks, rateRef.current);
    const durations = model.items.map((item) => item.durationSeconds);

    return {
      key: cacheKey,
      chapterIndex,
      url: streamUrl,
      directStream: true,
      cacheStatus,
      cacheStore,
      cacheDedupe,
      durations,
      totalSeconds: totalSeconds || model.totalSeconds
    };
  }

  function hydrateSnapshotAudioEntry(snapshot, source = "snapshot-restore") {
    if (!snapshot || snapshot.audioMode !== "ai-audio") {
      return null;
    }

    const chapterIndex = clampIndex(snapshot.chapterIndex, chaptersRef.current.length);
    const chapter = chaptersRef.current[chapterIndex] || null;

    if (!chapter) {
      return null;
    }

    const chapterBlocks = splitIntoBlocks(chapter.text || "");

    if (!chapterBlocks.length) {
      return null;
    }

    const expectedCacheKey = getAudioCacheKeyForChapter(chapterIndex);
    const snapshotCacheKey = String(snapshot.audioCacheKey || expectedCacheKey || "").trim();
    const normalizedStreamUrl = normalizeEchoAudioStreamUrl(
      snapshot.audioStreamUrl,
      snapshotCacheKey
    );

    if (!snapshotCacheKey || !normalizedStreamUrl) {
      return null;
    }

    if (expectedCacheKey && snapshotCacheKey !== expectedCacheKey) {
      debugLog("snapshot-audio-skipped", {
        source,
        reason: "cache-key-mismatch",
        chapterIndex,
        snapshotCacheKey,
        expectedCacheKey
      });
      return null;
    }

    const existingEntry = chapterAudioCacheRef.current.get(snapshotCacheKey);

    if (existingEntry?.url === normalizedStreamUrl) {
      return existingEntry;
    }

    const entry = buildEstimatedChapterAudioEntry({
      chapterIndex,
      cacheKey: snapshotCacheKey,
      streamUrl: normalizedStreamUrl,
      chapterBlocks,
      totalSeconds: snapshot.totalSeconds,
      cacheStatus: "snapshot",
      cacheStore: "persisted"
    });

    chapterAudioCacheRef.current.set(snapshotCacheKey, entry);
    pruneGeneratedChapterCache(chapterIndex);
    debugLog("snapshot-audio-hydrated", {
      source,
      chapterIndex,
      cacheKey: snapshotCacheKey
    });

    return entry;
  }

  function warmUpcomingChapterAudio(chapterIndex = currentChapterIndexRef.current, source = "chapter-start-prefetch") {
    if (!aiAudioAvailable || !supportsAudio) {
      return;
    }

    const maxChapterIndex = chaptersRef.current.length - 1;

    if (chapterIndex >= maxChapterIndex) {
      return;
    }

    const prefetchKey = `${getAudioCacheKeyForChapter(chapterIndex)}:${chapterIndex}`;

    if (!prefetchKey || chapterStartPrefetchKeyRef.current === prefetchKey) {
      return;
    }

    chapterStartPrefetchKeyRef.current = prefetchKey;
    debugLog("chapter-prefetch-armed", {
      source,
      chapterIndex
    });

    prefetchUpcomingChapters(chapterIndex, BACKGROUND_PREFETCH_AHEAD, {
      source
    })
      .then((didPrefetchAll) => {
        if (!didPrefetchAll && chapterStartPrefetchKeyRef.current === prefetchKey) {
          chapterStartPrefetchKeyRef.current = "";
        }
      })
      .catch(() => {
        if (chapterStartPrefetchKeyRef.current === prefetchKey) {
          chapterStartPrefetchKeyRef.current = "";
        }
      });
  }

  function getCurrentPlaybackModel() {
    return buildChapterProgressModel(
      blocksRef.current,
      rateRef.current,
      chapterTimingRef.current.durations
    );
  }

  function getAudioCacheKeyForChapter(chapterIndex) {
    const chapter = chapters[chapterIndex] || null;

    if (!chapter) {
      return "";
    }

    return createChapterAudioCacheKey({
      chapterTitle: chapter.title || "",
      chapterText: chapter.text || "",
      selectedEnglishVoiceURI,
      selectedFrenchVoiceURI,
      selectedChineseVoiceURI
    });
  }

  function revokeCachedChapterAudio(entry) {
    if (entry?.url && String(entry.url).startsWith("blob:")) {
      window.URL.revokeObjectURL(entry.url);
    }
  }

  function resetChapterTiming() {
    chapterTimingRef.current = {
      key: "",
      durations: [],
      totalSeconds: 0
    };
  }

  function clearGeneratedChapterCache() {
    chapterAudioGenerationRef.current += 1;
    chapterAudioRequestsRef.current.clear();
    chapterAudioCacheRef.current.forEach((entry) => revokeCachedChapterAudio(entry));
    chapterAudioCacheRef.current.clear();
    restoreWarmupDoneRef.current = false;
    resetChapterTiming();
    clearRecoveryTimeout();
    shouldRecoverPlaybackRef.current = false;
    recoveryAttemptRef.current = 0;

    const audio = audioRef.current;

    if (audio) {
      runWithAudioPauseGuard(() => {
        audio.pause();
        audio.removeAttribute("src");
        delete audio.dataset.cacheKey;
        delete audio.dataset.directStream;
        audio.load();
      });
    }
  }

  function getCurrentEstimatedElapsedSeconds() {
    const audio = audioRef.current;
    const cachedEntry = chapterTimingRef.current;

    if (
      audio &&
      audio.dataset.cacheKey &&
      cachedEntry.key &&
      audio.dataset.cacheKey === cachedEntry.key
    ) {
      const totalSeconds =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : cachedEntry.totalSeconds;

      return clampNumber(audio.currentTime || 0, 0, totalSeconds || 0);
    }

    const currentProgress = playbackProgressRef.current;

    if (!currentProgress.totalSeconds) {
      const fallbackModel = getCurrentPlaybackModel();
      return getElapsedForBlockOffset(
        fallbackModel,
        blocksRef.current,
        currentIndexRef.current,
        queuedOffsetRef.current
      );
    }

    if (!currentProgress.segmentStartTimeMs) {
      return clampNumber(
        currentProgress.segmentStartSeconds || currentProgress.elapsedSeconds || 0,
        0,
        currentProgress.totalSeconds
      );
    }

    return clampNumber(
      currentProgress.segmentStartSeconds + (performance.now() - currentProgress.segmentStartTimeMs) / 1000,
      0,
      currentProgress.totalSeconds
    );
  }

  function refreshProgressTicker() {
    const model = getCurrentPlaybackModel();
    const elapsedSeconds = getCurrentEstimatedElapsedSeconds();
    const target = findSeekTarget(model, blocksRef.current, elapsedSeconds);
    const totalSeconds =
      chapterTimingRef.current.totalSeconds ||
      playbackProgressRef.current.totalSeconds ||
      model.totalSeconds;

    if (target.blockIndex !== currentIndexRef.current) {
      setCurrentIndex(target.blockIndex);
      currentIndexRef.current = target.blockIndex;
    }

    queuedOffsetRef.current = target.offsetChars;
    updateReadingWindowPlaybackCursor(
      currentChapterIndexRef.current,
      target.blockIndex,
      target.offsetChars
    );
    updateProgressSnapshot(elapsedSeconds, totalSeconds);
  }

  function syncProgressToCursor(index = currentIndexRef.current, offsetChars = queuedOffsetRef.current) {
    const model = getCurrentPlaybackModel();
    const safeIndex = clampIndex(index, blocksRef.current.length);
    const elapsedSeconds = getElapsedForBlockOffset(model, blocksRef.current, safeIndex, offsetChars);

    stopProgressTicker();
    playbackProgressRef.current = {
      elapsedSeconds,
      totalSeconds: model.totalSeconds,
      segmentStartTimeMs: 0,
      segmentStartSeconds: elapsedSeconds
    };
    updateReadingWindowPlaybackCursor(currentChapterIndexRef.current, safeIndex, offsetChars, {
      force: true
    });
    updateProgressSnapshot(elapsedSeconds, model.totalSeconds);
  }

  syncProgressToCursorRef.current = syncProgressToCursor;

  function beginProgressTracking(index, offsetChars = 0) {
    const model = getCurrentPlaybackModel();
    const safeIndex = clampIndex(index, blocksRef.current.length);
    const elapsedSeconds = getElapsedForBlockOffset(model, blocksRef.current, safeIndex, offsetChars);

    stopProgressTicker();
    playbackProgressRef.current = {
      elapsedSeconds,
      totalSeconds: model.totalSeconds,
      segmentStartTimeMs: performance.now(),
      segmentStartSeconds: elapsedSeconds
    };
    updateReadingWindowPlaybackCursor(currentChapterIndexRef.current, safeIndex, offsetChars, {
      force: true
    });
    updateProgressSnapshot(elapsedSeconds, model.totalSeconds);
    progressIntervalRef.current = window.setInterval(refreshProgressTicker, 250);
  }

  function freezeProgressTracking() {
    const totalSeconds =
      playbackProgressRef.current.totalSeconds ||
      getCurrentPlaybackModel().totalSeconds;
    const elapsedSeconds = getCurrentEstimatedElapsedSeconds();

    stopProgressTicker();
    playbackProgressRef.current = {
      ...playbackProgressRef.current,
      elapsedSeconds,
      totalSeconds,
      segmentStartTimeMs: 0,
      segmentStartSeconds: elapsedSeconds
    };
    updateReadingWindowPlaybackCursor();
    updateProgressSnapshot(elapsedSeconds, totalSeconds);
  }

  function resumeProgressTracking() {
    const totalSeconds =
      playbackProgressRef.current.totalSeconds ||
      getCurrentPlaybackModel().totalSeconds;
    const elapsedSeconds = getCurrentEstimatedElapsedSeconds();

    stopProgressTicker();
    playbackProgressRef.current = {
      ...playbackProgressRef.current,
      elapsedSeconds,
      totalSeconds,
      segmentStartTimeMs: performance.now(),
      segmentStartSeconds: elapsedSeconds
    };
    updateReadingWindowPlaybackCursor();
    updateProgressSnapshot(elapsedSeconds, totalSeconds);
    progressIntervalRef.current = window.setInterval(refreshProgressTicker, 250);
  }

  freezeProgressTrackingRef.current = freezeProgressTracking;
  resumeProgressTrackingRef.current = resumeProgressTracking;

  async function ensureChapterAudioLoaded(chapterIndex, options = {}) {
    const chapter = chapters[chapterIndex] || null;
    const requestSource = options.source || (options.prefetch ? "prefetch" : "foreground");

    if (!chapter) {
      throw new Error(content.noBlocks);
    }

    const chapterBlocks = splitIntoBlocks(chapter.text || "");

    if (!chapterBlocks.length) {
      throw new Error(content.noBlocks);
    }

    const cacheKey = getAudioCacheKeyForChapter(chapterIndex);

    if (chapterAudioCacheRef.current.has(cacheKey)) {
      debugLog("chapter-audio-hit", {
        source: requestSource,
        chapterIndex,
        cacheKey
      });
      return chapterAudioCacheRef.current.get(cacheKey);
    }

    if (chapterAudioRequestsRef.current.has(cacheKey)) {
      debugLog("chapter-audio-wait", {
        source: requestSource,
        chapterIndex,
        cacheKey
      });
      return chapterAudioRequestsRef.current.get(cacheKey);
    }

    const requestPromise = (async () => {
      if (!aiAudioAvailable) {
        throw new Error("AI chapter audio is temporarily unavailable.");
      }

      const isForegroundLoad = !options.prefetch;
      const requestGeneration = chapterAudioGenerationRef.current;

      if (isForegroundLoad) {
        setIsGeneratingAudio(true);
        setStatus(content.statusVoiceWait);
      }

      try {
        const response = await requestChapterAudio({
          segments: chapterBlocks,
          selectedEnglishVoiceURI,
          selectedFrenchVoiceURI,
          selectedChineseVoiceURI,
          personalApiKey,
          preferStreamResponse: prefersDirectAiAudioStream
        });
        const useDirectStream = Boolean(response.streamUrl);
        const blobUrl = useDirectStream
          ? response.streamUrl
          : window.URL.createObjectURL(
              new Blob([response.audioBuffer], {
                type: response.mimeType || "audio/wav"
              })
            );

        if (requestGeneration !== chapterAudioGenerationRef.current) {
          if (!useDirectStream) {
            window.URL.revokeObjectURL(blobUrl);
          }
          throw new Error("Audio request expired.");
        }

        const entry = {
          key: cacheKey,
          chapterIndex,
          url: blobUrl,
          directStream: useDirectStream,
          cacheStatus: response.cacheStatus,
          cacheStore: response.cacheStore,
          cacheDedupe: response.cacheDedupe,
          durations:
            response.durations.length === chapterBlocks.length
              ? response.durations
              : chapterBlocks.map((block) =>
                  estimateBlockDurationSeconds(
                    block.text,
                    block.language,
                    rateRef.current
                  )
                )
        };

        entry.totalSeconds = entry.durations.reduce((sum, value) => sum + value, 0);
        chapterAudioCacheRef.current.set(cacheKey, entry);
        pruneGeneratedChapterCache(chapterIndex);
        debugLog("chapter-audio-ready", {
          source: requestSource,
          chapterIndex,
          cacheKey,
          cacheStatus: response.cacheStatus || "unknown",
          cacheStore: response.cacheStore || "unknown",
          cacheDedupe: response.cacheDedupe || "none"
        });
        return entry;
      } catch (error) {
        if (canUseBrowserSpeechFallback(error)) {
          suppressAiPlaybackForSession(error, `${requestSource}-ensure`);
        }
        debugLog("chapter-audio-failed", {
          source: requestSource,
          chapterIndex,
          cacheKey,
          message: error instanceof Error ? error.message : "Unknown chapter audio error."
        });
        throw error;
      } finally {
        if (isForegroundLoad) {
          setIsGeneratingAudio(false);
        }
      }
    })();

    chapterAudioRequestsRef.current.set(cacheKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      chapterAudioRequestsRef.current.delete(cacheKey);
    }
  }

  async function attachChapterAudio(entry, targetElapsedSeconds) {
    const audio = audioRef.current;

    if (!audio) {
      throw new Error(content.statusError);
    }

    const loadMetadata = () =>
      new Promise((resolve, reject) => {
        const handleLoaded = () => {
          audio.removeEventListener("loadedmetadata", handleLoaded);
          audio.removeEventListener("error", handleError);
          resolve();
        };
        const handleError = () => {
          audio.removeEventListener("loadedmetadata", handleLoaded);
          audio.removeEventListener("error", handleError);
          reject(new Error(content.statusError));
        };

        audio.addEventListener("loadedmetadata", handleLoaded);
        audio.addEventListener("error", handleError);
      });

    if (audio.dataset.cacheKey !== entry.key) {
      audio.pause();
      audio.src = entry.url;
      audio.preload = "auto";
      audio.dataset.cacheKey = entry.key;
      audio.dataset.directStream = entry.directStream ? "1" : "0";
      audio.load();
      await loadMetadata();
    }

    audio.playbackRate = rateRef.current;
    chapterTimingRef.current = entry;

    const safeTarget = clampNumber(
      targetElapsedSeconds || 0,
      0,
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : entry.totalSeconds
    );

    if (Math.abs((audio.currentTime || 0) - safeTarget) > 0.02) {
      audio.currentTime = safeTarget;
    }

    updateProgressSnapshot(
      safeTarget,
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : entry.totalSeconds
    );

    return audio;
  }

  async function startAiChapterPlayback({
    chapterIndex = currentChapterIndexRef.current,
    blockIndex = currentIndexRef.current,
    offsetChars = queuedOffsetRef.current,
    token = playTokenRef.current,
    source = "ai-playback"
  }) {
    const targetChapter = chaptersRef.current[chapterIndex] || null;

    if (!targetChapter) {
      throw new Error(content.noBlocks);
    }

    const targetBlocks = splitIntoBlocks(targetChapter.text || "");

    if (!targetBlocks.length) {
      throw new Error(content.noBlocks);
    }

    const isChapterTransition = chapterIndex !== currentChapterIndexRef.current;
    const safeIndex = clampIndex(blockIndex, targetBlocks.length);
    const safeOffsetChars = clampNumber(
      offsetChars,
      0,
      targetBlocks[safeIndex]?.text?.length || 0
    );

    setPlaybackPhaseState(isChapterTransition ? "transitioning" : "loading", {
      source,
      chapterIndex,
      blockIndex: safeIndex
    });
    debugLog("chapter-start", {
      source,
      chapterIndex,
      blockIndex: safeIndex,
      offsetChars: safeOffsetChars
    });

    const entry = await ensureChapterAudioLoaded(chapterIndex, {
      prefetch: false,
      source
    });

    if (playTokenRef.current !== token) {
      return null;
    }

    const targetModel = buildChapterProgressModel(
      targetBlocks,
      rateRef.current,
      entry.durations
    );
    const targetElapsedSeconds = getElapsedForBlockOffset(
      targetModel,
      targetBlocks,
      safeIndex,
      safeOffsetChars
    );

    currentChapterIndexRef.current = chapterIndex;
    currentIndexRef.current = safeIndex;
    blocksRef.current = targetBlocks;
    queuedOffsetRef.current = safeOffsetChars;
    shouldRecoverPlaybackRef.current = false;
    recoveryAttemptRef.current = 0;
    clearRecoveryTimeout();
    pauseOriginRef.current = "none";
    playbackIntentRef.current = true;

    setCurrentChapterIndex(chapterIndex);
    setCurrentIndex(safeIndex);

    const audio = await attachChapterAudio(entry, targetElapsedSeconds);

    if (playTokenRef.current !== token) {
      return null;
    }

    const playPromise = audio.play();

    if (playPromise && typeof playPromise.then === "function") {
      await playPromise;
    }

    if (playTokenRef.current !== token) {
      return null;
    }

    beginProgressTracking(safeIndex, safeOffsetChars);
    setIsPlaying(true);
    setIsPaused(false);
    clearAiPlaybackSuppression(source);
    setStatus(`${content.currentParagraph}: ${safeIndex + 1}`);
    setPlaybackPhaseState("playing", {
      source,
      chapterIndex,
      blockIndex: safeIndex
    });
    requestWakeLock();
    persistReaderState({
      playbackSnapshot: {
        phase: "playing",
        audioMode: "ai-audio",
        pauseOrigin: "none",
        userPaused: false,
        shouldResume: true,
        chapterIndex,
        blockIndex: safeIndex,
        offsetChars: safeOffsetChars,
        audioCacheKey: entry.key,
        audioStreamUrl: entry.directStream ? entry.url : "",
        elapsedSeconds: targetElapsedSeconds,
        totalSeconds: entry.totalSeconds
      }
    });

    warmUpcomingChapterAudio(chapterIndex, `${source}-ahead`);

    return audio;
  }

  async function transitionToChapterPlayback(nextChapterIndex, source = "chapter-transition") {
    const nextChapter = chaptersRef.current[nextChapterIndex] || null;

    if (!nextChapter) {
      playbackIntentRef.current = false;
      setPlaybackPhaseState("ended", {
        source,
        chapterIndex: nextChapterIndex
      });
      setStatus(content.statusFinished);
      return;
    }

    const token = playTokenRef.current + 1;
    const nextChapterKey = getAudioCacheKeyForChapter(nextChapterIndex);
    const prefetchedEntry = chapterAudioCacheRef.current.get(nextChapterKey);

    playTokenRef.current = token;
    setStatus(content.statusBuffering);
    debugLog("chapter-transition-start", {
      source,
      chapterIndex: nextChapterIndex,
      prefetched: Boolean(prefetchedEntry)
    });

    try {
      await startAiChapterPlayback({
        chapterIndex: nextChapterIndex,
        blockIndex: 0,
        offsetChars: 0,
        token,
        source
      });
      debugLog("chapter-transition-ready", {
        source,
        chapterIndex: nextChapterIndex
      });
    } catch (error) {
      let transitionError = error;

      if (playTokenRef.current !== token) {
        return;
      }

      if (
        !canUseBrowserSpeechFallback(transitionError) &&
        playbackIntentRef.current &&
        !isPausedRef.current
      ) {
        debugLog("chapter-transition-retry", {
          source,
          chapterIndex: nextChapterIndex,
          message:
            transitionError instanceof Error
              ? transitionError.message
              : "Unknown transition retry error."
        });

        try {
          await new Promise((resolve) => window.setTimeout(resolve, 220));
          await startAiChapterPlayback({
            chapterIndex: nextChapterIndex,
            blockIndex: 0,
            offsetChars: 0,
            token,
            source: `${source}-retry`
          });
          debugLog("chapter-transition-retry-ready", {
            source,
            chapterIndex: nextChapterIndex
          });
          return;
        } catch (retryError) {
          transitionError = retryError;
        }
      }

      if (supportsSpeechFallback && canUseBrowserSpeechFallback(transitionError)) {
        suppressAiPlaybackForSession(transitionError, source);
      }

      debugLog("chapter-transition-failed", {
        source,
        chapterIndex: nextChapterIndex,
        message:
          transitionError instanceof Error
            ? transitionError.message
            : "Unknown transition error."
      });
      applyChapterSelection(nextChapterIndex, content.statusBuffering, {
        shouldResumePlayback: playbackIntentRef.current && !isPausedRef.current
      });
    }
  }

  async function prefetchUpcomingChapters(fromChapterIndex, lookAheadCount = 1, options = {}) {
    if (!aiAudioAvailable || !supportsAudio) {
      return false;
    }

    const requestSource = options.source || "prefetch";
    const maxChapterIndex = chaptersRef.current.length - 1;
    const endChapterIndex = Math.min(fromChapterIndex + lookAheadCount, maxChapterIndex);
    let prefetchedAllRequestedChapters = true;

    for (let chapterIndex = fromChapterIndex + 1; chapterIndex <= endChapterIndex; chapterIndex += 1) {
      try {
        debugLog("chapter-prefetch-start", {
          source: requestSource,
          chapterIndex
        });
        await ensureChapterAudioLoaded(chapterIndex, {
          prefetch: true,
          source: requestSource
        });
        debugLog("chapter-prefetch-ready", {
          source: requestSource,
          chapterIndex
        });
      } catch (error) {
        prefetchedAllRequestedChapters = false;
        debugLog("chapter-prefetch-failed", {
          source: requestSource,
          chapterIndex,
          message: error instanceof Error ? error.message : "Unknown prefetch error."
        });
        break;
      }
    }

    return prefetchedAllRequestedChapters;
  }

  async function requestWakeLock() {
    if (
      typeof navigator === "undefined" ||
      !("wakeLock" in navigator) ||
      typeof document === "undefined" ||
      document.visibilityState !== "visible" ||
      !isPlayingRef.current ||
      isPausedRef.current
    ) {
      return;
    }

    try {
      if (wakeLockRef.current) {
        return;
      }

      wakeLockRef.current = await navigator.wakeLock.request("screen");
      wakeLockRef.current.addEventListener("release", () => {
        wakeLockRef.current = null;
      });
    } catch (_error) {
      wakeLockRef.current = null;
    }
  }

  async function releaseWakeLock() {
    if (!wakeLockRef.current) {
      return;
    }

    try {
      await wakeLockRef.current.release();
    } catch (_error) {
      // Ignore release failures from browsers that already dropped the lock.
    } finally {
      wakeLockRef.current = null;
    }
  }

  function moveLoadedAudioToElapsed(elapsedSeconds) {
    const audio = audioRef.current;
    const entry = chapterAudioCacheRef.current.get(currentChapterAudioKey);

    if (!audio || !entry || audio.dataset.cacheKey !== entry.key) {
      return;
    }

    const totalSeconds =
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : entry.totalSeconds;
    const safeElapsed = clampNumber(elapsedSeconds, 0, totalSeconds);
    startManualSeekGuard({
      shouldResume: playbackIntentRef.current && !isPausedRef.current && !audio.ended
    });

    if (typeof audio.fastSeek === "function") {
      try {
        audio.fastSeek(safeElapsed);
      } catch (error) {
        audio.currentTime = safeElapsed;
      }
    } else {
      audio.currentTime = safeElapsed;
    }
    chapterTimingRef.current = entry;
    updateProgressSnapshot(safeElapsed, totalSeconds);
  }

  function stopPlayback(options = {}) {
    const preserveIntent = Boolean(options.preserveIntent);
    const stopSource = options.source || (preserveIntent ? "transition-stop" : "stop");

    pendingAutoPlayRef.current = false;
    playTokenRef.current += 1;
    clearRecoveryTimeout();
    clearFallbackContinuationTimeout();
    clearSeekResumeTimeout();
    clearManualSeekGuard();
    shouldRecoverPlaybackRef.current = false;
    recoveryAttemptRef.current = 0;
    freezeProgressTracking();
    if (audioRef.current) {
      runWithAudioPauseGuard(() => {
        audioRef.current.pause();
      });
    }
    if (supportsSpeechFallback) {
      window.speechSynthesis.cancel();
      speechUtteranceRef.current = null;
    }
    if (!preserveIntent) {
      playbackIntentRef.current = false;
      pauseOriginRef.current = "none";
    }
    setIsPlaying(false);
    setIsPaused(false);
    setPlaybackPhaseState(preserveIntent ? "transitioning" : "idle", {
      source: stopSource,
      chapterIndex: currentChapterIndexRef.current,
      blockIndex: currentIndexRef.current
    });
    if (!preserveIntent) {
      releaseWakeLock();
    }
    persistReaderState({
      playbackSnapshot: {
        phase: preserveIntent ? "transitioning" : "idle",
        pauseOrigin: preserveIntent ? pauseOriginRef.current : "none",
        userPaused: !preserveIntent && pauseOriginRef.current === "user",
        shouldResume: preserveIntent && playbackIntentRef.current,
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current,
        offsetChars: queuedOffsetRef.current,
        audioCacheKey: String(audioRef.current?.dataset.cacheKey || ""),
        audioStreamUrl: getPersistableAudioStreamUrl(
          audioRef.current,
          String(audioRef.current?.dataset.cacheKey || "")
        )
      }
    });
  }

  function handleSelectRate(nextRate) {
    if (rateRef.current === nextRate) {
      return;
    }

    setRate(nextRate);
    rateRef.current = nextRate;
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.playbackRate = nextRate;
    }

    if (!aiAudioAvailable && supportsSpeechFallback && blocksRef.current.length && (isPlaying || isPaused)) {
      const currentTarget = findSeekTarget(
        getCurrentPlaybackModel(),
        blocksRef.current,
        getCurrentEstimatedElapsedSeconds()
      );
      const shouldResume = playbackIntentRef.current;

      stopPlayback({ preserveIntent: shouldResume });
      setCurrentIndex(currentTarget.blockIndex);
      currentIndexRef.current = currentTarget.blockIndex;
      queuedOffsetRef.current = currentTarget.offsetChars;
      syncProgressToCursor(currentTarget.blockIndex, currentTarget.offsetChars);

      if (shouldResume) {
        window.setTimeout(() => {
          speakFromIndexRef.current(currentTarget.blockIndex, {
            offsetChars: currentTarget.offsetChars
          });
        }, 50);
      }
    }

    setStatus(content.statusRateChanged);
  }

  function handleRateRailPointerDown(event) {
    if (!event.currentTarget || RATE_OPTIONS.length <= 1) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    if (!rect.width) {
      return;
    }

    const ratio = clampNumber((event.clientX - rect.left) / rect.width, 0, 1);
    const nextIndex = Math.round(ratio * (RATE_OPTIONS.length - 1));
    handleSelectRate(RATE_OPTIONS[nextIndex]);
  }

  function handleVoiceSelection(setter, currentValue, nextValue) {
    if (currentValue === nextValue) {
      return;
    }

    const model = getCurrentPlaybackModel();
    const currentTarget = findSeekTarget(
      model,
      blocksRef.current,
      getCurrentEstimatedElapsedSeconds()
    );
    const shouldResume = Boolean(playbackIntentRef.current && pauseOriginRef.current !== "user");
    const shouldStayPaused = Boolean(isPausedRef.current && pauseOriginRef.current === "user");

    voiceSelectionResumeRef.current = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      chapterIndex: currentChapterIndexRef.current,
      blockIndex: currentTarget.blockIndex,
      offsetChars: currentTarget.offsetChars,
      elapsedSeconds: currentTarget.elapsedSeconds,
      totalSeconds: model.totalSeconds,
      shouldResume,
      shouldStayPaused
    };

    stopPlayback({ source: "voice-selection-change" });
    clearGeneratedChapterCache();
    setCurrentIndex(currentTarget.blockIndex);
    currentIndexRef.current = currentTarget.blockIndex;
    queuedOffsetRef.current = currentTarget.offsetChars;
    syncProgressToCursor(currentTarget.blockIndex, currentTarget.offsetChars);
    setter(nextValue);
    setStatus(content.statusVoiceUpdated);
  }

  function handleClearPersonalApiKey() {
    setPersonalApiKey("");
    setPersonalApiKeyDraft("");
    setIsEditingPersonalApiKey(true);
    setPersonalApiKeyError("");
    setStatus(supportsSpeechFallback ? content.statusReady : content.statusNoSpeech);
  }

  function handleSelectSleep(option) {
    setShowTimerMenu(false);
    if (option === "off") {
      cancelSleepTimer();
      setStatus(content.statusSleepCleared);
      return;
    }

    handleSelectSleepTimer(option, setStatus);
  }

  async function speakFromIndex(index, options = {}) {
    const forceBrowserFallback = Boolean(options.forceBrowserFallback);
    const aiPlaybackReady = audioGenerationReady && !browserFallbackForcedRef.current;
    const useBrowserFallback =
      forceBrowserFallback || !aiPlaybackReady;

    if (useBrowserFallback && !supportsSpeechFallback) {
      setStatus(content.statusNoSpeech);
      return;
    }

    const block = blocksRef.current[index];
    const blockText = block?.text || "";
    const safeOffsetChars = clampNumber(options.offsetChars || 0, 0, blockText.length);

    if (!block) {
      setStatus(content.noBlocks);
      return;
    }

    playbackIntentRef.current = true;
    pauseOriginRef.current = "none";
    shouldRecoverPlaybackRef.current = false;
    recoveryAttemptRef.current = 0;
    clearRecoveryTimeout();
    playTokenRef.current += 1;
    const token = playTokenRef.current;
    const playbackSource =
      options.source || (useBrowserFallback ? "fallback-play-request" : "ai-play-request");

    if (useBrowserFallback) {
      const playbackModel = getCurrentPlaybackModel();
      const safeIndex = clampIndex(index, blocksRef.current.length);
      const normalizedTranslationMode = normalizeTranslationPlaybackMode(
        translationPlaybackMode
      );
      const shouldSpeakTranslatedText =
        normalizedTranslationMode !== TRANSLATION_PLAYBACK_MODES.OFF;
      const sourceChunkPlan = browserSpeechChunksRef.current.length
        ? browserSpeechChunksRef.current
        : buildSpeechChunks(blocksRef.current, BROWSER_SPEECH_CHUNK_OPTIONS);
      const sourceChunkPosition = findSpeechChunkPosition(
        sourceChunkPlan,
        safeIndex,
        safeOffsetChars
      );

      if (shouldSpeakTranslatedText) {
        const translationWarmBlocks = sourceChunkPosition.chunk
          ? collectBlocksFromChunkWindow(
              blocksRef.current,
              sourceChunkPlan,
              Math.max(sourceChunkPosition.chunkIndex, 0),
              1 + TRANSLATED_BROWSER_SPEECH_AWAIT_LOOKAHEAD_COUNT
            )
          : [blocksRef.current[safeIndex]].filter(Boolean);

        // Do not block speech synthesis on remote translation warmup.
        // Delaying speak() here can break playback entirely because the
        // browser no longer treats it as part of the original play gesture.
        void warmTranslationCache(
          translationWarmBlocks,
          normalizedTranslationMode,
          {
            preferRemote: true,
            onRemoteUpdate: () => {
              setTranslationPlaybackVersion((currentValue) => currentValue + 1);
            }
          }
        );
      }
      const playbackBlocks = shouldSpeakTranslatedText
        ? buildPlaybackBlocks(blocksRef.current, normalizedTranslationMode)
        : blocksRef.current;
      const currentPlaybackBlock = playbackBlocks[safeIndex] || block;
      const translatedOffsetChars = shouldSpeakTranslatedText
        ? mapOffsetAcrossTexts(blockText, currentPlaybackBlock?.text || "", safeOffsetChars)
        : safeOffsetChars;
      const chunkPlan = shouldSpeakTranslatedText
        ? buildSpeechChunks(playbackBlocks, TRANSLATED_BROWSER_SPEECH_CHUNK_OPTIONS)
        : sourceChunkPlan;
      const chunkPosition = findSpeechChunkPosition(
        chunkPlan,
        safeIndex,
        translatedOffsetChars
      );
      const speechChunk = chunkPosition.chunk;

      if (!speechChunk) {
        setStatus(content.noBlocks);
        return;
      }

      const chunkStartChar = clampNumber(
        chunkPosition.startChar,
        0,
        Math.max(speechChunk.textLength - 1, 0)
      );
      const remainingText = speechChunk.text.slice(chunkStartChar);
      const utterance = new window.SpeechSynthesisUtterance(remainingText);
      const speechConfig = getCurrentBrowserSpeechConfig(speechChunk.language);
      const nextChunk = chunkPlan[chunkPosition.chunkIndex + 1] || null;
      const continuationDelayMs = getFallbackContinuationDelayMs(
        speechChunk.language,
        nextChunk?.language
      );
      const chunkEndCursor = mapSpeechChunkCharToCursor(
        speechChunk,
        Math.max(speechChunk.textLength - 1, 0)
      );
      const chunkEndOriginalOffset = shouldSpeakTranslatedText
        ? mapOffsetAcrossTexts(
            playbackBlocks[chunkEndCursor.blockIndex]?.text || "",
            blocksRef.current[chunkEndCursor.blockIndex]?.text || "",
            chunkEndCursor.offsetChars
          )
        : chunkEndCursor.offsetChars;
      const chunkStartElapsed = getElapsedForBlockOffset(
        playbackModel,
        blocksRef.current,
        safeIndex,
        safeOffsetChars
      );
      const chunkEndElapsed = getElapsedForBlockOffset(
        playbackModel,
        blocksRef.current,
        chunkEndCursor.blockIndex,
        chunkEndOriginalOffset
      );
      const estimatedRemainingSeconds = Math.max(
        chunkEndElapsed - chunkStartElapsed,
        getRemainingBlockDurationSeconds(
          playbackModel,
          blocksRef.current,
          safeIndex,
          safeOffsetChars
        )
      );
      const fallbackContinuationGuard = makeFallbackContinuationGuard(token, utterance);

      if (shouldSpeakTranslatedText && chunkPosition.chunkIndex >= 0) {
        const backgroundWarmBlocks = collectBlocksFromChunkWindow(
          blocksRef.current,
          sourceChunkPlan,
          Math.max(sourceChunkPosition.chunkIndex + 1, 0),
          TRANSLATED_BROWSER_SPEECH_BACKGROUND_LOOKAHEAD_COUNT
        );

        if (backgroundWarmBlocks.length) {
          void warmTranslationCache(backgroundWarmBlocks, normalizedTranslationMode, {
            preferRemote: true,
            onRemoteUpdate: () => {
              setTranslationPlaybackVersion((currentValue) => currentValue + 1);
            }
          });
        }
      }

      browserSpeechLookaheadRef.current = chunkPlan.slice(
        chunkPosition.chunkIndex + 1,
        chunkPosition.chunkIndex + 1 + BROWSER_SPEECH_LOOKAHEAD_COUNT
      );
      debugLog("speech-chunks-primed", {
        source: playbackSource,
        chunkIndex: chunkPosition.chunkIndex,
        lookAheadCount: browserSpeechLookaheadRef.current.length
      });

      const scheduleFallbackContinuationGuard = fallbackContinuationGuard.schedule;

      const finalizeFallbackSegment = (reason) => {
        if (!fallbackContinuationGuard.finalize(reason)) {
          return;
        }
        queuedOffsetRef.current = 0;
        debugLog("fallback-segment-complete", {
          reason,
          source: playbackSource,
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current,
          chunkIndex: chunkPosition.chunkIndex
        });

        if (nextChunk) {
          debugLog("speech-chunk-transition", {
            source: playbackSource,
            fromChunkIndex: chunkPosition.chunkIndex,
            toChunkIndex: nextChunk.chunkIndex
          });
          window.setTimeout(() => {
            speakFromIndexRef.current(nextChunk.startBlockIndex, {
              offsetChars: 0,
              forceBrowserFallback: true,
              source: `${playbackSource}-continue`
            });
          }, continuationDelayMs);
          return;
        }

        stopProgressTicker();
        setIsPlaying(false);
        setIsPaused(false);
        const totalSeconds = playbackModel.totalSeconds;
        updateProgressSnapshot(totalSeconds, totalSeconds);

        if (sleepModeRef.current === "chapter") {
          playbackIntentRef.current = false;
          cancelSleepTimer();
          setPlaybackPhaseState("ended", {
            source: `${playbackSource}-ended-sleep`,
            chapterIndex: currentChapterIndexRef.current,
            blockIndex: safeIndex
          });
          setStatus(content.statusSleepChapterTriggered);
          persistPlaybackSnapshotIfNeeded(
            "speech-ended-sleep",
            {
              phase: "ended",
              audioMode: "browser-fallback",
              pauseOrigin: "none",
              userPaused: false,
              shouldResume: false,
              backgroundInterrupted: false
            },
            { force: true }
          );
          return;
        }

        if (currentChapterIndexRef.current < chaptersRef.current.length - 1) {
          const nextChapterIndex = currentChapterIndexRef.current + 1;
          playbackIntentRef.current = true;
          pauseOriginRef.current = "none";
          setPlaybackPhaseState("transitioning", {
            source: `${playbackSource}-chapter-ended`,
            chapterIndex: nextChapterIndex,
            blockIndex: 0
          });
          persistPlaybackSnapshotIfNeeded(
            "speech-ended-transition",
            {
              phase: "transitioning",
              audioMode: "browser-fallback",
              pauseOrigin: "system",
              userPaused: false,
              shouldResume: true,
              chapterIndex: nextChapterIndex,
              blockIndex: 0,
              offsetChars: 0,
              elapsedSeconds: 0,
              totalSeconds: 0,
              backgroundInterrupted: false
            },
            { force: true }
          );
          jumpToChapter(nextChapterIndex, content.statusChapterChanged);
          return;
        }

        playbackIntentRef.current = false;
        setPlaybackPhaseState("ended", {
          source: `${playbackSource}-ended`,
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: safeIndex
        });
        setStatus(content.statusFinished);
        persistPlaybackSnapshotIfNeeded(
          "speech-ended-final",
          {
            phase: "ended",
            audioMode: "browser-fallback",
            pauseOrigin: "none",
            userPaused: false,
            shouldResume: false,
            backgroundInterrupted: false
          },
          { force: true }
        );
      };

      utterance.lang = speechConfig.utteranceLang || speechChunk.language || "en-US";
      utterance.rate = rateRef.current;
      if (speechConfig.voice) {
        utterance.voice = speechConfig.voice;
      }

      utterance.onboundary = (event) => {
        if (playTokenRef.current !== token) {
          return;
        }

        if (typeof event.charIndex === "number") {
          const cursor = mapSpeechChunkCharToCursor(
            speechChunk,
            chunkStartChar + event.charIndex
          );
          const originalOffsetChars = shouldSpeakTranslatedText
            ? mapOffsetAcrossTexts(
                playbackBlocks[cursor.blockIndex]?.text || "",
                blocksRef.current[cursor.blockIndex]?.text || "",
                cursor.offsetChars
              )
            : cursor.offsetChars;
          queuedOffsetRef.current = originalOffsetChars;
          updateReadingWindowPlaybackCursor(
            currentChapterIndexRef.current,
            cursor.blockIndex,
            originalOffsetChars
          );
          if (cursor.blockIndex !== currentIndexRef.current) {
            setCurrentIndex(cursor.blockIndex);
            currentIndexRef.current = cursor.blockIndex;
            debugLog("speech-chunk-cursor", {
              source: playbackSource,
              chapterIndex: currentChapterIndexRef.current,
              blockIndex: cursor.blockIndex,
              chunkIndex: chunkPosition.chunkIndex
            });
          }
          updateProgressSnapshot(
            getElapsedForBlockOffset(
              playbackModel,
              blocksRef.current,
              cursor.blockIndex,
              originalOffsetChars
            ),
            playbackModel.totalSeconds
          );
          persistPlaybackSnapshotIfNeeded("speech-boundary", {
            phase: "fallback",
            audioMode: "browser-fallback",
            pauseOrigin: "none",
            userPaused: false,
            shouldResume: true,
            chapterIndex: currentChapterIndexRef.current,
            blockIndex: cursor.blockIndex,
            offsetChars: originalOffsetChars,
            speechChunkIndex: chunkPosition.chunkIndex,
            backgroundInterrupted: false,
            visibleExcerptContext: buildVisibleExcerptContext(
              blocksRef.current,
              cursor.blockIndex
            )
          });
        }
      };

      utterance.onend = () => {
        debugLog("speech-end", {
          source: playbackSource,
          chapterIndex: currentChapterIndexRef.current,
          chunkIndex: chunkPosition.chunkIndex
        });
        finalizeFallbackSegment("onend");
      };

      utterance.onerror = () => {
        if (!fallbackContinuationGuard.finalize("error")) {
          return;
        }

        freezeProgressTracking();
        setIsPlaying(false);
        setIsPaused(false);
        setPlaybackPhaseState("idle", {
          source: `${playbackSource}-error`,
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: safeIndex
        });
        setStatus(content.statusResumeFailed || content.statusError);
        offerBrowserSpeechResume(`${playbackSource}-error`, buildPlaybackSnapshot({
          phase: "ready",
          audioMode: "browser-fallback",
          pauseOrigin: "system",
          userPaused: false,
          shouldResume: true,
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current,
          offsetChars: queuedOffsetRef.current,
          speechChunkIndex: chunkPosition.chunkIndex,
          backgroundInterrupted: true,
          visibleExcerptContext: buildVisibleExcerptContext(
            blocksRef.current,
            currentIndexRef.current
          )
        }));
      };

      if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
        window.speechSynthesis.cancel();
      }

      speechUtteranceRef.current = utterance;
      queuedOffsetRef.current = safeOffsetChars;
      setCurrentIndex(safeIndex);
      currentIndexRef.current = safeIndex;
      clearBrowserSpeechResumeCard();
      browserSpeechWasHiddenRef.current = false;
      beginProgressTracking(safeIndex, safeOffsetChars);
      setIsPlaying(true);
      setIsPaused(false);
      setPlaybackPhaseState("fallback", {
        source: playbackSource,
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: safeIndex
      });
      setStatus(`${content.currentParagraph}: ${safeIndex + 1}`);
      persistPlaybackSnapshotIfNeeded(
        "speech-start",
        {
          phase: "fallback",
          audioMode: "browser-fallback",
          pauseOrigin: "none",
          userPaused: false,
          shouldResume: true,
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: safeIndex,
          offsetChars: safeOffsetChars,
          speechChunkIndex: chunkPosition.chunkIndex,
          backgroundInterrupted: false,
          visibleExcerptContext: buildVisibleExcerptContext(
            blocksRef.current,
            safeIndex
          )
        },
        { force: true }
      );
      debugLog("speech-start", {
        source: playbackSource,
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: safeIndex,
        offsetChars: safeOffsetChars,
        chunkIndex: chunkPosition.chunkIndex,
        chunkCount: chunkPlan.length,
        utteranceLang: utterance.lang,
        voiceName: speechConfig.voice?.name || ""
      });

      if (!remainingText.trim()) {
        finalizeFallbackSegment("empty-chunk");
        return;
      }

      clearFallbackContinuationTimeout();
      window.speechSynthesis.speak(utterance);

      scheduleFallbackContinuationGuard(
        Math.max(estimatedRemainingSeconds * 1000 + continuationDelayMs + 1200, 1800)
      );
      return;
    }

    (async () => {
      try {
        await startAiChapterPlayback({
          chapterIndex: currentChapterIndex,
          blockIndex: index,
          offsetChars: safeOffsetChars,
          token,
          source: playbackSource
        });
      } catch (error) {
        if (playTokenRef.current !== token) {
          return;
        }

        if (supportsSpeechFallback && canUseBrowserSpeechFallback(error)) {
          suppressAiPlaybackForSession(error, playbackSource);
          debugLog("ai-playback-fallback", {
            source: playbackSource,
            chapterIndex: currentChapterIndexRef.current,
            blockIndex: index,
            message: error instanceof Error ? error.message : "Unknown AI playback error."
          });
          setStatus(content.statusAiFallback);
          speakFromIndexRef.current(index, {
            offsetChars: safeOffsetChars,
            forceBrowserFallback: true,
            source: `${playbackSource}-fallback`
          });
          return;
        }

        freezeProgressTracking();
        setIsPlaying(false);
        setIsPaused(false);
        setPlaybackPhaseState("idle", {
          source: `${playbackSource}-error`,
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        setStatus(
          error instanceof Error && error.message
            ? error.message
            : content.statusError
        );
      }
    })();
  }

  speakFromIndexRef.current = speakFromIndex;

  function previewVoice(language) {
    if (!aiAudioAvailable && !supportsSpeechFallback) {
      setStatus(content.statusNoSpeech);
      return;
    }

    const voice = getPreferredVoiceForLanguage(language, {
      english: englishVoices,
      french: frenchVoices,
      chinese: chineseVoices,
      selectedEnglishVoiceURI,
      selectedFrenchVoiceURI,
      selectedChineseVoiceURI
    });

    if (!voice) {
      setStatus(content.noVoiceAvailable);
      return;
    }

    stopPlayback();
    queuedOffsetRef.current = 0;
    syncProgressToCursor(currentIndexRef.current, 0);

    setStatus(
      isChineseLanguage(language)
        ? content.statusPreviewingChinese
        : isFrenchLanguage(language)
        ? content.statusPreviewingFrench
        : content.statusPreviewingEnglish
    );

    if (!aiAudioAvailable) {
      const previewUtterance = new window.SpeechSynthesisUtterance(
        isChineseLanguage(language)
          ? content.previewChineseSample
          : isFrenchLanguage(language)
          ? content.previewFrenchSample
          : content.previewEnglishSample
      );
      const speechConfig = getCurrentBrowserSpeechConfig(language);

      previewUtterance.lang = speechConfig.utteranceLang || language;
      previewUtterance.rate = rateRef.current;
      if (speechConfig.voice) {
        previewUtterance.voice = speechConfig.voice;
      }
      previewUtterance.onerror = () => {
        setStatus(content.statusError);
      };
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(previewUtterance);
      return;
    }

    setIsGeneratingAudio(true);

    requestChapterAudio({
      segments: [
        {
          id: "preview-segment",
          language,
          text: isChineseLanguage(language)
            ? content.previewChineseSample
            : isFrenchLanguage(language)
            ? content.previewFrenchSample
            : content.previewEnglishSample
        }
      ],
      selectedEnglishVoiceURI,
      selectedFrenchVoiceURI,
      selectedChineseVoiceURI,
      personalApiKey
    })
      .then((response) => {
        if (previewAudioRef.current) {
          previewAudioRef.current.pause();
          window.URL.revokeObjectURL(previewAudioRef.current.src);
          previewAudioRef.current = null;
        }

        const previewUrl = window.URL.createObjectURL(
          new Blob([response.audioBuffer], { type: response.mimeType || "audio/wav" })
        );
        const previewAudio = new window.Audio(previewUrl);
        previewAudio.playbackRate = rateRef.current;
        previewAudio.onended = () => {
          window.URL.revokeObjectURL(previewUrl);
          if (previewAudioRef.current === previewAudio) {
            previewAudioRef.current = null;
          }
        };
        previewAudio.onerror = () => {
          window.URL.revokeObjectURL(previewUrl);
          if (previewAudioRef.current === previewAudio) {
            previewAudioRef.current = null;
          }
          setStatus(content.statusError);
        };
        previewAudioRef.current = previewAudio;
        clearAiPlaybackSuppression("preview-ai-success");
        return previewAudio.play();
      })
      .catch((error) => {
        if (supportsSpeechFallback) {
          suppressAiPlaybackForSession(error, "preview-ai-fallback");
          const previewUtterance = new window.SpeechSynthesisUtterance(
            isChineseLanguage(language)
              ? content.previewChineseSample
              : isFrenchLanguage(language)
              ? content.previewFrenchSample
              : content.previewEnglishSample
          );
          const speechConfig = getCurrentBrowserSpeechConfig(language);

          previewUtterance.lang = speechConfig.utteranceLang || language;
          previewUtterance.rate = rateRef.current;
          if (speechConfig.voice) {
            previewUtterance.voice = speechConfig.voice;
          }
          previewUtterance.onerror = () => {
            setStatus(content.statusError);
          };
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(previewUtterance);
          setStatus(content.statusAiFallback);
          return;
        }

        setStatus(
          error instanceof Error && error.message
            ? error.message
            : content.statusError
        );
      })
      .finally(() => {
        setIsGeneratingAudio(false);
      });
  }

  function handleLoadDemo() {
    clearBrowserSpeechResumeCard();
    stopPlayback();
    clearGeneratedChapterCache();
    restorePlaybackSnapshotRef.current = null;
    autoSaveEnabledRef.current = true;
    queuedOffsetRef.current = 0;
    const nextBookId = createBookId("reader-lab-demo");
    setActiveBookId(nextBookId);
    setBookTitle(content.workspaceTitle);
    setBookCoverImage(createGeneratedCoverDataUrl(content.workspaceTitle));
    setChapters([
      {
        id: "chapter-0",
        title: content.demoChapterTitle,
        text: content.demoText
      }
    ]);
    setCurrentChapterIndex(0);
    setCurrentIndex(0);
    currentIndexRef.current = 0;
    setLastImportedFileName("");
    setLastImportedFileType(content.fileTypeTxt);
    setStatus(content.statusLoadedDemo);
  }

  function importExternalSelection(payload = {}) {
    const normalizedText = normalizeExternalSelectionText(
      typeof payload === "string" ? payload : payload.text
    );

    if (!normalizedText) {
      setStatus(content.statusUnsupported);
      return false;
    }

    clearBrowserSpeechResumeCard();
    stopPlayback();
    clearGeneratedChapterCache();
    restorePlaybackSnapshotRef.current = null;
    autoSaveEnabledRef.current = true;
    queuedOffsetRef.current = 0;

    const rawSourceTitle =
      typeof payload === "object" && typeof payload.sourceTitle === "string"
        ? payload.sourceTitle.trim()
        : "";
    const rawSourceUrl =
      typeof payload === "object" && typeof payload.sourceUrl === "string"
        ? payload.sourceUrl.trim()
        : "";
    const nextBookTitle = rawSourceTitle || content.importedTextTitle;
    const nextBookId = createBookId(rawSourceTitle || "echo-selection");

    setActiveBookId(nextBookId);
    setBookTitle(nextBookTitle);
    setBookCoverImage(
      createGeneratedFileCoverDataUrl(rawSourceTitle || rawSourceUrl || "selection", nextBookTitle)
    );
    setChapters([
      {
        id: "chapter-0",
        title: nextBookTitle,
        text: normalizedText
      }
    ]);
    setCurrentChapterIndex(0);
    setCurrentIndex(0);
    currentIndexRef.current = 0;
    setLastImportedFileName(rawSourceTitle || rawSourceUrl || "Selected text");
    setLastImportedFileType(content.fileTypeTxt);
    pendingAutoPlayRef.current = true;
    setStatus(content.statusAutoStarted);

    debugLog("external-selection-imported", {
      title: nextBookTitle,
      characters: normalizedText.length
    });

    return true;
  }

  function handleClear() {
    clearBrowserSpeechResumeCard();
    stopPlayback();
    clearGeneratedChapterCache();
    restorePlaybackSnapshotRef.current = null;
    autoSaveEnabledRef.current = false;
    queuedOffsetRef.current = 0;
    setActiveBookId("");
    setBookTitle(content.workspaceTitle);
    setBookCoverImage("");
    setChapters([
      {
        id: "chapter-0",
        title: content.importedTextTitle,
        text: ""
      }
    ]);
    setCurrentChapterIndex(0);
    setCurrentIndex(0);
    currentIndexRef.current = 0;
    setReaderTextValue("");
    setLastImportedFileName("");
    setLastImportedFileType("");
    setStatus(content.statusCleared);
  }

  async function importFile(file) {
    if (!file) {
      return;
    }

    if (!hasAcceptedUserDeclaration) {
      setStatus("Please accept the declaration before uploading.");
      return;
    }

    clearBrowserSpeechResumeCard();

    const fileName = file.name || "";
    const normalizedName = fileName.toLowerCase();
    const isEpub = normalizedName.endsWith(".epub") || file.type === "application/epub+zip";
    const documentImportKind = getDocumentImportKind(fileName, file.type);
    const isImportedDocument = Boolean(documentImportKind);
    const isTextLike =
      isEpub ||
      normalizedName.endsWith(".txt") ||
      normalizedName.endsWith(".text") ||
      normalizedName.endsWith(".md") ||
      normalizedName.endsWith(".log") ||
      file.type.startsWith("text/");

    if (!isTextLike && !isImportedDocument) {
      setStatus(content.statusUnsupported);
      return;
    }

    try {
      stopPlayback();
      clearGeneratedChapterCache();
      restorePlaybackSnapshotRef.current = null;
      let importedText = "";
      let nextBookTitle = content.importedTextTitle;
      let nextChapters = [];
      let nextFileType = content.fileTypeTxt;

      if (isEpub) {
        setStatus(content.statusExtractingEpub);
        const epubBook = await extractEpubBook(file, content);
        nextBookTitle = epubBook.title;
        nextChapters = epubBook.chapters;
        nextFileType = content.fileTypeEpub;
        importedText = epubBook.chapters[0]?.text || "";
        setBookCoverImage(
          sanitizeCoverImage(epubBook.coverImage) || createGeneratedFileCoverDataUrl(fileName, epubBook.title)
        );
      } else if (isImportedDocument) {
        setStatus(content.statusExtractingDocument);
        const importedDocument = await importEchoDocument(file);
        importedText = importedDocument.text;
        nextBookTitle =
          importedDocument.title ||
          fileNameWithoutExtension(fileName) ||
          content.importedTextTitle;
        nextChapters = buildImportedDocumentChapters(
          importedText,
          content.chapterLabel,
          nextBookTitle
        );
        nextFileType =
          importedDocument.fileType ||
          (documentImportKind === "mobi"
            ? content.fileTypeMobi
            : documentImportKind === "docx"
            ? content.fileTypeDocx
            : documentImportKind === "doc"
            ? content.fileTypeDoc
            : content.fileTypePdf);
        setBookCoverImage(createGeneratedFileCoverDataUrl(fileName, nextBookTitle));
      } else {
        importedText = await decodeUploadedText(file);
        nextBookTitle = fileNameWithoutExtension(fileName) || content.importedTextTitle;
        nextChapters = [
          {
            id: "chapter-0",
            title: nextBookTitle,
            text: importedText
          }
        ];
        setBookCoverImage(createGeneratedFileCoverDataUrl(fileName, nextBookTitle));
      }

      if (!importedText.trim()) {
        throw new Error("Imported file was empty.");
      }

      autoSaveEnabledRef.current = true;
      setActiveBookId(
        findExistingBookId(nextBookTitle, fileName, nextFileType) ||
        createBookId(fileNameWithoutExtension(fileName) || nextBookTitle)
      );
      setBookTitle(nextBookTitle);
      chaptersRef.current = nextChapters;
      currentChapterIndexRef.current = 0;
      blocksRef.current = splitIntoBlocks(nextChapters[0]?.text || "");
      chapterTimingRef.current = {
        key: "",
        durations: [],
        totalSeconds: 0
      };
      setChapters(nextChapters);
      setCurrentChapterIndex(0);
      setCurrentIndex(0);
      currentIndexRef.current = 0;
      queuedOffsetRef.current = 0;
      setLastImportedFileName(fileName);
      setLastImportedFileType(nextFileType);
      pendingAutoPlayRef.current = true;
      setStatus(getImportedStatusMessage(nextFileType, content));
    } catch (error) {
      pendingAutoPlayRef.current = false;
      setStatus(getImportErrorStatus(error, content));
    }
  }

  async function handleConvertCurrentDocument() {
    const sourceFormat = getFileTypeLabel(conversionSourceFormat, content);
    const targetFormat = getFileTypeLabel(conversionTargetFormat, content);
    const currentFormat = getFileTypeLabel(currentConversionSourceFormat, content);

    if (!lastImportedFileName || !currentFormat) {
      setStatus(content.statusConvertNeedFile);
      return;
    }

    if (sourceFormat !== currentFormat) {
      setStatus(
        fillTemplate(content.statusConvertMismatch, {
          current: currentFormat,
          expected: sourceFormat
        })
      );
      return;
    }

    if (sourceFormat === targetFormat) {
      setStatus(content.statusConvertSameFormat);
      return;
    }

    try {
      setIsConvertingDocument(true);
      setStatus(content.statusConverting);

      const convertedDocument = await convertEchoDocument({
        targetFormat,
        title: bookTitle || content.importedTextTitle,
        fileName: lastImportedFileName,
        chapters: chaptersRef.current,
        chapterLabel: content.chapterLabel
      });
      const saveResult = await saveConvertedEchoDocument(
        convertedDocument.blob,
        convertedDocument.fileName
      );

      if (saveResult?.cancelled) {
        setStatus(content.statusConvertCancelled);
        return;
      }

      setStatus(fillTemplate(content.statusConverted, { format: targetFormat }));
    } catch (error) {
      setStatus(
        error instanceof Error && error.message
          ? error.message
          : content.statusConvertFailed
      );
    } finally {
      setIsConvertingDocument(false);
    }
  }

  async function handleExportMp3({ targetLanguage = null } = {}) {
    if (!chapters.length || isExportingMp3) {
      return;
    }

    const shouldTranslate = Boolean(targetLanguage);
    const audioParts = [];
    let translationFallbackCount = 0;

    try {
      setIsExportingMp3(true);
      setExportProgress({ done: 0, total: chapters.length });
      setStatus(
        shouldTranslate
          ? `Preparing MP3 export in ${getExportLanguageLabel(targetLanguage)}…`
          : content.statusExportingMp3
      );

      for (let chapterIndex = 0; chapterIndex < chapters.length; chapterIndex += 1) {
        const chapter = chapters[chapterIndex];
        const chapterBlocks = splitIntoBlocks(chapter?.text || "").filter(Boolean);

        for (const block of chapterBlocks) {
          let textToSpeak = block.text;
          let languageToSpeak = block.language || "en-US";

          if (shouldTranslate) {
            try {
              textToSpeak = await exportTranslateBlock(block.text, targetLanguage, {
                sourceLanguage: block.language || "en-US"
              });
              languageToSpeak = targetLanguage;
            } catch (error) {
              translationFallbackCount += 1;
              textToSpeak = block.text;
              languageToSpeak = block.language || "en-US";
            }
          }

          const response = await exportChapterAudio({
            text: textToSpeak,
            language: languageToSpeak,
            chapterTitle:
              getDisplayChapterTitle(chapter, chapterIndex, {
                chapterLabel: content.chapterLabel,
                bookTitle,
                fileName: lastImportedFileName,
                workspaceTitle: content.workspaceTitle
              }) || content.importedTextTitle
          });

          audioParts.push(new Uint8Array(response.audioBuffer));
        }

        setExportProgress({ done: chapterIndex + 1, total: chapters.length });
        setStatus(
          `Exporting… ${chapterIndex + 1} / ${chapters.length} ${content.chapterLabel.toLowerCase()}s`
        );
      }

      const totalLength = audioParts.reduce((sum, part) => sum + part.byteLength, 0);
      const mergedAudio = new Uint8Array(totalLength);
      let writeOffset = 0;

      audioParts.forEach((part) => {
        mergedAudio.set(part, writeOffset);
        writeOffset += part.byteLength;
      });

      const langSuffix = targetLanguage ? `-${targetLanguage}` : "";
      const exportBlob = new Blob([mergedAudio], { type: "audio/mpeg" });
      const exportFileName = `${bookTitle || "pagetone"}${langSuffix}.mp3`;
      const saveResult = await saveConvertedEchoDocument(exportBlob, exportFileName);

      if (saveResult?.cancelled) {
        setStatus(content.statusConvertCancelled);
        return;
      }

      if (shouldTranslate && translationFallbackCount > 0) {
        setStatus(
          `${fillTemplate(content.statusConverted, { format: "MP3" })} ${translationFallbackCount} block${translationFallbackCount === 1 ? "" : "s"} stayed in the original language.`
        );
        return;
      }

      setStatus(fillTemplate(content.statusConverted, { format: "MP3" }));
    } catch (error) {
      setStatus(
        error instanceof Error && error.message
          ? error.message
          : content.statusExportMp3Failed
      );
    } finally {
      setIsExportingMp3(false);
      setExportProgress({ done: 0, total: 0 });
    }
  }

  async function handleExportChineseEpub() {
    if (!chapters.length || isExportingChineseEpub || isConvertingDocument) {
      return;
    }

    try {
      setIsExportingChineseEpub(true);
      setChineseEpubProgress({ done: 0, total: chapters.length });
      setStatus(content.statusExportingChineseEpub);

      const translatedChapters = [];

      for (let chapterIndex = 0; chapterIndex < chapters.length; chapterIndex += 1) {
        const chapter = chapters[chapterIndex];
        const chapterBlocks = splitIntoBlocks(chapter?.text || "").filter(Boolean);
        const translatedBlocks = [];
        let translatedTitle =
          String(chapter?.title || "").trim() || `${content.chapterLabel} ${chapterIndex + 1}`;

        if (chapter?.title) {
          try {
            translatedTitle = await exportTranslateBlock(chapter.title, "zh-CN", {
              sourceLanguage: detectLanguage(chapter.title)
            });
          } catch (error) {
            // Keep the original title if the export translation cannot cleanly translate it.
          }
        }

        for (const block of chapterBlocks) {
          try {
            const translatedBlock = await exportTranslateBlock(block.text, "zh-CN", {
              sourceLanguage: block.language || "en-US"
            });
            translatedBlocks.push(translatedBlock);
          } catch (error) {
            translatedBlocks.push(block.text);
          }
        }

        translatedChapters.push({
          id: chapter?.id || `chapter-${chapterIndex}`,
          title: translatedTitle,
          text: translatedBlocks.join("\n\n").trim()
        });

        setChineseEpubProgress({ done: chapterIndex + 1, total: chapters.length });
        setStatus(
          fillTemplate(content.statusExportingChineseEpubProgress, {
            done: chapterIndex + 1,
            total: chapters.length
          })
        );
      }

      const convertedDocument = await convertEchoDocument({
        targetFormat: content.fileTypeEpub,
        title: `${bookTitle || content.importedTextTitle} 中文版`,
        fileName: `${lastImportedFileName || bookTitle || "pagetone"}-zh-CN`,
        chapters: translatedChapters,
        chapterLabel: content.chapterLabel
      });

      const fileName = convertedDocument.fileName.replace(/\.epub$/i, "-zh-CN.epub");
      const saveResult = await saveConvertedEchoDocument(convertedDocument.blob, fileName);

      if (saveResult?.cancelled) {
        setStatus(content.statusConvertCancelled);
        return;
      }

      setStatus(fillTemplate(content.statusConverted, { format: "EPUB" }));
    } catch (error) {
      setStatus(
        error instanceof Error && error.message
          ? error.message
          : content.statusConvertFailed
      );
    } finally {
      setIsExportingChineseEpub(false);
      setChineseEpubProgress({ done: 0, total: 0 });
    }
  }

  async function handleExportOriginalEpub() {
    if (
      !chapters.length ||
      isExportingPlainEpub ||
      isConvertingDocument ||
      isExportingMp3 ||
      isExportingChineseEpub
    ) {
      return;
    }

    try {
      setIsExportingPlainEpub(true);
      setPlainEpubProgress({ done: 0, total: chapters.length });
      setStatus(content.statusExportingOriginalEpub);

      const exportChapters = [];

      for (let chapterIndex = 0; chapterIndex < chapters.length; chapterIndex += 1) {
        const chapter = chapters[chapterIndex];

        exportChapters.push({
          id: chapter?.id || `chapter-${chapterIndex}`,
          title:
            String(chapter?.title || "").trim() || `${content.chapterLabel} ${chapterIndex + 1}`,
          text: String(chapter?.text || "").trim()
        });

        setPlainEpubProgress({ done: chapterIndex + 1, total: chapters.length });
        setStatus(
          fillTemplate(content.statusExportingOriginalEpubProgress, {
            done: chapterIndex + 1,
            total: chapters.length
          })
        );
      }

      const convertedDocument = await convertEchoDocument({
        targetFormat: content.fileTypeEpub,
        title: bookTitle || content.importedTextTitle,
        fileName: lastImportedFileName || bookTitle || "pagetone",
        chapters: exportChapters,
        chapterLabel: content.chapterLabel
      });

      const saveResult = await saveConvertedEchoDocument(
        convertedDocument.blob,
        convertedDocument.fileName
      );

      if (saveResult?.cancelled) {
        setStatus(content.statusConvertCancelled);
        return;
      }

      setStatus(fillTemplate(content.statusConverted, { format: "EPUB" }));
    } catch (error) {
      setStatus(
        error instanceof Error && error.message
          ? error.message
          : content.statusConvertFailed
      );
    } finally {
      setIsExportingPlainEpub(false);
      setPlainEpubProgress({ done: 0, total: 0 });
    }
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];

    if (file) {
      await importFile(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function handlePlayToggle() {
    clearBrowserSpeechResumeCard();
    const audio = audioRef.current;
    const hasLoadedAudio = hasCurrentChapterLoadedAudio();
    const shouldRetryAiPlayback = personalAiAudioEnabled && aiPlaybackSuppressed && !hasLoadedAudio;

    if (shouldRetryAiPlayback) {
      clearAiPlaybackSuppression("manual-play-retry");
    }

    const aiPlaybackReady = audioGenerationReady && !browserFallbackForcedRef.current;
    const useBrowserFallback = !aiPlaybackReady && supportsSpeechFallback && !hasLoadedAudio;

    if (audio) {
      audio.playbackRate = rateRef.current;
    }

    if (!supportsAudio && !useBrowserFallback) {
      setStatus(content.statusNoSpeech);
      return;
    }

    if (!aiPlaybackReady && !hasLoadedAudio && !useBrowserFallback) {
      setStatus(content.statusNoSpeech);
      return;
    }

    if (!isPlaying) {
      playbackIntentRef.current = true;
      pauseOriginRef.current = "none";

      if (hasLoadedAudio) {
        setPlaybackPhaseState("loading", {
          source: "toggle-resume-loaded",
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        const playPromise = audio?.play();

        if (playPromise && typeof playPromise.then === "function") {
          playPromise
            .then(() => {
              recoveryAttemptRef.current = 0;
              shouldRecoverPlaybackRef.current = false;
              pauseOriginRef.current = "none";
              resumeProgressTracking();
              setIsPlaying(true);
              setIsPaused(false);
              setPlaybackPhaseState("playing", {
                source: "toggle-resume-loaded",
                chapterIndex: currentChapterIndexRef.current,
                blockIndex: currentIndexRef.current
              });
              setStatus(content.statusResumed);
              requestWakeLock();
            })
            .catch((error) => {
              setPlaybackPhaseState("pausedBySystem", {
                source: "toggle-resume-loaded-error",
                chapterIndex: currentChapterIndexRef.current,
                blockIndex: currentIndexRef.current
              });
              setStatus(
                error instanceof Error && error.message
                  ? error.message
                  : content.statusError
              );
            });
        } else {
          recoveryAttemptRef.current = 0;
          shouldRecoverPlaybackRef.current = false;
          pauseOriginRef.current = "none";
          resumeProgressTracking();
          setIsPlaying(true);
          setIsPaused(false);
          setPlaybackPhaseState("playing", {
            source: "toggle-resume-loaded",
            chapterIndex: currentChapterIndexRef.current,
            blockIndex: currentIndexRef.current
          });
          setStatus(content.statusResumed);
          requestWakeLock();
        }
        return;
      }

      speakFromIndex(currentIndexRef.current, { offsetChars: queuedOffsetRef.current });
      return;
    }

    if (isPaused) {
      playbackIntentRef.current = true;
      pauseOriginRef.current = "none";

      if (useBrowserFallback) {
        window.speechSynthesis.resume();
        recoveryAttemptRef.current = 0;
        shouldRecoverPlaybackRef.current = false;
        resumeProgressTracking();
        setIsPlaying(true);
        setIsPaused(false);
        setPlaybackPhaseState("fallback", {
          source: "toggle-resume-fallback",
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        setStatus(content.statusResumed);
        return;
      }

      setPlaybackPhaseState("loading", {
        source: "toggle-resume-audio",
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current
      });
      const playPromise = audio?.play();

      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            recoveryAttemptRef.current = 0;
            shouldRecoverPlaybackRef.current = false;
            pauseOriginRef.current = "none";
            resumeProgressTracking();
            setIsPlaying(true);
            setIsPaused(false);
            setPlaybackPhaseState("playing", {
              source: "toggle-resume-audio",
              chapterIndex: currentChapterIndexRef.current,
              blockIndex: currentIndexRef.current
            });
            setStatus(content.statusResumed);
            requestWakeLock();
          })
          .catch((error) => {
            setPlaybackPhaseState("pausedBySystem", {
              source: "toggle-resume-audio-error",
              chapterIndex: currentChapterIndexRef.current,
              blockIndex: currentIndexRef.current
            });
            setStatus(
              error instanceof Error && error.message
                  ? error.message
                : content.statusError
            );
          });
      } else {
        recoveryAttemptRef.current = 0;
        shouldRecoverPlaybackRef.current = false;
        pauseOriginRef.current = "none";
        resumeProgressTracking();
        setIsPlaying(true);
        setIsPaused(false);
        setPlaybackPhaseState("playing", {
          source: "toggle-resume-audio",
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        setStatus(content.statusResumed);
        requestWakeLock();
      }
      return;
    }

    if (useBrowserFallback) {
      playbackIntentRef.current = false;
      pauseOriginRef.current = "user";
      window.speechSynthesis.pause();
      freezeProgressTracking();
      setIsPaused(true);
      setPlaybackPhaseState("pausedByUser", {
        source: "toggle-pause-fallback",
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current
      });
      setStatus(content.statusPaused);
      persistReaderState({
        playbackSnapshot: {
          phase: "pausedByUser",
          audioMode: "browser-fallback",
          pauseOrigin: "user",
          userPaused: true,
          shouldResume: false
        }
      });
      return;
    }

    playbackIntentRef.current = false;
    pauseOriginRef.current = "user";
    runWithAudioPauseGuard(() => {
      audio?.pause();
    });
    freezeProgressTracking();
    setIsPaused(true);
    setPlaybackPhaseState("pausedByUser", {
      source: "toggle-pause-audio",
      chapterIndex: currentChapterIndexRef.current,
      blockIndex: currentIndexRef.current
    });
    setStatus(content.statusPaused);
    releaseWakeLock();
    persistReaderState({
      playbackSnapshot: {
        phase: "pausedByUser",
        audioMode: "ai-audio",
        pauseOrigin: "user",
        userPaused: true,
        shouldResume: false,
        audioCacheKey: String(audio?.dataset.cacheKey || ""),
        audioStreamUrl: getPersistableAudioStreamUrl(
          audio,
          String(audio?.dataset.cacheKey || "")
        )
      }
    });
  }

  handlePlayToggleRef.current = handlePlayToggle;
  seekToPercentRef.current = (nextPercent) => {
    seekToProgressRatio(Number(nextPercent) / 100);
  };

  function jumpToBlock(index, nextStatus) {
    clearBrowserSpeechResumeCard();
    if (!blocks.length) {
      setStatus(content.noBlocks);
      return;
    }

    const nextIndex = clampIndex(index, blocks.length);
    const shouldResumePlayback = playbackIntentRef.current;

    stopPlayback({ preserveIntent: shouldResumePlayback, source: "jump-to-block" });
    setCurrentIndex(nextIndex);
    currentIndexRef.current = nextIndex;
    queuedOffsetRef.current = 0;
    moveLoadedAudioToElapsed(
      getElapsedForBlockOffset(getCurrentPlaybackModel(), blocksRef.current, nextIndex, 0)
    );
    syncProgressToCursor(nextIndex, 0);
    setStatus(nextStatus);

    if (shouldResumePlayback && (aiAudioAvailable || supportsSpeechFallback)) {
      window.setTimeout(() => speakFromIndexRef.current(nextIndex, { offsetChars: 0 }), 50);
    }
  }

  function handleSelectBlock(index) {
    jumpToBlock(index, content.statusQueueJump);
  }

  function handleTextChange(event) {
    stopPlayback();
    clearGeneratedChapterCache();
    const nextValue = event.target.value;
    const hasManualTextWorkspace = !lastImportedFileName && !activeBookId;
    const nextTitle = content.importedTextTitle;
    setReaderTextValue(nextValue);
    queuedOffsetRef.current = 0;

    if (hasManualTextWorkspace) {
      const nextChapters = [
        {
          id: "chapter-0",
          title: nextTitle,
          text: nextValue
        }
      ];
      const nextBlocks = splitIntoBlocks(nextValue);
      autoSaveEnabledRef.current = Boolean(nextValue.trim());
      setBookTitle(nextTitle);
      setBookCoverImage((previousValue) =>
        previousValue || createGeneratedFileCoverDataUrl("", nextTitle)
      );
      setLastImportedFileType(nextValue.trim() ? content.fileTypeTxt : "");
      setCurrentChapterIndex(0);
      currentChapterIndexRef.current = 0;
      chaptersRef.current = nextChapters;
      blocksRef.current = nextBlocks;
      setChapters(nextChapters);
    } else {
      setChapters((prev) => {
        const nextChapters = prev.map((chapter, index) =>
          index === currentChapterIndex
            ? { ...chapter, text: nextValue }
            : chapter
        );
        chaptersRef.current = nextChapters;
        blocksRef.current = splitIntoBlocks(nextChapters[currentChapterIndex]?.text || "");
        return nextChapters;
      });
    }

    setCurrentIndex(0);
    currentIndexRef.current = 0;
    chapterTimingRef.current = {
      key: "",
      durations: [],
      totalSeconds: 0
    };
    setStatus(nextValue.trim() ? content.statusReady : content.noBlocks);
  }

  function applyChapterSelection(nextChapterIndex, nextStatus, options = {}) {
    clearBrowserSpeechResumeCard();
    const shouldResumePlayback = Boolean(options.shouldResumePlayback);
    const nextBlocks = splitIntoBlocks(chaptersRef.current[nextChapterIndex]?.text || "");
    const nextAudioKey = getAudioCacheKeyForChapter(nextChapterIndex);
    const cachedTiming = chapterAudioCacheRef.current.get(nextAudioKey);

    stopPlayback({ preserveIntent: shouldResumePlayback, source: "jump-to-chapter" });
    currentChapterIndexRef.current = nextChapterIndex;
    blocksRef.current = nextBlocks;
    setCurrentChapterIndex(nextChapterIndex);
    setCurrentIndex(0);
    currentIndexRef.current = 0;
    queuedOffsetRef.current = 0;
    chapterTimingRef.current = cachedTiming || {
      key: nextAudioKey,
      durations: [],
      totalSeconds: 0
    };
    if (audioRef.current) {
      audioRef.current.playbackRate = rateRef.current;
    }
    syncProgressToCursor(0, 0);
    pendingAutoPlayRef.current = shouldResumePlayback;
    playbackIntentRef.current = shouldResumePlayback;
    setStatus(nextStatus);
  }

  function jumpToChapter(nextChapterIndex, nextStatus) {
    const shouldResumePlayback = playbackIntentRef.current && !isPausedRef.current;
    const canUseDirectAiTransition = shouldResumePlayback && aiAudioAvailable && supportsAudio;

    if (canUseDirectAiTransition) {
      debugLog("chapter-transition-start", {
        source: "manual-chapter-jump",
        chapterIndex: nextChapterIndex
      });
      setStatus(content.statusBuffering);
      setPlaybackPhaseState("transitioning", {
        source: "manual-chapter-jump",
        chapterIndex: nextChapterIndex,
        blockIndex: 0
      });
      persistPlaybackSnapshotIfNeeded(
        "manual-chapter-jump",
        {
          phase: "transitioning",
          audioMode: "ai-audio",
          pauseOrigin: "system",
          userPaused: false,
          shouldResume: true,
          chapterIndex: nextChapterIndex,
          blockIndex: 0,
          offsetChars: 0,
          elapsedSeconds: 0,
          totalSeconds: 0
        },
        { force: true }
      );
      transitionToChapterPlayback(nextChapterIndex, "manual-chapter-jump").catch(() => {
        applyChapterSelection(nextChapterIndex, nextStatus, {
          shouldResumePlayback: true
        });
      });
      return;
    }

    applyChapterSelection(nextChapterIndex, nextStatus, {
      shouldResumePlayback
    });
  }

  function handlePreviousChapter() {
    if (!hasPreviousChapter) {
      setStatus(content.statusNoPreviousChapter);
      return;
    }

    jumpToChapter(currentChapterIndex - 1, content.statusMovedPreviousChapter);
  }

  function handleNextChapter() {
    if (!hasNextChapter) {
      setStatus(content.statusNoNextChapter);
      return;
    }

    jumpToChapter(currentChapterIndex + 1, content.statusChapterChanged);
  }

  previousChapterActionRef.current = handlePreviousChapter;
  nextChapterActionRef.current = handleNextChapter;

  function handleOpenSavedBook(bookId, options = {}) {
    clearBrowserSpeechResumeCard();
    const book = savedBooksRef.current.find((item) => item.id === bookId);

    if (!book) {
      return;
    }

    stopPlayback();
    clearGeneratedChapterCache();
    restorePlaybackSnapshotRef.current = null;
    autoSaveEnabledRef.current = true;

    const nextChapterIndex = clampIndex(book.currentChapterIndex, book.chapters.length);
    const nextBlocks = splitIntoBlocks(book.chapters[nextChapterIndex]?.text || "");
    const nextBlockIndex = clampIndex(book.currentIndex, nextBlocks.length);
    const shouldAutoplay = Boolean(options.shouldAutoplay);
    const persistedVoicePreferences = getPersistedVoicePreferences();
    const nextVoicePreferences = mergeVoicePreferences(book, persistedVoicePreferences);

    setActiveBookId(book.id);
    setBookTitle(book.bookTitle);
    setBookCoverImage(
      book.fileType === content.fileTypeEpub
        ? book.coverImage || createGeneratedFileCoverDataUrl(book.lastImportedFileName, book.bookTitle)
        : createGeneratedFileCoverDataUrl(book.lastImportedFileName, book.bookTitle)
    );
    setChapters(book.chapters);
    setCurrentChapterIndex(nextChapterIndex);
    currentChapterIndexRef.current = nextChapterIndex;
    setCurrentIndex(nextBlockIndex);
    currentIndexRef.current = nextBlockIndex;
    blocksRef.current = splitIntoBlocks(book.chapters[nextChapterIndex]?.text || "");
    queuedOffsetRef.current = 0;
    setRate(book.rate);
    rateRef.current = book.rate;
    setSelectedEnglishVoiceURI(nextVoicePreferences.selectedEnglishVoiceURI);
    setSelectedFrenchVoiceURI(nextVoicePreferences.selectedFrenchVoiceURI);
    setSelectedChineseVoiceURI(nextVoicePreferences.selectedChineseVoiceURI);
    setLastImportedFileName(book.lastImportedFileName);
    setLastImportedFileType(book.fileType);
    pendingAutoPlayTargetRef.current = shouldAutoplay
      ? {
          index: nextBlockIndex,
          offsetChars: 0
        }
      : null;
    pendingAutoPlayRef.current = shouldAutoplay;
    setStatus(content.statusOpenedSavedBook);
  }

  function handleDeleteSavedBook(bookId) {
    const nextBooks = savedBooksRef.current.filter((book) => book.id !== bookId);

    if (activeBookId === bookId) {
      stopPlayback();
      clearGeneratedChapterCache();
      restorePlaybackSnapshotRef.current = null;
      autoSaveEnabledRef.current = false;
      setActiveBookId("");
      setBookTitle(content.workspaceTitle);
      setBookCoverImage("");
      setChapters([
        {
          id: "chapter-0",
          title: content.importedTextTitle,
          text: ""
        }
      ]);
      setCurrentChapterIndex(0);
      setCurrentIndex(0);
      currentIndexRef.current = 0;
      queuedOffsetRef.current = 0;
      setLastImportedFileName("");
      setLastImportedFileType("");
    }

    commitSavedBooks(nextBooks);
    setStatus(content.statusDeletedSavedBook);
  }

  const isImportInteractionLocked =
    isConvertingDocument ||
    isExportingMp3 ||
    isExportingChineseEpub ||
    isExportingPlainEpub;

  function revealImportPanel() {
    window.setTimeout(() => {
      importPanelRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start"
      });
    }, 120);
  }

  function handleRequestFileImport(options = {}) {
    const shouldCloseSheet = Boolean(options.closeSheet);

    if (!hasAcceptedUserDeclaration) {
      setStatus("Please accept the declaration before uploading.");
      revealImportPanel();
      return;
    }

    if (isImportInteractionLocked) {
      return;
    }

    fileInputRef.current?.click?.();

    if (shouldCloseSheet) {
      closeUtilitySheet();
    }
  }

  function handleShelfWindowPrevious() {
    setShelfStartIndex((previous) => Math.max(previous - 1, 0));
  }

  function handleShelfWindowNext() {
    setShelfStartIndex((previous) => Math.min(previous + 1, maxShelfStartIndex));
  }

  function handleDragOver(event) {
    event.preventDefault();
    if (!hasAcceptedUserDeclaration || isImportInteractionLocked) {
      setDragActive(false);
      return;
    }
    setDragActive(true);
  }

  function handleDragLeave(event) {
    event.preventDefault();
    setDragActive(false);
  }

  async function handleDrop(event) {
    event.preventDefault();
    setDragActive(false);

    if (!hasAcceptedUserDeclaration) {
      setStatus("Please accept the declaration before uploading.");
      revealImportPanel();
      return;
    }

    if (isImportInteractionLocked) {
      return;
    }

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      await importFile(file);
    }
  }

  function seekToElapsedSeconds(targetSeconds) {
    clearBrowserSpeechResumeCard();
    if (!blocksRef.current.length) {
      setStatus(content.noBlocks);
      return;
    }

    const model = getCurrentPlaybackModel();
    const target = findSeekTarget(model, blocksRef.current, targetSeconds);
    const shouldContinueTracking = playbackIntentRef.current && isPlayingRef.current && !isPausedRef.current;
    const hasLoadedAudio = hasCurrentChapterLoadedAudio();

    setCurrentIndex(target.blockIndex);
    currentIndexRef.current = target.blockIndex;
    queuedOffsetRef.current = target.offsetChars;
    moveLoadedAudioToElapsed(target.elapsedSeconds);
    syncProgressToCursor(target.blockIndex, target.offsetChars);

      if (shouldContinueTracking) {
        if (!hasLoadedAudio && supportsSpeechFallback && !aiAudioAvailable) {
          stopPlayback({ preserveIntent: true });
          scheduleSeekResume(target.blockIndex, target.offsetChars);
          return;
        }

      resumeProgressTracking();
    }

    persistReaderState({
      playbackSnapshot: {
        phase: shouldContinueTracking ? playbackPhaseRef.current : "ready",
        shouldResume: playbackIntentRef.current && !isPausedRef.current,
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: target.blockIndex,
        offsetChars: target.offsetChars,
        elapsedSeconds: target.elapsedSeconds
      }
    });
  }

  function handleSkipBySeconds(deltaSeconds) {
    if (!blocksRef.current.length) {
      setStatus(content.noBlocks);
      return;
    }

    seekToElapsedSeconds(getCurrentEstimatedElapsedSeconds() + deltaSeconds);
  }

  skipBackActionRef.current = () => handleSkipBySeconds(-15);
  skipForwardActionRef.current = () => handleSkipBySeconds(15);

  function seekToProgressRatio(ratio) {
    const model = getCurrentPlaybackModel();
    const totalSeconds =
      progressState.totalSeconds ||
      playbackProgressRef.current.totalSeconds ||
      model.totalSeconds;

    if (!blocksRef.current.length || !totalSeconds) {
      setStatus(content.noBlocks);
      return;
    }

    seekToElapsedSeconds(totalSeconds * clampNumber(ratio, 0, 1));
  }

  function commitSeekPercent(percent) {
    const safePercent = clampNumber(Number(percent) || 0, 0, 100);
    setSeekPreviewPercent(safePercent);
    seekToProgressRatio(safePercent / 100);
  }

  function getSeekPercentFromClientX(clientX) {
    const rect =
      progressInputRef.current?.getBoundingClientRect?.() ||
      progressWrapRef.current?.getBoundingClientRect?.();

    if (!rect || !rect.width) {
      return null;
    }

    return ((clientX - rect.left) / rect.width) * 100;
  }

  function handleSeekInteractionEnd(event) {
    const activePointerId = seekDragRef.current?.pointerId;

    if (
      activePointerId != null &&
      event?.pointerId != null &&
      activePointerId !== event.pointerId
    ) {
      return;
    }

    if (activePointerId != null) {
      try {
        progressInputRef.current?.releasePointerCapture?.(activePointerId);
      } catch (error) {
        // Ignore unsupported pointer capture release.
      }
    }

    seekDragRef.current = null;
    setIsSeekInteracting(false);
  }

  function handleSeekTrackPointerDown(event) {
    if (
      (event.pointerType === "mouse" && event.button !== 0) ||
      !blocks.length ||
      !seekableDuration ||
      !progressInputRef.current
    ) {
      return;
    }

    const nextPercent = getSeekPercentFromClientX(event.clientX);

    if (nextPercent == null) {
      return;
    }

    seekDragRef.current = { pointerId: event.pointerId };
    setIsSeekInteracting(true);
    commitSeekPercent(nextPercent);

    try {
      progressInputRef.current.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture on older browsers.
    }

    event.preventDefault();
  }

  function handleSeekTrackPointerMove(event) {
    if (seekDragRef.current?.pointerId !== event.pointerId) {
      return;
    }

    const nextPercent = getSeekPercentFromClientX(event.clientX);

    if (nextPercent == null) {
      return;
    }

    commitSeekPercent(nextPercent);
    event.preventDefault();
  }

  function handleSeekTrackKeyDown(event) {
    if (!blocks.length || !seekableDuration) {
      return;
    }

    let nextPercent = displayedProgressPct || 0;

    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
        nextPercent -= 5;
        break;
      case "ArrowRight":
      case "ArrowUp":
        nextPercent += 5;
        break;
      case "PageDown":
        nextPercent -= 10;
        break;
      case "PageUp":
        nextPercent += 10;
        break;
      case "Home":
        nextPercent = 0;
        break;
      case "End":
        nextPercent = 100;
        break;
      default:
        return;
    }

    event.preventDefault();
    commitSeekPercent(nextPercent);
    setIsSeekInteracting(false);
  }

  function readPersistedPlaybackSnapshot() {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
      return sanitizePlaybackSnapshot(saved.playbackSnapshot);
    } catch (error) {
      return null;
    }
  }

  function reconcileForegroundPlayback(source) {
    const audio = audioRef.current;
    const persistedSnapshot = readPersistedPlaybackSnapshot();
    const audioLoaded = hasCurrentChapterLoadedAudio();
    const browserSpeechActive = Boolean(
      supportsSpeechFallback &&
      typeof window !== "undefined" &&
      window.speechSynthesis &&
      (window.speechSynthesis.speaking || window.speechSynthesis.pending)
    );
    const snapshotEntry = persistedSnapshot
      && persistedSnapshot.chapterIndex === currentChapterIndexRef.current
      ? hydrateSnapshotAudioEntry(persistedSnapshot, `${source}-snapshot`)
      : null;

    debugLog("foreground-reconcile", {
      source,
      phase: playbackPhaseRef.current,
      audioLoaded,
      snapshotHydrated: Boolean(snapshotEntry),
      intendedResume: Boolean(persistedSnapshot?.shouldResume),
      currentChapterIndex: currentChapterIndexRef.current
    });

    if (
      supportsSpeechFallback &&
      persistedSnapshot &&
      persistedSnapshot.activeBookId === (activeBookId || persistedSnapshot.activeBookId) &&
      persistedSnapshot.audioMode === "browser-fallback"
    ) {
      debugLog("speech-restore-attempt", {
        source,
        chapterIndex: persistedSnapshot.chapterIndex,
        blockIndex: persistedSnapshot.blockIndex,
        backgroundInterrupted: persistedSnapshot.backgroundInterrupted
      });

      if (browserSpeechActive) {
        playbackIntentRef.current = true;
        pauseOriginRef.current = "none";
        clearBrowserSpeechResumeCard();
        resumeProgressTrackingRef.current();
        setIsPlaying(true);
        setIsPaused(false);
        setPlaybackPhaseState("fallback", {
          source: `${source}-speech-active`,
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        debugLog("speech-resume-success", {
          source,
          mode: "already-speaking"
        });
        return;
      }

      if (persistedSnapshot.shouldResume && persistedSnapshot.pauseOrigin !== "user") {
        playbackIntentRef.current = false;
        pauseOriginRef.current = "system";
        freezeProgressTrackingRef.current();
        setIsPlaying(false);
        setIsPaused(false);
        setPlaybackPhaseState("ready", {
          source: `${source}-speech-resume-ready`,
          chapterIndex: persistedSnapshot.chapterIndex,
          blockIndex: persistedSnapshot.blockIndex
        });
        offerBrowserSpeechResume(source, persistedSnapshot);
        debugLog("speech-resume-failure", {
          source,
          mode: "resume-offered"
        });
        return;
      }
    }

    if (
      audio &&
      persistedSnapshot &&
      persistedSnapshot.activeBookId === (activeBookId || persistedSnapshot.activeBookId) &&
      persistedSnapshot.chapterIndex === currentChapterIndexRef.current &&
      audioLoaded &&
      audio.paused
    ) {
      const delta = Math.abs((audio.currentTime || 0) - persistedSnapshot.elapsedSeconds);

      if (delta > SNAPSHOT_AUDIO_DRIFT_TOLERANCE_SECONDS) {
        moveLoadedAudioToElapsed(persistedSnapshot.elapsedSeconds);
      }
    }

    if (playbackIntentRef.current && aiAudioAvailable) {
      if (audioLoaded) {
        if (audio && audio.paused && !isPausedRef.current) {
          schedulePlaybackRecovery(140);
      } else if (audio && !audio.paused && !audio.ended) {
        resumeProgressTrackingRef.current();
        setPlaybackPhaseState("playing", {
          source: `${source}-active`,
          chapterIndex: currentChapterIndexRef.current
        });
        warmUpcomingChapterAudio(currentChapterIndexRef.current, `${source}-ahead`);
      }
      } else if (
        persistedSnapshot &&
        persistedSnapshot.shouldResume &&
        persistedSnapshot.audioMode === "ai-audio" &&
        persistedSnapshot.pauseOrigin !== "user"
      ) {
        const snapshotReadyPromise = snapshotEntry
          ? attachChapterAudio(snapshotEntry, persistedSnapshot.elapsedSeconds).catch(() =>
              ensureChapterAudioLoaded(currentChapterIndexRef.current, {
                prefetch: true,
                source: `${source}-snapshot-refresh`
              }).then((entry) => attachChapterAudio(entry, persistedSnapshot.elapsedSeconds))
            )
          : ensureChapterAudioLoaded(currentChapterIndexRef.current, {
              prefetch: true,
              source: `${source}-warm`
            }).then((entry) => attachChapterAudio(entry, persistedSnapshot.elapsedSeconds));

        snapshotReadyPromise
          .then(() => {
            debugLog("foreground-restore-ready", {
              source,
              chapterIndex: currentChapterIndexRef.current,
              restoredFromSnapshot: Boolean(snapshotEntry)
            });
            warmUpcomingChapterAudio(currentChapterIndexRef.current, `${source}-ahead`);
            schedulePlaybackRecovery(140);
          })
          .catch((error) => {
            if (canUseBrowserSpeechFallback(error)) {
              suppressAiPlaybackForSession(error, `${source}-warm`);
            }
            debugLog("foreground-reconcile-failed", {
              source,
              chapterIndex: currentChapterIndexRef.current,
              message: error instanceof Error ? error.message : "Unknown foreground reconcile error."
            });
          });
      }
    }

    if (isPlayingRef.current && !isPausedRef.current) {
      requestWakeLock();
    }
  }

  const playButtonLabel = isPlaying ? (isPaused ? content.resume : content.pause) : content.play;
  const currentPlaybackLanguage = shouldPreferUnderstandingLanguage(
    currentBlock?.language,
    preferredReadingLanguage
  )
    ? currentTranslatedBlock?.targetLanguage ||
      (sanitizePreferredReadingLanguage(preferredReadingLanguage) === PREFERRED_READING_LANGUAGES.ZH
        ? "zh-CN"
        : "en-US")
    : currentBlock?.language || "en-US";
  const preferredCurrentVoice = currentBlock
    ? getPreferredVoiceForLanguage(currentPlaybackLanguage, {
        english: englishVoices,
        french: frenchVoices,
        chinese: chineseVoices,
        selectedEnglishVoiceURI,
        selectedFrenchVoiceURI,
        selectedChineseVoiceURI
      })
    : null;
  const activeBrowserVoice = currentBlock && !aiAudioAvailable && supportsSpeechFallback
    ? getCurrentBrowserSpeechConfig(currentPlaybackLanguage).voice
    : null;
  const activeVoiceName =
    activeBrowserVoice?.name ||
    preferredCurrentVoice?.optionLabel ||
    preferredCurrentVoice?.name ||
    "";
  const usesPersonalAiKey = FREE_BROWSER_SPEECH_ONLY_MODE ? false : personalAiAudioEnabled;
  const personalFallbackActive =
    !FREE_BROWSER_SPEECH_ONLY_MODE &&
    audioConfig.checked &&
    usesPersonalAiKey &&
    !aiAudioAvailable &&
    supportsSpeechFallback;
  const shouldShowAudioStatusPill =
    supportsSpeechFallback || (!FREE_BROWSER_SPEECH_ONLY_MODE && (aiAudioAvailable || personalFallbackActive));
  const audioStatusMode = aiAudioAvailable && usesPersonalAiKey
    ? "personal-key-ai-audio"
    : personalFallbackActive
    ? "personal-key-browser-fallback"
    : "browser-speech-fallback";
  const visibleVoiceCount = new Set(
    [...englishVoices, ...frenchVoices, ...chineseVoices].map((voice) => voice.voiceURI)
  ).size;
  const hasPreviousChapter = currentChapterIndex > 0;
  const hasNextChapter = currentChapterIndex < chapters.length - 1;
  const compactAudioStatusLabel = !audioConfig.checked
    ? content.audioEngineChecking
    : aiAudioAvailable
    ? content.browserSpeechReady
    : personalFallbackActive
    ? content.browserFallbackDegraded
    : supportsSpeechFallback
    ? content.browserFallbackReady
    : content.browserSpeechMissing;
  const compactAudioStatusMeta = aiAudioAvailable && usesPersonalAiKey
    ? content.audioEnginePersonalKeyActive
    : personalFallbackActive
    ? content.audioEnginePersonalKeyAdded
    : supportsSpeechFallback
    ? content.browserSpeechSupportNote
    : null;
  const canTogglePlayback =
    blocks.length > 0 &&
    !isGeneratingAudio &&
    (
      isPlaying ||
      isPaused ||
      hasCurrentChapterLoadedAudio() ||
      aiAudioAvailable ||
      supportsSpeechFallback
    );
  const seekableDuration =
    progressState.totalSeconds ||
    playbackProgressRef.current.totalSeconds ||
    getCurrentPlaybackModel().totalSeconds ||
    0;
  const hasLoadedAudioForProgress = hasCurrentChapterLoadedAudio();
  const liveDuration =
    hasLoadedAudioForProgress &&
    Number.isFinite(audioRef.current?.duration) &&
    audioRef.current.duration > 0
      ? audioRef.current.duration
      : seekableDuration;
  const liveCurrentTime =
    hasLoadedAudioForProgress &&
    Number.isFinite(audioRef.current?.currentTime) &&
    audioRef.current.currentTime >= 0
      ? clampNumber(audioRef.current.currentTime, 0, liveDuration || seekableDuration || 0)
      : clampNumber(progressState.elapsedSeconds, 0, seekableDuration || 0);
  const progressPct = hasLoadedAudioForProgress && liveDuration > 0
    ? clampNumber((liveCurrentTime / liveDuration) * 100, 0, 100)
    : progressState.progressPercent;
  const displayedProgressPct = isSeekInteracting ? seekPreviewPercent : progressPct;
  const miniPlayerDuration = seekableDuration || liveDuration || 0;
  const miniPlayerCurrentTime = miniPlayerDuration
    ? clampNumber((displayedProgressPct / 100) * miniPlayerDuration, 0, miniPlayerDuration)
    : 0;
  const generatedFileCoverImage = createGeneratedFileCoverDataUrl(
    lastImportedFileName || bookTitle || content.workspaceTitle,
    bookTitle || content.workspaceTitle
  );
  const sleepTimerRemainingSeconds = sleepCountdownSeconds || 0;
  const sleepTimerDisplayLabel =
    sleepMode === "off"
      ? content.autoStopOff
      : sleepMode === "chapter"
      ? content.autoStopChapter
      : sleepTimerRemainingSeconds > 0
      ? formatClock(sleepTimerRemainingSeconds)
      : content.autoStopOff;
  const maskedPersonalApiKey = maskPersonalApiKey(personalApiKey);

  function focusPersonalAudioKey() {
    if ((isEditingPersonalApiKey || !personalApiKey.trim()) && personalApiKeyInputRef.current) {
      personalApiKeyInputRef.current.focus?.();
      personalApiKeyInputRef.current.select?.();
    }
  }

  function revealVoiceSettings() {
    setShowVoiceTuning(true);

    if (isCompactLayout) {
      setActiveUtilitySheet("audio");
      window.setTimeout(() => {
        focusPersonalAudioKey();
      }, 120);
      return;
    }

    window.setTimeout(() => {
      voiceSectionRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start"
      });
      focusPersonalAudioKey();
    }, 120);
  }

  function revealApiKeyPanel() {
    if (isCompactLayout) {
      setShowVoiceTuning(true);
      setActiveUtilitySheet("audio");
      window.setTimeout(() => {
        focusPersonalAudioKey();
      }, 120);
      return;
    }

    window.setTimeout(() => {
      apiKeySectionRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start"
      });
      focusPersonalAudioKey();
    }, 120);
  }

  function handleOpenAudioSettings() {
    revealVoiceSettings();
  }

  function handleOpenAudioKeyGuide() {
    setShowAudioKeyGuide(true);
    revealApiKeyPanel();
  }

  function handlePersonalApiKeyDraftChange(event) {
    setPersonalApiKeyDraft(event.target.value);
    if (personalApiKeyError) {
      setPersonalApiKeyError("");
    }
  }

  function handleSavePersonalApiKey() {
    const normalizedDraft = normalizePersonalApiKey(personalApiKeyDraft);

    if (!isLikelyOpenAiApiKey(normalizedDraft)) {
      setPersonalApiKeyError(content.audioEnginePersonalKeyInvalid);
      window.setTimeout(() => {
        personalApiKeyInputRef.current?.focus?.();
        personalApiKeyInputRef.current?.select?.();
      }, 20);
      return;
    }

    setPersonalApiKey(normalizedDraft);
    setPersonalApiKeyDraft(normalizedDraft);
    setIsEditingPersonalApiKey(false);
    setPersonalApiKeyError("");
    setStatus(content.audioEnginePersonalKeyAdded);
  }

  function handleEditPersonalApiKey() {
    setIsEditingPersonalApiKey(true);
    setPersonalApiKeyDraft(personalApiKey);
    setPersonalApiKeyError("");
    window.setTimeout(() => {
      personalApiKeyInputRef.current?.focus?.();
      personalApiKeyInputRef.current?.select?.();
    }, 20);
  }

  function handleToggleVoiceTuning() {
    setShowVoiceTuning((currentValue) => {
      const nextValue = !currentValue;

      if (!nextValue) {
        setShowAudioKeyGuide(false);
      }

      if (nextValue) {
        window.setTimeout(() => {
          if (isCompactLayout) {
            focusPersonalAudioKey();
            return;
          }

          voiceSectionRef.current?.scrollIntoView?.({
            behavior: "smooth",
            block: "start"
          });
          focusPersonalAudioKey();
        }, 120);
      }

      return nextValue;
    });
  }

  function openUtilitySheet(panelName) {
    if (!isCompactLayout) {
      if (panelName === "audio") {
        revealVoiceSettings();
      } else if (panelName === "source") {
        sourceDetailsRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start"
        });
      }
      return;
    }

    if (panelName === "audio") {
      setShowVoiceTuning(true);
    }

    setActiveUtilitySheet(panelName);
  }

  function closeUtilitySheet() {
    setActiveUtilitySheet("");
  }

  function handleTopbarImportShortcut() {
    handleRequestFileImport();
  }

  function handleTopbarImportDragOver(event) {
    event.preventDefault();

    if (!hasAcceptedUserDeclaration || isImportInteractionLocked) {
      setIsTopbarImportDragActive(false);
      return;
    }

    setIsTopbarImportDragActive(true);
  }

  function handleTopbarImportDragLeave(event) {
    event.preventDefault();
    setIsTopbarImportDragActive(false);
  }

  async function handleTopbarImportDrop(event) {
    event.preventDefault();
    setIsTopbarImportDragActive(false);

    if (!hasAcceptedUserDeclaration) {
      setStatus("Please accept the declaration before uploading.");
      revealImportPanel();
      return;
    }

    if (isImportInteractionLocked) {
      return;
    }

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      await importFile(file);
    }
  }

  function clearReadingWindowAutoResetTimeout() {
    if (readingWindowAutoResetTimeoutRef.current != null) {
      window.clearTimeout(readingWindowAutoResetTimeoutRef.current);
      readingWindowAutoResetTimeoutRef.current = null;
    }
  }

  function handleChapterPopoverDragStart(event) {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    const popover = chapterPickerRef.current?.querySelector?.(".echoChapterPopover");
    const rect = popover?.getBoundingClientRect?.();

    if (!rect) {
      return;
    }

    chapterPopoverDragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startTop: rect.top,
      startOffset: chapterPopoverOffsetY,
      height: rect.height,
      didDrag: false
    };

    try {
      chapterPickerRef.current?.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture.
    }
  }

  function handleChapterPopoverDragMove(event) {
    const dragState = chapterPopoverDragRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaY = event.clientY - dragState.startY;

    if (!dragState.didDrag && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
      return;
    }

    if (!dragState.didDrag) {
      dragState.didDrag = true;
      setIsChapterPopoverDragging(true);
    }

    const viewportHeight = window.innerHeight || 0;
    const minTop = VIEWPORT_EDGE_GAP_PX;
    const maxTop = Math.max(
      VIEWPORT_EDGE_GAP_PX,
      viewportHeight - dragState.height - VIEWPORT_EDGE_GAP_PX
    );
    const desiredTop = dragState.startTop + deltaY;
    const clampedTop = Math.max(minTop, Math.min(maxTop, desiredTop));

    setChapterPopoverOffsetY(dragState.startOffset + (clampedTop - dragState.startTop));
  }

  function handleChapterPopoverDragEnd(event) {
    const dragState = chapterPopoverDragRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    try {
      chapterPickerRef.current?.releasePointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture release.
    }

    chapterPopoverDragRef.current = null;
    setIsChapterPopoverDragging(false);
  }

  function handleReadingWindowDragStart(event) {
    if (isCompactLayout || isPureReadingMode) {
      return;
    }

    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    const combinedOffset = clampNumber(
      readingWindowUserOffsetY + readingWindowAutoOffsetY,
      -READING_WINDOW_DRAG_LIMIT_PX,
      READING_WINDOW_DRAG_LIMIT_PX
    );

    clearReadingWindowAutoResetTimeout();
    setReadingWindowAutoOffsetY(0);
    setReadingWindowUserOffsetY(combinedOffset);

    readingWindowDragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startOffset: combinedOffset,
      didDrag: false
    };

    try {
      readingWindowRef.current?.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture.
    }
  }

  function handleReadingWindowDragMove(event) {
    if (isCompactLayout || isPureReadingMode) {
      return;
    }

    const dragState = readingWindowDragRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaY = event.clientY - dragState.startY;

    if (!dragState.didDrag && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
      return;
    }

    if (!dragState.didDrag) {
      dragState.didDrag = true;
      setIsReadingWindowDragging(true);
    }

    event.preventDefault();

    setReadingWindowUserOffsetY(
      clampNumber(
        dragState.startOffset + deltaY,
        -READING_WINDOW_DRAG_LIMIT_PX,
        READING_WINDOW_DRAG_LIMIT_PX
      )
    );
  }

  function handleReadingWindowDragEnd(event) {
    if (isCompactLayout || isPureReadingMode) {
      return;
    }

    const dragState = readingWindowDragRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    try {
      readingWindowRef.current?.releasePointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture release.
    }

    readingWindowDragRef.current = null;
    setIsReadingWindowDragging(false);
  }

  function openSourceEditor(options = {}) {
    const shouldCloseSheet = Boolean(options.closeSheet);

    if (shouldCloseSheet && isCompactLayout) {
      closeUtilitySheet();
    }

    window.setTimeout(() => {
      sourceDetailsRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start"
      });
      sourceTextareaRef.current?.focus?.();
    }, 120);
  }

  function navigatePureReading(delta) {
    if (!blocks.length) {
      return;
    }

    setPureReadingIndex((currentValue) => {
      const nextIndex = clampIndex(currentValue + delta, blocks.length);

      if (nextIndex === currentValue) {
        return currentValue;
      }

      setPureReadingDisplaySnapshot(buildPureReadingDisplaySnapshot(nextIndex));
      setStatus(delta > 0 ? content.statusMovedNext : content.statusMovedPrevious);
      return nextIndex;
    });
  }
  navigatePureReadingRef.current = navigatePureReading;

  function handlePureReadingToggle() {
    if (!isPaused) {
      setStatus(content.statusPureReadingNeedsPause);
      return;
    }

    setShowUnderstandLanguageMenu(false);
    setPureReadingIndex(currentIndexRef.current);
    setPureReadingDisplaySnapshot(buildPureReadingDisplaySnapshot(currentIndexRef.current));
    setReadingWindowUserOffsetY(0);
    setReadingWindowAutoOffsetY(0);
    setIsReadingWindowDragging(false);
    setIsPureReadingMode((currentValue) => {
      const nextValue = !currentValue;
      setStatus(
        nextValue ? content.statusPureReadingEnabled : content.statusPureReadingDisabled
      );
      return nextValue;
    });
  }

  function handlePureReadingWheel(event) {
    if (!isPureReadingMode || !blocks.length) {
      return;
    }

    if (Math.abs(event.deltaY) < 8) {
      return;
    }

    const now = Date.now();

    if (now - pureReadingWheelLockRef.current < 180) {
      event.preventDefault();
      return;
    }

    pureReadingWheelLockRef.current = now;
    event.preventDefault();
    navigatePureReading(event.deltaY > 0 ? 1 : -1);
  }

  function handlePureReadingClick(event) {
    if (!isPureReadingMode || !blocks.length) {
      return;
    }

    if (window.getSelection?.()?.toString()) {
      return;
    }

    const rect = event.currentTarget?.getBoundingClientRect?.();

    if (!rect || !rect.height) {
      return;
    }

    const relativeY = (event.clientY - rect.top) / rect.height;

    if (relativeY <= 0.34) {
      navigatePureReading(-1);
    } else if (relativeY >= 0.66) {
      navigatePureReading(1);
    }
  }

  function renderRateControls() {
    return (
      <div className="readerLabRateRow readerLabRateRowStandalone">
        <label>{content.speed}</label>
        <div className="readerLabRateScale">
          <span className="readerLabRateIcon" aria-hidden="true">🐢</span>
          <div
            className="readerLabRateRailShell"
            onPointerDown={handleRateRailPointerDown}
            style={{
              "--reader-lab-rate-progress": `${rateProgressPercent}%`,
              "--reader-lab-rate-count": RATE_OPTIONS.length
            }}
          >
            <div className="readerLabRateTrack" aria-hidden="true">
              <span className="readerLabRateTrackFill" />
            </div>
            <div className="readerLabRateStops" aria-hidden="true">
              {RATE_OPTIONS.map((rateOption, index) => (
                <span
                  key={`rate-stop-${rateOption}`}
                  className={`readerLabRateStop ${
                    index < selectedRateIndex ? "active" : ""
                  } ${index === selectedRateIndex ? "current" : ""}`}
                >
                  <span className="readerLabRateBead" />
                  <span className="readerLabRateStopLabel">{rateOption.toFixed(1)}</span>
                </span>
              ))}
            </div>
            <input
              type="range"
              min="0"
              max={String(Math.max(RATE_OPTIONS.length - 1, 0))}
              step="1"
              value={selectedRateIndex}
              className="readerLabRateSlider"
              aria-label={content.speed}
              aria-valuetext={`${RATE_OPTIONS[selectedRateIndex].toFixed(1)}x`}
              onChange={(event) =>
                handleSelectRate(
                  RATE_OPTIONS[
                    Math.round(
                      clampNumber(
                        Number(event.target.value),
                        0,
                        Math.max(RATE_OPTIONS.length - 1, 0)
                      )
                    )
                  ]
                )
              }
            />
          </div>
          <span className="readerLabRateIcon" aria-hidden="true">🐰</span>
        </div>
      </div>
    );
  }

  function handlePreferredReadingLanguageChange(nextLanguage) {
    const normalizedLanguage = sanitizePreferredReadingLanguage(
      nextLanguage,
      preferredReadingLanguage
    );

    if (normalizedLanguage === preferredReadingLanguage) {
      setShowUnderstandLanguageMenu(false);
      return;
    }

    if (blocksRef.current.length && (isPlaying || isPaused)) {
      const currentTarget = findSeekTarget(
        getCurrentPlaybackModel(),
        blocksRef.current,
        getCurrentEstimatedElapsedSeconds()
      );
      const shouldResume = Boolean(playbackIntentRef.current && !isPausedRef.current);

      stopPlayback({ preserveIntent: shouldResume, source: "translation-playback-change" });
      setCurrentIndex(currentTarget.blockIndex);
      currentIndexRef.current = currentTarget.blockIndex;
      queuedOffsetRef.current = currentTarget.offsetChars;
      syncProgressToCursor(currentTarget.blockIndex, currentTarget.offsetChars);
      setPreferredReadingLanguage(normalizedLanguage);
      setShowUnderstandLanguageMenu(false);
      setStatus(content.statusTranslationPlaybackUpdated);

      if (shouldResume) {
        window.setTimeout(() => {
          speakFromIndexRef.current(currentTarget.blockIndex, {
            offsetChars: currentTarget.offsetChars,
            forceBrowserFallback: true,
            source: "translation-playback-change"
          });
        }, 60);
      }
      return;
    }

    setPreferredReadingLanguage(normalizedLanguage);
    setShowUnderstandLanguageMenu(false);
    setStatus(content.statusTranslationPlaybackUpdated);
  }

  function renderUnderstandLanguageControl() {
    const preferredReadingLanguageLabel = getPreferredReadingLanguageLabel(
      preferredReadingLanguage,
      content
    );

    return (
      <div
        ref={understandLanguageMenuRef}
        className={`echoUnderstandControl ${showUnderstandLanguageMenu ? "is-open" : ""}`}
      >
        <button
          type="button"
          className={`echoUnderstandControl__trigger ${showUnderstandLanguageMenu ? "active" : ""}`}
          aria-haspopup="menu"
          aria-expanded={showUnderstandLanguageMenu}
          onClick={() => setShowUnderstandLanguageMenu((currentValue) => !currentValue)}
        >
          <span className="echoUnderstandControl__copy">
            <span className="echoUnderstandControl__label">{content.understandInLabel}</span>
            <span className="echoUnderstandControl__value">{preferredReadingLanguageLabel}</span>
          </span>
          <span className="echoUnderstandControl__chevron" aria-hidden="true">
            ▾
          </span>
        </button>

        {showUnderstandLanguageMenu ? (
          <div className="echoUnderstandControl__menu" role="menu" aria-label={content.understandInLabel}>
            <div className="echoUnderstandControl__options">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={preferredReadingLanguage === PREFERRED_READING_LANGUAGES.ZH}
                className={`echoUnderstandControl__option ${
                  preferredReadingLanguage === PREFERRED_READING_LANGUAGES.ZH ? "is-active" : ""
                }`}
                onClick={() => handlePreferredReadingLanguageChange(PREFERRED_READING_LANGUAGES.ZH)}
              >
                {content.understandInChinese}
              </button>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={preferredReadingLanguage === PREFERRED_READING_LANGUAGES.EN}
                className={`echoUnderstandControl__option ${
                  preferredReadingLanguage === PREFERRED_READING_LANGUAGES.EN ? "is-active" : ""
                }`}
                onClick={() => handlePreferredReadingLanguageChange(PREFERRED_READING_LANGUAGES.EN)}
              >
                {content.understandInEnglish}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  function renderConversionQuickPanel() {
    return (
      <section className="echoTranslationQuickCard echoConversionQuickCard" aria-label={content.convertTitle}>
        <div className="echoTranslationQuickCard__header">
          <div>
            <h3 className="echoTranslationQuickCard__title">{content.convertTitle}</h3>
            <p className="echoTranslationQuickCard__hint">
              {!currentConversionSourceFormat
                ? content.statusConvertNeedFile
                : conversionSourceMismatch
                ? fillTemplate(content.statusConvertMismatch, {
                    current: currentConversionSourceFormat,
                    expected: conversionSourceFormat || content.fileTypeTxt
                  })
                : content.convertSupportedTargets}
            </p>
          </div>
        </div>

        <div className="echoConvertPanel echoConvertPanel--quick" aria-label={content.convertTitle}>
          <div className="echoConvertRow">
            <label className="echoConvertField">
              <span className="echoConvertLabel">{content.convertSourceLabel}</span>
              <select
                className="echoConvertSelect"
                value={conversionSourceFormat}
                onChange={(event) => setConversionSourceFormat(event.target.value)}
                disabled={
                  !currentConversionSourceFormat ||
                  isConvertingDocument ||
                  isExportingMp3 ||
                  isExportingChineseEpub ||
                  isExportingPlainEpub
                }
              >
                {!currentConversionSourceFormat ? (
                  <option value="">{content.statusConvertNeedFile}</option>
                ) : null}
                {SUPPORTED_CONVERSION_SOURCE_FORMATS.map((formatLabel) => (
                  <option key={`convert-source-main-${formatLabel}`} value={getFileTypeLabel(formatLabel, content)}>
                    {getFileTypeLabel(formatLabel, content)}
                  </option>
                ))}
              </select>
            </label>

            <button
              className="echoConvertArrowButton"
              type="button"
              onClick={handleConvertCurrentDocument}
              disabled={
                !canConvertCurrentDocument ||
                isExportingMp3 ||
                isExportingChineseEpub ||
                isExportingPlainEpub
              }
              aria-label={content.convertArrowAria}
              title={content.convertArrowAria}
            >
              {isConvertingDocument ? "…" : "→"}
            </button>

            <label className="echoConvertField">
              <span className="echoConvertLabel">{content.convertTargetLabel}</span>
              <select
                className="echoConvertSelect"
                value={conversionTargetFormat}
                onChange={(event) => setConversionTargetFormat(event.target.value)}
                disabled={
                  !currentConversionSourceFormat ||
                  isConvertingDocument ||
                  isExportingMp3 ||
                  isExportingChineseEpub ||
                  isExportingPlainEpub
                }
              >
                {SUPPORTED_CONVERSION_TARGET_FORMATS.map((formatLabel) => (
                  <option key={`convert-target-main-${formatLabel}`} value={getFileTypeLabel(formatLabel, content)}>
                    {getFileTypeLabel(formatLabel, content)}
                  </option>
                ))}
              </select>
            </label>
          </div>

            <div className="echoConvertFootnote echoConvertFootnote--stack">
              <p>{content.convertHint}</p>
              <div className="echoConvertExportRow echoConvertExportRow--single">
                <button
                  className="echoImportButton"
                  type="button"
                  onClick={handleExportOriginalEpub}
                  disabled={
                    !chapters.length ||
                    isConvertingDocument ||
                    isExportingMp3 ||
                    isExportingChineseEpub ||
                    isExportingPlainEpub
                  }
                >
                  {content.exportEpubOriginalLabel}
                </button>
              </div>
              <div className="echoConvertExportRow">
                <button
                  className="echoImportButton"
                  type="button"
                  onClick={() => handleExportMp3()}
                  disabled={
                    !chapters.length ||
                    isExportingMp3 ||
                    isExportingChineseEpub ||
                    isExportingPlainEpub
                  }
                  title={content.exportMp3Hint || content.exportMp3OriginalLabel}
                >
                  {content.exportMp3OriginalLabel}
                </button>
                <button
                  className="echoImportButton primary"
                  type="button"
                  onClick={() => handleExportMp3({ targetLanguage: "zh-CN" })}
                  disabled={
                    !chapters.length ||
                    isExportingMp3 ||
                    isExportingChineseEpub ||
                    isExportingPlainEpub
                  }
                  title={content.exportMp3Hint || content.exportMp3ChineseLabel}
                >
                  {content.exportMp3ChineseLabel}
              </button>
            </div>
            <div className="echoConvertExportRow echoConvertExportRow--single">
              <button
                className="echoImportButton accent"
                type="button"
                onClick={handleExportChineseEpub}
                disabled={
                  !chapters.length ||
                  isConvertingDocument ||
                  isExportingMp3 ||
                  isExportingChineseEpub ||
                  isExportingPlainEpub
                }
              >
                {content.exportEpubChineseLabel}
              </button>
            </div>
            {isExportingMp3 && exportProgress.total > 0 ? (
              <p className="echoConvertFootnote echoConvertFootnote--progress" role="status" aria-live="polite">
                {`${content.statusExportingMp3} ${Math.min(
                  exportProgress.done,
                  exportProgress.total
                )}/${exportProgress.total || chapters.length}`}
              </p>
            ) : null}
            {isExportingPlainEpub && plainEpubProgress.total > 0 ? (
              <p className="echoConvertFootnote echoConvertFootnote--progress" role="status" aria-live="polite">
                {fillTemplate(content.statusExportingOriginalEpubProgress, {
                  done: Math.min(plainEpubProgress.done, plainEpubProgress.total),
                  total: plainEpubProgress.total || chapters.length
                })}
              </p>
            ) : null}
            {isExportingChineseEpub && chineseEpubProgress.total > 0 ? (
              <p className="echoConvertFootnote echoConvertFootnote--progress" role="status" aria-live="polite">
                {fillTemplate(content.statusExportingChineseEpubProgress, {
                  done: Math.min(chineseEpubProgress.done, chineseEpubProgress.total),
                  total: chineseEpubProgress.total || chapters.length
                })}
              </p>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  function renderTimerControl() {
    return (
      <div
        className={`echoTimerControl ${showTimerMenu ? "is-open" : ""} ${
          timerMenuDirection === "up" ? "is-open-up" : ""
        }`}
        ref={timerMenuRef}
      >
        <span className="echoTimerControl__label">{content.timerTitle || content.autoStopTitle}</span>
        <button
          ref={timerTriggerRef}
          className={`echoTimerControl__trigger ${showTimerMenu ? "active" : ""}`}
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setShowTimerMenu((value) => !value);
          }}
          aria-expanded={showTimerMenu}
          aria-haspopup="menu"
          aria-label={`${content.timerTitle || content.autoStopTitle}: ${sleepTimerDisplayLabel}`}
        >
          <span className="echoTimerControl__value">{sleepTimerDisplayLabel}</span>
          <span className="echoTimerControl__chevron" aria-hidden="true">
            {showTimerMenu ? "−" : "+"}
          </span>
        </button>

        {showTimerMenu ? (
          <div
            className="echoTimerMenu"
            role="menu"
            aria-label={content.timerTitle || content.autoStopTitle}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              role="menuitemradio"
              aria-checked={sleepMode === "off"}
              className={`echoTimerMenu__option ${sleepMode === "off" ? "active" : ""}`}
              onClick={() => handleSelectSleep("off")}
            >
              {content.autoStopOff}
            </button>

            {TIMER_OPTIONS.map((minutes) => {
              const value = `timer-${minutes}`;
              return (
                <button
                  key={`timer-${minutes}`}
                  type="button"
                  role="menuitemradio"
                  aria-checked={sleepMode === value}
                  className={`echoTimerMenu__option ${sleepMode === value ? "active" : ""}`}
                  onClick={() => handleSelectSleep(minutes)}
                >
                  {minutes}m
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }

  function renderVoiceSelectors() {
    return (
      <>
        <div className="readerLabAudioConfigCard readerLabAudioConfigCardCompact">
          <div className="readerLabAudioConfigHeader readerLabAudioConfigHeaderCompact">
            <div>
              <h4 className="readerLabAudioConfigTitle">{content.audioEngineTitle}</h4>
              <p className="readerLabAudioConfigHint readerLabAudioConfigHintCompact">
                {compactAudioStatusLabel}
              </p>
            </div>

            {shouldShowAudioStatusPill ? (
              <AudioStatusPill
                mode={audioStatusMode}
                hasServerKey={!audioConfig.personalKeyRequired && audioConfig.hasServerKey}
                hasPersonalKey={Boolean(personalApiKey)}
                onOpenSettings={handleOpenAudioSettings}
              />
            ) : null}
          </div>

          <div className="readerLabAudioConfigMeta">
            {compactAudioStatusMeta ? (
              <span className="readerLabMetaChip">{compactAudioStatusMeta}</span>
            ) : null}
            <span className="readerLabMetaChip">
              {content.voiceCount}: {visibleVoiceCount}
            </span>
          </div>
        </div>

        <div className="readerLabVoiceField">
          <label htmlFor="reader-lab-en-voice">{content.englishVoice}</label>
          <div className="readerLabVoiceFieldRow">
            <select
              id="reader-lab-en-voice"
              className="readerLabVoiceSelect"
              value={selectedEnglishVoiceURI}
              onChange={(event) =>
                handleVoiceSelection(
                  setSelectedEnglishVoiceURI,
                  selectedEnglishVoiceURI,
                  event.target.value
                )
              }
            >
              <option value="">{content.voiceAuto}</option>
              {englishVoices.map((voice) => (
                <option key={voice.voiceURI} value={voice.voiceURI}>
                  {voice.optionLabel}
                </option>
              ))}
            </select>
            <button
              className="readerLabPreviewButton"
              onClick={() => previewVoice("en-US")}
              disabled={!englishVoices.length || (!aiAudioAvailable && !supportsSpeechFallback) || isGeneratingAudio}
            >
              {content.previewVoice}
            </button>
          </div>
        </div>

        <div className="readerLabVoiceField">
          <label htmlFor="reader-lab-fr-voice">{content.frenchVoice}</label>
          <div className="readerLabVoiceFieldRow">
            <select
              id="reader-lab-fr-voice"
              className="readerLabVoiceSelect"
              value={selectedFrenchVoiceURI}
              onChange={(event) =>
                handleVoiceSelection(
                  setSelectedFrenchVoiceURI,
                  selectedFrenchVoiceURI,
                  event.target.value
                )
              }
            >
              <option value="">{content.voiceAuto}</option>
              {frenchVoices.map((voice) => (
                <option key={voice.voiceURI} value={voice.voiceURI}>
                  {voice.optionLabel}
                </option>
              ))}
            </select>
            <button
              className="readerLabPreviewButton"
              onClick={() => previewVoice("fr-FR")}
              disabled={!frenchVoices.length || (!aiAudioAvailable && !supportsSpeechFallback) || isGeneratingAudio}
            >
              {content.previewVoice}
            </button>
          </div>
        </div>

        <div className="readerLabVoiceField">
          <label htmlFor="reader-lab-zh-voice">{content.chineseVoice}</label>
          <div className="readerLabVoiceFieldRow">
            <select
              id="reader-lab-zh-voice"
              className="readerLabVoiceSelect"
              value={selectedChineseVoiceURI}
              onChange={(event) =>
                handleVoiceSelection(
                  setSelectedChineseVoiceURI,
                  selectedChineseVoiceURI,
                  event.target.value
                )
              }
            >
              <option value="">{content.voiceAuto}</option>
              {chineseVoices.map((voice) => (
                <option key={voice.voiceURI} value={voice.voiceURI}>
                  {voice.optionLabel}
                </option>
              ))}
            </select>
            <button
              className="readerLabPreviewButton"
              onClick={() => previewVoice("zh-CN")}
              disabled={!chineseVoices.length || (!aiAudioAvailable && !supportsSpeechFallback) || isGeneratingAudio}
            >
              {content.previewVoice}
            </button>
          </div>
        </div>
      </>
    );
  }

  function renderApiKeyPanel({ inSheet = false } = {}) {
    if (FREE_BROWSER_SPEECH_ONLY_MODE) {
      return null;
    }

    const shouldShowPersonalKeyCard =
      audioConfig.personalKeyRequired || !audioConfig.hasServerKey || Boolean(personalApiKey.trim());

    if (!shouldShowPersonalKeyCard) {
      return null;
    }

    return (
      <section
        className={`echoRailCard echoKeyCard ${inSheet ? "is-sheet" : ""}`}
        ref={inSheet ? null : apiKeySectionRef}
      >
        <div className="echoRailCardHeader echoKeyCard__header">
          <div>
            <h3 className="echoRailCardTitle">{content.audioKeyCardTitle}</h3>
            <p className="echoRailCardHint">{content.audioKeyCardSubtitle}</p>
          </div>
          {personalApiKey.trim() ? (
            <span className="readerLabMetaChip">{content.audioEngineSavedKey}</span>
          ) : null}
        </div>

        {!personalApiKey.trim() ? (
          <div className="echoAudioPromptCard echoKeyCard__prompt">
            <div className="echoAudioPromptTitle">{content.audioKeyPromptTitle}</div>
            <p className="echoAudioPromptBody">{content.audioKeyPromptBody}</p>
            <div className="echoAudioPromptActions">
              <button
                className="echoTextLinkAction is-strong"
                type="button"
                onClick={handleOpenAudioKeyGuide}
              >
                {content.audioKeyPromptAction}
              </button>
              <span className="readerLabMetaChip">
                {content.audioKeyGuideSessionLabel}: {content.audioEnginePersonalKeyHint}
              </span>
            </div>
          </div>
        ) : null}

        <div className={`echoAudioGuideCard ${showAudioKeyGuide ? "is-open" : ""}`}>
          <button
            className="echoAudioGuideToggle"
            type="button"
            onClick={() => setShowAudioKeyGuide((currentValue) => !currentValue)}
            aria-expanded={showAudioKeyGuide}
          >
            <span>{content.audioKeyGuideTitle}</span>
            <span className="echoAudioGuideToggleIcon" aria-hidden="true">
              {showAudioKeyGuide ? "−" : "+"}
            </span>
          </button>

          {showAudioKeyGuide ? (
            <div className="echoAudioGuideBody">
              <p className="echoAudioGuideIntro">{content.audioKeyGuideIntro}</p>
              <ol className="echoAudioGuideList">
                <li>{content.audioKeyGuideStepOne}</li>
                <li>{content.audioKeyGuideStepTwo}</li>
                <li>{content.audioKeyGuideStepThree}</li>
              </ol>
              <div className="echoAudioGuideSessionNote">
                <strong>{content.audioKeyGuideSessionLabel}.</strong>{" "}
                {content.audioEnginePersonalKeyHint}
              </div>
            </div>
          ) : null}
        </div>

        <div className="readerLabVoiceField echoKeyCard__field">
          <label htmlFor="reader-lab-api-key">{content.audioEnginePersonalKeyLabel}</label>
          {personalApiKey.trim() && !isEditingPersonalApiKey ? (
            <div className="readerLabApiKeySavedCard">
              <div className="readerLabApiKeySavedMeta">
                <span className="readerLabMetaChip">{content.audioEngineSavedKey}</span>
                <strong className="readerLabApiKeySavedValue">{maskedPersonalApiKey}</strong>
              </div>
              <div className="readerLabApiKeySavedActions">
                <button
                  className="echoTextLinkAction"
                  type="button"
                  onClick={handleEditPersonalApiKey}
                >
                  {content.audioEngineEditKey}
                </button>
                <button
                  className="echoTextLinkAction subtle"
                  type="button"
                  onClick={handleClearPersonalApiKey}
                >
                  {content.audioEngineDeleteKey}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="readerLabVoiceFieldRow">
                <input
                  id="reader-lab-api-key"
                  ref={personalApiKeyInputRef}
                  className={`readerLabApiKeyInput ${personalApiKeyError ? "has-error" : ""}`}
                  type="password"
                  value={personalApiKeyDraft}
                  placeholder={content.audioEnginePersonalKeyPlaceholder}
                  onChange={handlePersonalApiKeyDraftChange}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleSavePersonalApiKey();
                    }
                  }}
                  autoComplete="new-password"
                  spellCheck="false"
                  autoCapitalize="none"
                  autoCorrect="off"
                  inputMode="text"
                  enterKeyHint="done"
                />
                <button
                  className="readerLabPreviewButton"
                  type="button"
                  onClick={handleSavePersonalApiKey}
                >
                  {content.audioEngineSaveKey}
                </button>
              </div>
              {personalApiKeyError ? (
                <p className="readerLabAudioConfigHint readerLabAudioConfigHintError">
                  {personalApiKeyError}
                </p>
              ) : null}
            </>
          )}
          <p className="readerLabAudioConfigHint">{content.audioEnginePersonalKeyHint}</p>
        </div>
      </section>
    );
  }
  const resolvedBookCoverImage = blocks.length
    ? lastImportedFileType === content.fileTypeEpub && bookCoverImage
      ? bookCoverImage
      : lastImportedFileName
      ? generatedFileCoverImage
      : bookCoverImage || generatedFileCoverImage
    : "";
  const currentBook = useMemo(
    () =>
      blocks.length
        ? {
            title: bookTitle || content.workspaceTitle,
            cover: resolvedBookCoverImage
          }
        : null,
    [blocks.length, bookTitle, content.workspaceTitle, resolvedBookCoverImage]
  );
  const readingWindowBlockIndex = isPureReadingMode
    ? clampIndex(pureReadingIndex, blocks.length)
    : currentIndex;
  const readingWindowPreviousBlock =
    !isPureReadingMode && readingWindowBlockIndex > 0 ? blocks[readingWindowBlockIndex - 1] : null;
  const readingWindowCurrentBlock = blocks[readingWindowBlockIndex] || null;
  const readingWindowNextBlock =
    !isPureReadingMode && readingWindowBlockIndex + 1 < blocks.length
      ? blocks[readingWindowBlockIndex + 1]
      : null;
  const readingWindowPreviousExcerpt = formatExcerpt(readingWindowPreviousBlock?.text || "", 180);
  const readingWindowCurrentExcerpt = formatReadingWindowText(readingWindowCurrentBlock?.text || "");
  const readingWindowNextExcerpt = formatExcerpt(readingWindowNextBlock?.text || "", 180);
  const currentReadingWindowOffsetChars =
    !isPureReadingMode &&
    readingWindowPlaybackCursor.chapterIndex === currentChapterIndex &&
    readingWindowPlaybackCursor.blockIndex === currentIndex
      ? readingWindowPlaybackCursor.offsetChars
      : !isPureReadingMode
      ? queuedOffsetRef.current
      : null;
  const shouldRenderSupportingTranslations = !isCompactLayout;
  const previousTranslatedExcerpt =
    shouldRenderSupportingTranslations && !isPureReadingMode && previousTranslatedBlock?.didTranslate
      ? formatExcerpt(previousTranslatedBlock.text || "", 180)
      : "";
  const currentTranslationStatus = getTranslationStatusForBlock(
    readingWindowCurrentBlock,
    translationPlaybackMode
  );
  const readingWindowCurrentTranslatedBlock = getTranslatedBlockForPlayback(
    readingWindowCurrentBlock,
    translationPlaybackMode
  );
  const currentTranslatedExcerpt =
    readingWindowCurrentTranslatedBlock?.didTranslate
      ? formatReadingWindowText(
          readingWindowCurrentTranslatedBlock.text || ""
        )
      : "";
  const currentTranslatedOffsetChars =
    currentTranslatedExcerpt &&
    readingWindowCurrentExcerpt &&
    Number.isFinite(currentReadingWindowOffsetChars)
      ? mapOffsetAcrossTexts(
          readingWindowCurrentExcerpt,
          currentTranslatedExcerpt,
          currentReadingWindowOffsetChars
        )
      : null;
  const nextTranslatedExcerpt =
    shouldRenderSupportingTranslations && !isPureReadingMode && nextTranslatedBlock?.didTranslate
      ? formatExcerpt(nextTranslatedBlock.text || "", 180)
      : "";
  const readingWindowTrackOffsetY = clampNumber(
    readingWindowUserOffsetY + readingWindowAutoOffsetY,
    -READING_WINDOW_DRAG_LIMIT_PX,
    READING_WINDOW_DRAG_LIMIT_PX
  );
  const canShiftShelfBackward = shelfStartIndex > 0;
  const canShiftShelfForward = shelfStartIndex < maxShelfStartIndex;
  const shelfWindowLabel = visibleShelfBooks.length
    ? fillTemplate(content.shelfWindowLabel, {
        start: shelfStartIndex + 1,
        end: shelfStartIndex + visibleShelfBooks.length,
        total: orderedSavedBooks.length
      })
    : "";
  const shelfStorageTotalMb = formatStorageMegabytes(shelfStorageStats.totalBudgetBytes);
  const shelfStorageUsedMb = formatStorageMegabytes(shelfStorageStats.usedBytes);
  const shelfStorageLeftMb = formatStorageMegabytes(shelfStorageStats.remainingBytes);
  const shelfStorageBooksMb = formatStorageMegabytes(shelfStorageStats.bookBytes);
  const shelfStorageUsagePercent = shelfStorageStats.totalBudgetBytes
    ? clampNumber(
        (shelfStorageStats.usedBytes / shelfStorageStats.totalBudgetBytes) * 100,
        0,
        100
      )
    : 0;
  const currentChapterCountLabel = `${content.chapterLabel} ${chapters.length ? currentChapterIndex + 1 : 0}/${chapters.length || 1}`;
  const currentBlockCountLabel = `${content.currentBlock} ${blocks.length ? readingWindowBlockIndex + 1 : 0}/${blocks.length || 1}`;
  const detectedLanguageLabel = readingWindowCurrentBlock
    ? getLanguageLabel(readingWindowCurrentBlock.language)
    : "—";
  const preferredReadingLanguageLabel = getPreferredReadingLanguageLabel(
    preferredReadingLanguage,
    content
  );
  const understandingChipLabel = `${content.understandInLabel}: ${preferredReadingLanguageLabel}`;
  const buildPureReadingDisplaySnapshot = useCallback((nextIndex) => {
    const block = blocks[nextIndex] || null;
    const translatedBlock = getTranslatedBlockForPlayback(block, translationPlaybackMode);

    return {
      chapterIndex: currentChapterIndex,
      blockIndex: nextIndex,
      showTranslated: Boolean(
        block &&
          translatedBlock?.didTranslate &&
          shouldPreferUnderstandingLanguage(block.language, preferredReadingLanguage)
      )
    };
  }, [blocks, currentChapterIndex, preferredReadingLanguage, translationPlaybackMode]);
  const currentTranslationPending = Boolean(
    currentTranslationStatus?.pendingRemote &&
      !currentTranslationStatus?.didTranslate &&
      shouldPreferUnderstandingLanguage(
        readingWindowCurrentBlock?.language,
        preferredReadingLanguage
      )
  );
  const previousReadingWindowPrefersTranslatedTop = Boolean(
    shouldRenderSupportingTranslations &&
    readingWindowPreviousBlock &&
      previousTranslatedBlock?.didTranslate &&
      shouldPreferUnderstandingLanguage(
        readingWindowPreviousBlock.language,
        preferredReadingLanguage
      )
  );
  const currentReadingWindowPrefersTranslatedTop = Boolean(
    readingWindowCurrentBlock &&
      ((isPureReadingMode &&
        readingWindowCurrentTranslatedBlock?.didTranslate) ||
        (!isPureReadingMode &&
          (readingWindowCurrentTranslatedBlock?.didTranslate ||
            currentTranslationPending))) &&
      shouldPreferUnderstandingLanguage(
        readingWindowCurrentBlock.language,
        preferredReadingLanguage
      )
  );
  const shouldLockPureReadingToSource =
    isPureReadingMode &&
    (pureReadingDisplaySnapshot.chapterIndex !== currentChapterIndex ||
      pureReadingDisplaySnapshot.blockIndex !== readingWindowBlockIndex ||
      !pureReadingDisplaySnapshot.showTranslated);
  const nextReadingWindowPrefersTranslatedTop = Boolean(
    shouldRenderSupportingTranslations &&
    readingWindowNextBlock &&
      nextTranslatedBlock?.didTranslate &&
      shouldPreferUnderstandingLanguage(readingWindowNextBlock.language, preferredReadingLanguage)
  );
  const activeBookProgressPercent = blocks.length
    ? clampNumber(((currentIndex + 1) / Math.max(blocks.length, 1)) * 100, 0, 100)
    : 0;
  const utilitySheetTitle = activeUtilitySheet === "audio"
    ? content.audioSettingsTitle
    : activeUtilitySheet === "chapters"
    ? content.chapterListTitle
    : activeUtilitySheet === "shelf"
    ? content.shelfTitle
    : activeUtilitySheet === "queue"
    ? content.queueTitle
    : activeUtilitySheet === "source"
    ? content.editorLabel
    : content.importTxt;

  useEffect(() => {
    if (!blocks.length) {
      clearReadingWindowAutoResetTimeout();
      setReadingWindowUserOffsetY(0);
      setReadingWindowAutoOffsetY(0);
      lastReadingWindowCursorRef.current = null;
      readingWindowPlaybackCursorRef.current = {
        chapterIndex: currentChapterIndex,
        blockIndex: 0,
        offsetChars: 0
      };
      setReadingWindowPlaybackCursor({
        chapterIndex: currentChapterIndex,
        blockIndex: 0,
        offsetChars: 0
      });
      return;
    }

    const nextCursor = {
      chapterIndex: currentChapterIndex,
      blockIndex: currentIndex
    };
    const previousCursor = lastReadingWindowCursorRef.current;

    if (!previousCursor) {
      lastReadingWindowCursorRef.current = nextCursor;
      return;
    }

    const cursorChanged =
      previousCursor.chapterIndex !== nextCursor.chapterIndex ||
      previousCursor.blockIndex !== nextCursor.blockIndex;

    if (!cursorChanged) {
      return;
    }

    lastReadingWindowCursorRef.current = nextCursor;

    if (isReadingWindowDragging) {
      return;
    }

    const chapterDelta = nextCursor.chapterIndex - previousCursor.chapterIndex;
    const blockDelta = nextCursor.blockIndex - previousCursor.blockIndex;
    const movementDirection = chapterDelta !== 0 ? Math.sign(chapterDelta) : Math.sign(blockDelta);

    clearReadingWindowAutoResetTimeout();
    setReadingWindowUserOffsetY(0);
    setReadingWindowAutoOffsetY(
      movementDirection >= 0 ? -READING_WINDOW_AUTO_SHIFT_PX : READING_WINDOW_AUTO_SHIFT_PX
    );

    readingWindowAutoResetTimeoutRef.current = window.setTimeout(() => {
      setReadingWindowAutoOffsetY(0);
      readingWindowAutoResetTimeoutRef.current = null;
    }, READING_WINDOW_AUTO_RESET_DELAY_MS);
  }, [
    blocks.length,
    currentChapterIndex,
    currentIndex,
    isReadingWindowDragging,
    setReadingWindowAutoOffsetY,
    setReadingWindowPlaybackCursor,
    setReadingWindowUserOffsetY
  ]);

  useEffect(() => {
    if (!isPureReadingMode) {
      setPureReadingIndex(currentIndex);
    }
  }, [currentChapterIndex, currentIndex, isPureReadingMode, setPureReadingIndex]);

  useEffect(() => {
    if (isPureReadingMode && !isPaused) {
      setIsPureReadingMode(false);
    }
  }, [isPaused, isPureReadingMode, setIsPureReadingMode]);

  useEffect(() => {
    if (!isPureReadingMode || !blocks.length) {
      return;
    }

    setPureReadingDisplaySnapshot(buildPureReadingDisplaySnapshot(pureReadingIndex));
  }, [
    blocks,
    buildPureReadingDisplaySnapshot,
    currentChapterIndex,
    isPureReadingMode,
    preferredReadingLanguage,
    pureReadingIndex,
    setPureReadingDisplaySnapshot,
    translationPlaybackMode
  ]);

  useEffect(() => {
    if (!isPureReadingMode || !blocks.length) {
      return;
    }

    const liveBlocks = blocksRef.current;
    const currentPureBlock = liveBlocks[pureReadingIndex] || null;

    if (
      !currentPureBlock ||
      !shouldPreferUnderstandingLanguage(currentPureBlock.language, preferredReadingLanguage)
    ) {
      return;
    }

    const focusBlocks = [
      liveBlocks[pureReadingIndex],
      liveBlocks[pureReadingIndex + 1],
      liveBlocks[pureReadingIndex - 1]
    ].filter(Boolean);

    void warmTranslationCache(focusBlocks, translationPlaybackMode, {
      preferRemote: true,
      awaitRemote: false,
      onRemoteUpdate: () => {
        setTranslationPlaybackVersion((currentValue) => currentValue + 1);
      }
    });
  }, [
    blocks.length,
    currentChapterText,
    isPureReadingMode,
    preferredReadingLanguage,
    pureReadingIndex,
    translationPlaybackMode
  ]);

  useEffect(() => {
    if (!isCompactLayout) {
      return;
    }

    const readingWindowNode = readingWindowRef.current;
    const currentPane = readingWindowCurrentPaneRef.current;

    if (!currentPane || !readingWindowNode) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      const containerRect = readingWindowNode.getBoundingClientRect();
      const paneRect = currentPane.getBoundingClientRect();
      const topPadding = 12;
      const bottomPadding = 20;
      const isAboveViewport = paneRect.top < containerRect.top + topPadding;
      const isBelowViewport = paneRect.bottom > containerRect.bottom - bottomPadding;

      if (!isAboveViewport && !isBelowViewport) {
        return;
      }

      const nextScrollTop = Math.max(
        0,
        currentPane.offsetTop - topPadding
      );

      readingWindowNode.scrollTo({
        top: nextScrollTop,
        behavior: "auto"
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [isCompactLayout, currentChapterIndex, currentIndex]);

  function renderReadingWindowPane({
    sourceText,
    sourceLanguage,
    translatedText,
    translatedLanguage,
    fallbackText = " ",
    tone = "current",
    preferTranslatedFirst = false,
    primaryLanguageLabel = "",
    hidePrimarySectionLabel = false,
    primarySectionOffsetChars = 0,
    paneRef = null
  }) {
    const displaySource = sourceText || fallbackText;
    const shouldShowTranslation = Boolean(translatedText);
    const sourceLabel = getLanguageLabel(sourceLanguage || "en-US");
    const translatedLabel = getLanguageLabel(translatedLanguage || "zh-CN");
    const stackedSections =
      shouldShowTranslation && preferTranslatedFirst
        ? [
            {
              key: "translation",
              label: translatedLabel,
              text: translatedText,
              primary: true
            },
            {
              key: "source",
              label: sourceLabel,
              text: displaySource,
              secondary: true
            }
          ]
        : [
            {
              key: "source",
              label: sourceLabel,
              text: displaySource,
              primary: true
            }
          ];
    const visibleCharacterCount = stackedSections.reduce(
      (total, section) => total + String(section.text || "").replace(/\s+/g, "").length,
      0
    );
    const primaryCharacterCount = String(stackedSections[0]?.text || "").replace(/\s+/g, "").length;
    let densityClass = "";

    if (tone === "current") {
      if (isPureReadingMode) {
        if (visibleCharacterCount > 1900 || primaryCharacterCount > 1220) {
          densityClass = "echo-reading-window__pane--density-micro";
        } else if (visibleCharacterCount > 1450 || primaryCharacterCount > 940) {
          densityClass = "echo-reading-window__pane--density-mini";
        } else if (visibleCharacterCount > 1120 || primaryCharacterCount > 720) {
          densityClass = "echo-reading-window__pane--density-hyper";
        } else if (visibleCharacterCount > 820 || primaryCharacterCount > 540) {
          densityClass = "echo-reading-window__pane--density-ultra";
        } else if (visibleCharacterCount > 560 || primaryCharacterCount > 360) {
          densityClass = "echo-reading-window__pane--density-dense";
        } else if (visibleCharacterCount > 320 || primaryCharacterCount > 210) {
          densityClass = "echo-reading-window__pane--density-compact";
        } else if (visibleCharacterCount > 180 || primaryCharacterCount > 120) {
          densityClass = "echo-reading-window__pane--density-medium";
        }
      } else if (visibleCharacterCount > 860 || primaryCharacterCount > 560) {
        densityClass = "echo-reading-window__pane--density-hyper";
      } else if (visibleCharacterCount > 640 || primaryCharacterCount > 420) {
        densityClass = "echo-reading-window__pane--density-ultra";
      } else if (visibleCharacterCount > 420 || primaryCharacterCount > 280) {
        densityClass = "echo-reading-window__pane--density-dense";
      } else if (visibleCharacterCount > 240 || primaryCharacterCount > 160) {
        densityClass = "echo-reading-window__pane--density-compact";
      } else if (visibleCharacterCount > 120 || primaryCharacterCount > 84) {
        densityClass = "echo-reading-window__pane--density-medium";
      }
    }

    return (
      <div
        ref={paneRef}
        className={`echo-reading-window__pane echo-reading-window__pane--${tone} ${
          stackedSections.length > 1
            ? "echo-reading-window__pane--dual-language"
            : "echo-reading-window__pane--single-language"
        } ${densityClass}`.trim()}
      >
        {primaryLanguageLabel ? (
          <span className="echo-reading-window__pane-language">{primaryLanguageLabel}</span>
        ) : null}
        {stackedSections.map((section, index) => (
          <span
            key={`${tone}-${section.key}-${index}`}
            className={`echo-reading-window__section ${
              section.primary ? "echo-reading-window__section--primary" : ""
            } ${
              section.secondary ? "echo-reading-window__section--secondary" : ""
            }`}
          >
            {!hidePrimarySectionLabel || index !== 0 ? (
              <span className="echo-reading-window__section-label">{section.label}</span>
            ) : null}
            <span
              className={`${
                section.key === "translation"
                  ? "echo-reading-window__translation-text"
                  : "echo-reading-window__source"
              }`}
            >
              {tone === "current" &&
              section.primary &&
              Number.isFinite(primarySectionOffsetChars)
                ? buildReadingWindowTextSegments(
                    section.text,
                    primarySectionOffsetChars
                  ).map((segment, segmentIndex) =>
                    segment.active ? (
                      <mark
                        key={`${tone}-${section.key}-${index}-segment-${segmentIndex}`}
                        className="echo-reading-window__active-fragment"
                      >
                        {segment.text}
                      </mark>
                    ) : (
                      <React.Fragment
                        key={`${tone}-${section.key}-${index}-segment-${segmentIndex}`}
                      >
                        {segment.text}
                      </React.Fragment>
                    )
                  )
                : section.text}
            </span>
          </span>
        ))}
      </div>
    );
  }

  function renderImportPanel({ prominent = false, inSheet = false } = {}) {
    return (
      <section
        className={`echoRailCard echoImportCard ${prominent ? "is-prominent" : ""} ${
          inSheet ? "is-sheet" : ""
        } ${dragActive ? "is-active" : ""}`}
        ref={inSheet ? null : importPanelRef}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        aria-label={dragActive ? content.dropzoneActive : content.dropzoneTitle}
      >
        <div className="echoRailCardHeader">
          <div>
            <h3 className="echoRailCardTitle">
              {prominent ? content.dropzoneTitle : content.importTxt}
            </h3>
            <p className="echoRailCardHint">
              {prominent ? content.sandboxIntro : content.dropzoneHint}
            </p>
          </div>
        </div>

        <UserDeclaration
          className={`echoUserDeclaration--reader-lab ${
            prominent ? "echoUserDeclaration--prominent" : ""
          }`}
          checked={hasAcceptedUserDeclaration}
          onAccept={() => setHasAcceptedUserDeclaration(true)}
          onCheckedChange={setHasAcceptedUserDeclaration}
        />

        <div className="echoImportActionRow">
          <button
            className={`echoImportButton ${prominent ? "primary" : ""}`}
            type="button"
            onClick={() => handleRequestFileImport({ closeSheet: inSheet })}
            disabled={!hasAcceptedUserDeclaration || isImportInteractionLocked}
          >
            {content.importTxt}
          </button>
          <button
            className="echoTextLinkAction"
            type="button"
            onClick={() => openSourceEditor({ closeSheet: inSheet })}
            disabled={!hasAcceptedUserDeclaration || isImportInteractionLocked}
          >
            {content.pasteText}
          </button>
          {!blocks.length ? (
            <button
              className="echoTextLinkAction subtle"
              type="button"
              onClick={() => {
                handleLoadDemo();
                if (inSheet) {
                  closeUtilitySheet();
                }
              }}
            >
              {content.loadDemo}
            </button>
          ) : null}
        </div>

        {lastImportedFileName ? (
          <div className="echoImportMetaRow">
            <span className="readerLabMetaChip">
              {content.lastFileLabel}: {lastImportedFileName}
            </span>
            {lastImportedFileType ? (
              <span className="readerLabMetaChip">
                {content.fileTypeLabel}: {lastImportedFileType}
              </span>
            ) : null}
          </div>
        ) : null}

        {prominent ? (
          <div className="echoImportFormats">
            {[content.fileTypeTxt, content.fileTypeEpub, content.fileTypeMobi, content.fileTypeDocx, content.fileTypeDoc, content.fileTypePdf].map((formatLabel) => (
              <span key={`import-format-${formatLabel}`} className="echoImportFormatChip">
                {formatLabel}
              </span>
            ))}
          </div>
        ) : null}
      </section>
    );
  }

  function renderCurrentBookPanel({ inSheet = false } = {}) {
    if (!currentBook) {
      return null;
    }

    return (
      <section className={`echoRailCard echoCurrentBookRailCard ${inSheet ? "is-sheet" : ""}`}>
        <div className="echoRailCardHeader">
          <div>
            <h3 className="echoRailCardTitle">{content.currentBookTitle}</h3>
            <p className="echoRailCardHint">{content.currentBookHint}</p>
          </div>
        </div>

        <div className="readerLabShelfCurrent readerLabShelfCurrent--enhanced echoCurrentBookCard">
          <button
            className="readerLabShelfCurrentCover"
            type="button"
            onClick={() => {
              if (activeBookId) {
                handleOpenSavedBook(activeBookId, { shouldAutoplay: true });
              } else {
                handlePlayToggle();
              }
            }}
            aria-label={activeBookId ? `${content.resume}: ${bookTitle || content.workspaceTitle}` : playButtonLabel}
            title={activeBookId ? `${content.resume}: ${bookTitle || content.workspaceTitle}` : playButtonLabel}
          >
            {resolvedBookCoverImage ? (
              <img
                src={resolvedBookCoverImage}
                alt={bookTitle || content.workspaceTitle}
              />
            ) : (
              <div className="readerLabShelfCurrentCoverFallback">E</div>
            )}
          </button>
          <div className="readerLabShelfCurrentBody">
            <div className="readerLabShelfCurrentTitleRow">
              <h4>{bookTitle || content.workspaceTitle}</h4>
              <span className="readerLabShelfResumeBadge">{content.resume}</span>
            </div>

            <p className="readerLabShelfCurrentMeta">
              {currentChapterCountLabel} · {currentChapterDisplayTitle || content.importedTextTitle}
            </p>
            <p className="readerLabShelfCurrentMeta">
              {currentBlockCountLabel} · {detectedLanguageLabel}
            </p>
            <p className="readerLabShelfCurrentMeta">
              {content.currentVoice}: {activeVoiceName || content.voiceAuto}
            </p>

            <div className="readerLabShelfCardProgress">
              <span
                className="readerLabShelfCardProgressFill"
                style={{ width: `${activeBookProgressPercent}%` }}
              />
            </div>
          </div>
          <div className="readerLabShelfCurrentActions">
            <button
              className="readerLabShelfButton readerLabShelfButtonPrimary"
              type="button"
              onClick={handlePlayToggle}
              disabled={!canTogglePlayback}
            >
              {playButtonLabel}
            </button>
            <button
              className="echoTextLinkAction"
              type="button"
              onClick={() => handleRequestFileImport({ closeSheet: inSheet })}
              disabled={!hasAcceptedUserDeclaration || isImportInteractionLocked}
            >
              {content.importTxt}
            </button>
          </div>
        </div>
      </section>
    );
  }

  function renderChapterListPanel({ inSheet = false, placement = "rail" } = {}) {
    const chapterListBody = (
      <div className="echoChapterList">
        {chapters.length ? (
          chapters.map((chapter, index) => {
            const paragraphCount = splitIntoBlocks(chapter.text || "").length;

            return (
              <button
                key={chapter.id || `chapter-jump-${index}`}
                className={`echoChapterListLink ${
                  index === currentChapterIndex ? "active" : ""
                }`}
                type="button"
                onClick={() => {
                  jumpToChapter(
                    index,
                    index > currentChapterIndex
                      ? content.statusChapterChanged
                      : index < currentChapterIndex
                      ? content.statusMovedPreviousChapter
                      : status
                  );

                  if (inSheet) {
                    closeUtilitySheet();
                  } else {
                    setShowChapterPicker(false);
                  }
                }}
              >
                <span className="echoChapterListLink__index">
                  {content.chapterLabel} {index + 1}
                </span>
                <span className="echoChapterListLink__title">
                  {getDisplayChapterTitle(chapter, index, {
                    chapterLabel: content.chapterLabel,
                    bookTitle,
                    fileName: lastImportedFileName,
                    workspaceTitle: content.workspaceTitle
                  })}
                </span>
                <span className="echoChapterListLink__meta">
                  {content.paragraphCount}: {paragraphCount}
                </span>
              </button>
            );
          })
        ) : (
          <div className="readerLabQueueEmpty">{content.noChapters}</div>
        )}
      </div>
    );

    if (!inSheet) {
      const isTopbarPlacement = placement === "topbar";

      return (
        <section
          className={`${
            isTopbarPlacement ? "echoChapterPickerCard echoChapterPickerCard--topbar" : "echoRailCard echoChapterPickerCard"
          }`}
          ref={chapterPickerRef}
          onPointerMove={handleChapterPopoverDragMove}
          onPointerUp={handleChapterPopoverDragEnd}
          onPointerCancel={handleChapterPopoverDragEnd}
        >
          <div className="echoChapterPickerLabel">{content.chapterPickerOpen}</div>
          <button
            className={`echoChapterPickerTrigger ${showChapterPicker ? "active" : ""}`}
            type="button"
            onClick={() => setShowChapterPicker((value) => !value)}
            aria-expanded={showChapterPicker}
            aria-label={content.chapterPickerOpen}
          >
            <span className="echoChapterPickerTrigger__title">
              {currentChapterDisplayTitle || `${content.chapterLabel} 1`}
            </span>
            <span className="echoChapterPickerTrigger__meta">{currentChapterCountLabel}</span>
            <span className="echoChapterPickerTrigger__chevron" aria-hidden="true">
              {showChapterPicker ? "−" : "+"}
            </span>
          </button>

          {showChapterPicker ? (
            <div
              className={`echoChapterPopover ${
                isTopbarPlacement ? "echoChapterPopover--topbar" : ""
              } ${isChapterPopoverDragging ? "is-dragging" : ""}`}
              style={{
                transform: `translateY(${chapterPopoverOffsetY}px)`
              }}
            >
              <div
                className={`echoRailCardHeader echoChapterPopoverHeader ${
                  isChapterPopoverDragging ? "is-dragging" : ""
                }`}
                onPointerDown={handleChapterPopoverDragStart}
              >
                <div>
                  <h3 className="echoRailCardTitle">{content.chapterListTitle}</h3>
                  <p className="echoRailCardHint">{content.chapterListHint}</p>
                </div>
              </div>
              {chapterListBody}
            </div>
          ) : null}
        </section>
      );
    }

    return (
      <section className={`echoRailCard echoChapterRailCard ${inSheet ? "is-sheet" : ""}`}>
        <div className="echoRailCardHeader">
          <div>
            <h3 className="echoRailCardTitle">{content.chapterListTitle}</h3>
            <p className="echoRailCardHint">{content.chapterListHint}</p>
          </div>
        </div>

        {chapterListBody}
      </section>
    );
  }

  function renderAudioSettingsPanel({ inSheet = false } = {}) {
    return (
      <section
        className={`echoRailCard echo-settings-card echoSettingsRailCard ${inSheet ? "is-sheet" : ""}`}
        ref={inSheet ? null : voiceSectionRef}
      >
        <div className="echo-settings-card__header">
          <div>
            <div className="echo-settings-card__title">{content.audioSettingsTitle}</div>
            <div className="echo-settings-card__subtitle">{content.audioSettingsSubtitle}</div>
          </div>

          <button
            className="echoTextLinkAction"
            type="button"
            onClick={handleToggleVoiceTuning}
          >
            {showVoiceTuning ? content.audioSettingsHide : content.audioSettingsShow}
          </button>
        </div>

        {!showVoiceTuning ? (
          <div className="echo-settings-card__summary echoSettingsSummary">
            {shouldShowAudioStatusPill ? (
              <AudioStatusPill
                mode={audioStatusMode}
                hasServerKey={!audioConfig.personalKeyRequired && audioConfig.hasServerKey}
                hasPersonalKey={Boolean(personalApiKey)}
                onOpenSettings={handleOpenAudioSettings}
              />
            ) : null}
            <div className="echoSettingsSummaryMeta">
              <span className="readerLabMetaChip">
                {content.voiceCount}: {visibleVoiceCount}
              </span>
              <span className="readerLabMetaChip">
                {content.currentVoice}: {activeVoiceName || content.voiceAuto}
              </span>
              <span className="readerLabMetaChip">{understandingChipLabel}</span>
              {compactAudioStatusMeta ? (
                <span className="readerLabMetaChip">{compactAudioStatusMeta}</span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="echo-settings-card__body">
            {renderRateControls()}
            {renderVoiceSelectors()}
          </div>
        )}
      </section>
    );
  }

  function getSavedBookSummary(book) {
    const savedChapterIndex = clampIndex(book.currentChapterIndex, book.chapters.length);
    const savedChapter = book.chapters[savedChapterIndex] || null;
    const savedBlockCount = splitIntoBlocks(savedChapter?.text || "").length;
    const savedBlockIndex = savedBlockCount
      ? clampIndex(book.currentIndex, savedBlockCount) + 1
      : 0;
    const savedProgressPercent = savedBlockCount
      ? clampNumber((savedBlockIndex / Math.max(savedBlockCount, 1)) * 100, 0, 100)
      : 0;

    return {
      savedChapterIndex,
      savedChapter,
      savedBlockCount,
      savedBlockIndex,
      savedProgressPercent
    };
  }

  function renderShelfPanel({ inSheet = false } = {}) {
    const useDesktopShelfLayout = !inSheet && !isCompactLayout;
    const desktopPrimaryBooks = useDesktopShelfLayout ? orderedSavedBooks.slice(0, 2) : [];
    const desktopCoverBooks = useDesktopShelfLayout
      ? orderedSavedBooks.slice(2, MAX_SAVED_BOOKS)
      : [];

    return (
      <section className={`echoRailCard echoShelfRailCard ${inSheet ? "is-sheet" : ""}`}>
        <div className="echoRailCardHeader">
          <div>
            <h3 className="echoRailCardTitle">{content.shelfTitle}</h3>
            <p className="echoRailCardHint">{content.shelfHint}</p>
          </div>
          <div className="readerLabShelfHeaderActions">
            <button
              className="echoTextLinkAction"
              type="button"
              onClick={() => {
                fileInputRef.current?.click?.();
                if (inSheet) {
                  closeUtilitySheet();
                }
              }}
            >
              {content.importTxt}
            </button>
            {!useDesktopShelfLayout && visibleShelfBooks.length ? (
              <span className="readerLabShelfWindowLabel">{shelfWindowLabel}</span>
            ) : null}
            <span className="readerLabShelfCount">{savedBooks.length}/{MAX_SAVED_BOOKS}</span>
          </div>
        </div>

        <div className="echoShelfStorageGrid">
          <span className="readerLabShelfStorageChip">
            <strong>{content.shelfStorageBooks}</strong>
            <span>{savedBooks.length}/{MAX_SAVED_BOOKS}</span>
          </span>
          <span className="readerLabShelfStorageChip">
            <strong>{content.shelfStorageUsed}</strong>
            <span>{shelfStorageUsedMb} MB</span>
          </span>
          <span className="readerLabShelfStorageChip">
            <strong>{content.shelfStorageTotal}</strong>
            <span>{shelfStorageTotalMb} MB</span>
          </span>
          <span className="readerLabShelfStorageChip">
            <strong>{content.shelfStorageLeft}</strong>
            <span>{shelfStorageLeftMb} MB</span>
          </span>
        </div>

        <div className="readerLabShelfStorageBar" aria-hidden="true">
          <span
            className="readerLabShelfStorageBarFill"
            style={{ width: `${shelfStorageUsagePercent}%` }}
          />
        </div>

        {savedBooks.length ? (
          useDesktopShelfLayout ? (
            <div className="readerLabShelfDesktopLayout">
              <div className="readerLabShelfPrimaryGrid">
                {desktopPrimaryBooks.map((book, index) => {
                  const {
                    savedChapterIndex,
                    savedChapter,
                    savedBlockCount,
                    savedBlockIndex,
                    savedProgressPercent
                  } = getSavedBookSummary(book);

                  return (
                    <article
                      key={book.id}
                      className={`readerLabShelfCard readerLabShelfCard--library readerLabShelfCard--primary ${
                        book.id === activeBookId ? "active" : ""
                      }`}
                    >
                      <button
                        className="readerLabShelfCardCoverWrap"
                        type="button"
                        onClick={() => handleOpenSavedBook(book.id, { shouldAutoplay: true })}
                        aria-label={`${content.resume}: ${book.bookTitle}`}
                        title={`${content.resume}: ${book.bookTitle}`}
                      >
                        {book.coverImage ? (
                          <img
                            className="readerLabShelfCardCover"
                            src={book.coverImage}
                            alt={book.bookTitle}
                          />
                        ) : (
                          <div className="readerLabShelfCardCover readerLabShelfCardCoverFallback">
                            {(book.bookTitle || "E").trim().slice(0, 1).toUpperCase()}
                          </div>
                        )}
                      </button>

                      <div className="readerLabShelfCardBody">
                        <div className="readerLabShelfCardHeader">
                          <div
                            className={`readerLabShelfSlotLabel ${
                              index === 0 ? "is-playing" : "is-up-next"
                            }`}
                          >
                            {index === 0 ? content.shelfNowPlaying : content.shelfUpNext}
                          </div>
                          <div className="readerLabShelfCardHeaderTitleRow">
                            <h4 title={book.bookTitle}>{book.bookTitle}</h4>
                            {book.fileType ? (
                              <span className="readerLabMetaChip">{book.fileType}</span>
                            ) : null}
                          </div>
                        </div>

                        {book.lastImportedFileName ? (
                          <p className="readerLabShelfCardFile" title={book.lastImportedFileName}>
                            {book.lastImportedFileName}
                          </p>
                        ) : null}

                        <p className="readerLabShelfCardMeta">
                          {content.chapterLabel} {savedChapterIndex + 1}/{book.chapters.length} ·{" "}
                          {savedChapter?.title || content.importedTextTitle}
                        </p>
                        <p className="readerLabShelfCardMeta">
                          {content.currentBlock} {savedBlockIndex}/{savedBlockCount || 1}
                        </p>

                        <div className="readerLabShelfCardProgress">
                          <span
                            className="readerLabShelfCardProgressFill"
                            style={{ width: `${savedProgressPercent}%` }}
                          />
                        </div>

                        <div className="readerLabShelfCardActions readerLabShelfCardActions--links">
                          <button
                            className={`echoTextLinkAction ${
                              book.id === activeBookId ? "is-strong" : ""
                            }`}
                            type="button"
                            onClick={() => handleOpenSavedBook(book.id)}
                          >
                            {book.id === activeBookId ? content.resume : content.shelfOpen}
                          </button>
                          <button
                            className="echoTextLinkAction subtle"
                            type="button"
                            onClick={() => handleDeleteSavedBook(book.id)}
                          >
                            {content.shelfDelete}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="readerLabShelfCoverStrip">
                {desktopCoverBooks.map((book) => (
                  <button
                    key={book.id}
                    className={`readerLabShelfCoverTile ${
                      book.id === activeBookId ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => handleOpenSavedBook(book.id, { shouldAutoplay: true })}
                    aria-label={`${content.resume}: ${book.bookTitle}`}
                    title={book.bookTitle}
                  >
                    {book.coverImage ? (
                      <img
                        className="readerLabShelfCoverTileImage"
                        src={book.coverImage}
                        alt={book.bookTitle}
                      />
                    ) : (
                      <div className="readerLabShelfCoverTileImage readerLabShelfCardCoverFallback">
                        {(book.bookTitle || "E").trim().slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="readerLabShelfGrid readerLabShelfGrid--library">
                {visibleShelfBooks.map((book) => {
                  const {
                    savedChapterIndex,
                    savedChapter,
                    savedBlockCount,
                    savedBlockIndex,
                    savedProgressPercent
                  } = getSavedBookSummary(book);

                  return (
                    <article
                      key={book.id}
                      className={`readerLabShelfCard readerLabShelfCard--library ${
                        book.id === activeBookId ? "active" : ""
                      }`}
                    >
                      <button
                        className="readerLabShelfCardCoverWrap"
                        type="button"
                        onClick={() => handleOpenSavedBook(book.id, { shouldAutoplay: true })}
                        aria-label={`${content.resume}: ${book.bookTitle}`}
                        title={`${content.resume}: ${book.bookTitle}`}
                      >
                        {book.coverImage ? (
                          <img
                            className="readerLabShelfCardCover"
                            src={book.coverImage}
                            alt={book.bookTitle}
                          />
                        ) : (
                          <div className="readerLabShelfCardCover readerLabShelfCardCoverFallback">
                            {(book.bookTitle || "E").trim().slice(0, 1).toUpperCase()}
                          </div>
                        )}
                      </button>

                      <div className="readerLabShelfCardBody">
                        <div className="readerLabShelfCardHeader">
                          <h4 title={book.bookTitle}>{book.bookTitle}</h4>
                          {book.fileType ? (
                            <span className="readerLabMetaChip">{book.fileType}</span>
                          ) : null}
                        </div>

                        {book.lastImportedFileName ? (
                          <p className="readerLabShelfCardFile" title={book.lastImportedFileName}>
                            {book.lastImportedFileName}
                          </p>
                        ) : null}

                        <p className="readerLabShelfCardMeta">
                          {content.chapterLabel} {savedChapterIndex + 1}/{book.chapters.length} ·{" "}
                          {savedChapter?.title || content.importedTextTitle}
                        </p>
                        <p className="readerLabShelfCardMeta">
                          {content.currentBlock} {savedBlockIndex}/{savedBlockCount || 1}
                        </p>

                        <div className="readerLabShelfCardProgress">
                          <span
                            className="readerLabShelfCardProgressFill"
                            style={{ width: `${savedProgressPercent}%` }}
                          />
                        </div>

                        <div className="readerLabShelfCardActions readerLabShelfCardActions--links">
                          <button
                            className={`echoTextLinkAction ${
                              book.id === activeBookId ? "is-strong" : ""
                            }`}
                            type="button"
                            onClick={() => handleOpenSavedBook(book.id)}
                          >
                            {book.id === activeBookId ? content.resume : content.shelfOpen}
                          </button>
                          <button
                            className="echoTextLinkAction subtle"
                            type="button"
                            onClick={() => handleDeleteSavedBook(book.id)}
                          >
                            {content.shelfDelete}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="readerLabShelfWindowControls readerLabShelfWindowControlsRail">
                <button
                  className="readerLabShelfArrow"
                  type="button"
                  onClick={handleShelfWindowPrevious}
                  disabled={!canShiftShelfBackward}
                  aria-label={content.shelfPreviousWindow}
                  title={content.shelfPreviousWindow}
                >
                  ←
                </button>
                <button
                  className="readerLabShelfArrow"
                  type="button"
                  onClick={handleShelfWindowNext}
                  disabled={!canShiftShelfForward}
                  aria-label={content.shelfNextWindow}
                  title={content.shelfNextWindow}
                >
                  →
                </button>
              </div>
            </>
          )
        ) : (
          <div className="readerLabShelfEmpty">{content.shelfEmpty}</div>
        )}

        <p className="readerLabShelfLimit">
          {content.shelfStorageNote} {content.shelfStorageBooks}: {shelfStorageBooksMb} MB.
        </p>
      </section>
    );
  }

  function renderQueuePanel({ inSheet = false } = {}) {
    const isExpanded = inSheet || showQueuePanel;

    return (
      <section
        className={`echoRailCard echoQueueRailCard ${inSheet ? "is-sheet" : ""} ${
          isExpanded ? "is-expanded" : "is-collapsed"
        }`}
      >
        <div className="echoRailCardHeader">
          <div>
            <h3 className="echoRailCardTitle">{content.queueTitle}</h3>
            <p className="echoRailCardHint">
              {isExpanded ? content.clickToJump : content.queueSummary}
            </p>
          </div>
          {!inSheet ? (
            <button
              className="echoTextLinkAction"
              type="button"
              onClick={() => setShowQueuePanel((value) => !value)}
            >
              {isExpanded ? content.queueHide : content.queueShow}
            </button>
          ) : null}
        </div>

        {isExpanded ? (
          <div className="readerLabQueueList readerLabInlineQueueList" ref={inlineQueueRef}>
            {blocks.length ? (
              blocks.map((block, index) => (
                <button
                  key={block.id}
                  className={`readerLabQueueItem ${index === currentIndex ? "active" : ""}`}
                  onClick={() => {
                    handleSelectBlock(index);
                    if (inSheet) {
                      closeUtilitySheet();
                    }
                  }}
                >
                  <span className="readerLabQueueIndex">{index + 1}</span>
                  <span className="readerLabQueueBody">
                    <span className="readerLabQueueText">{block.text}</span>
                    <span className="readerLabQueueLang">{getLanguageLabel(block.language)}</span>
                  </span>
                </button>
              ))
            ) : (
              <div className="readerLabQueueEmpty">{content.noBlocks}</div>
            )}
          </div>
        ) : null}
      </section>
    );
  }

  function renderSourceEditor() {
    return (
      <section className="echoRailCard echoSourceCard" ref={sourceDetailsRef}>
        <div className="echoRailCardHeader">
          <div>
            <h3 className="echoRailCardTitle">{content.editorLabel}</h3>
            <p className="echoRailCardHint">{content.editorPlaceholder}</p>
          </div>
        </div>

        <textarea
          id="reader-lab-input"
          ref={sourceTextareaRef}
          className="readerLabTextarea"
          value={readerTextValue}
          onChange={handleTextChange}
          placeholder={content.editorPlaceholder}
          spellCheck="false"
        />
        <div className="echoSourceEditorActions">
          <button className="echoTextLinkAction subtle" type="button" onClick={handleClear}>
            {content.clearText}
          </button>
        </div>
      </section>
    );
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isPlaying || isPaused) {
      const model = getCurrentPlaybackModel();
      const elapsedSeconds = clampNumber(getCurrentEstimatedElapsedSeconds(), 0, model.totalSeconds);

      playbackProgressRef.current.totalSeconds = model.totalSeconds;
      if (!playbackProgressRef.current.segmentStartTimeMs) {
        playbackProgressRef.current.segmentStartSeconds = elapsedSeconds;
      }
      updateProgressSnapshot(elapsedSeconds, model.totalSeconds);
      return;
    }

    if (
      status === content.statusFinished ||
      status === content.statusSleepChapterTriggered
    ) {
      return;
    }

    syncProgressToCursor(currentIndex, queuedOffsetRef.current);
  }, [
    currentChapterText,
    blocks.length,
    currentIndex,
    currentChapterIndex,
    rate,
    isPlaying,
    isPaused,
    status,
    content.statusFinished,
    content.statusSleepChapterTriggered
  ]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return undefined;
    }

    const handlePlaying = () => {
      clearRecoveryTimeout();
      clearManualSeekGuard();
      shouldRecoverPlaybackRef.current = false;
      recoveryAttemptRef.current = 0;

      if (playbackIntentRef.current && !isPausedRef.current) {
        pauseOriginRef.current = "none";
        resumeProgressTrackingRef.current();
        setIsPlaying(true);
        setIsPaused(false);
        setPlaybackPhaseState("playing", {
          source: "audio-playing",
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        requestWakeLock();
        warmUpcomingChapterAudio(currentChapterIndexRef.current, "audio-playing-start");
        persistPlaybackSnapshotIfNeeded(
          "audio-playing",
          {
            phase: "playing",
            audioMode: "ai-audio",
            pauseOrigin: "none",
            userPaused: false,
            shouldResume: true,
            audioCacheKey: String(audio.dataset.cacheKey || ""),
            audioStreamUrl: getPersistableAudioStreamUrl(
              audio,
              String(audio.dataset.cacheKey || "")
            )
          },
          { force: true }
        );
      }
    };

    const handlePause = () => {
      if (audioPauseGuardCountRef.current > 0 || audio.ended) {
        return;
      }

      if (isManualSeekActive()) {
        debugLog("audio-pause-during-manual-seek", {
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        return;
      }

      freezeProgressTrackingRef.current();

      if (playbackIntentRef.current && !isPausedRef.current) {
        pauseOriginRef.current = "system";
        shouldRecoverPlaybackRef.current = true;
        setPlaybackPhaseState("pausedBySystem", {
          source: "audio-pause",
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        setStatus(content.statusBuffering);
        persistPlaybackSnapshotIfNeeded(
          "audio-pause",
          {
            phase: "pausedBySystem",
            audioMode: "ai-audio",
            pauseOrigin: "system",
            userPaused: false,
            shouldResume: true,
            audioCacheKey: String(audio.dataset.cacheKey || ""),
            audioStreamUrl: getPersistableAudioStreamUrl(
              audio,
              String(audio.dataset.cacheKey || "")
            )
          },
          { force: true }
        );
        schedulePlaybackRecovery();
        return;
      }

      setIsPlaying(false);
    };

    const handleWaiting = () => {
      if (!playbackIntentRef.current || isPausedRef.current) {
        return;
      }

      if (isManualSeekActive()) {
        return;
      }

      freezeProgressTrackingRef.current();
      pauseOriginRef.current = "system";
      shouldRecoverPlaybackRef.current = true;
      setPlaybackPhaseState("loading", {
        source: "audio-waiting",
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current
      });
      setStatus(content.statusBuffering);
    };

    const handleCanPlay = () => {
      if (isManualSeekActive()) {
        void settleManualSeek("audio-canplay");
        return;
      }

      if (shouldRecoverPlaybackRef.current && playbackIntentRef.current && !isPausedRef.current) {
        debugLog("audio-canplay-recover", {
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        schedulePlaybackRecovery(120);
      }
    };

    const handleStalled = () => {
      if (!playbackIntentRef.current || isPausedRef.current) {
        return;
      }

      if (isManualSeekActive()) {
        return;
      }

      freezeProgressTrackingRef.current();
      pauseOriginRef.current = "system";
      shouldRecoverPlaybackRef.current = true;
      setPlaybackPhaseState("pausedBySystem", {
        source: "audio-stalled",
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current
      });
      setStatus(content.statusBuffering);
      persistPlaybackSnapshotIfNeeded(
        "audio-stalled",
        {
          phase: "pausedBySystem",
          audioMode: "ai-audio",
          pauseOrigin: "system",
          userPaused: false,
          shouldResume: true,
          audioCacheKey: String(audio.dataset.cacheKey || ""),
          audioStreamUrl: getPersistableAudioStreamUrl(
            audio,
            String(audio.dataset.cacheKey || "")
          )
        },
        { force: true }
      );
      schedulePlaybackRecovery(RECOVERY_RETRY_DELAYS_MS[1]);
    };

    const handleSuspend = () => {
      if (
        !playbackIntentRef.current ||
        isPausedRef.current ||
        playbackPhaseRef.current === "loading" ||
        playbackPhaseRef.current === "transitioning" ||
        !audio.paused ||
        audio.ended
      ) {
        return;
      }

      if (isManualSeekActive()) {
        return;
      }

      const totalSeconds =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : chapterTimingRef.current.totalSeconds;
      const remainingSeconds = Math.max(0, totalSeconds - (audio.currentTime || 0));

      if (remainingSeconds <= 0.4) {
        return;
      }

      freezeProgressTrackingRef.current();
      pauseOriginRef.current = "system";
      shouldRecoverPlaybackRef.current = true;
      setPlaybackPhaseState("pausedBySystem", {
        source: "audio-suspend",
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current
      });
      setStatus(content.statusBuffering);
      persistPlaybackSnapshotIfNeeded(
        "audio-suspend",
        {
          phase: "pausedBySystem",
          audioMode: "ai-audio",
          pauseOrigin: "system",
          userPaused: false,
          shouldResume: true,
          audioCacheKey: String(audio.dataset.cacheKey || ""),
          audioStreamUrl: getPersistableAudioStreamUrl(
            audio,
            String(audio.dataset.cacheKey || "")
          )
        },
        { force: true }
      );
      schedulePlaybackRecovery(RECOVERY_RETRY_DELAYS_MS[1]);
    };

    const handleEmptied = () => {
      if (
        !playbackIntentRef.current ||
        isPausedRef.current ||
        playbackPhaseRef.current === "loading" ||
        playbackPhaseRef.current === "transitioning"
      ) {
        return;
      }

      if (isManualSeekActive()) {
        return;
      }

      freezeProgressTrackingRef.current();
      pauseOriginRef.current = "system";
      shouldRecoverPlaybackRef.current = true;
      setPlaybackPhaseState("loading", {
        source: "audio-emptied",
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current
      });
      setStatus(content.statusBuffering);
      persistPlaybackSnapshotIfNeeded(
        "audio-emptied",
        {
          phase: "pausedBySystem",
          audioMode: "ai-audio",
          pauseOrigin: "system",
          userPaused: false,
          shouldResume: true,
          audioCacheKey: String(audio.dataset.cacheKey || ""),
          audioStreamUrl: getPersistableAudioStreamUrl(
            audio,
            String(audio.dataset.cacheKey || "")
          )
        },
        { force: true }
      );
      schedulePlaybackRecovery(RECOVERY_RETRY_DELAYS_MS[0]);
    };

    const handleTimeUpdate = () => {
      if (!playbackIntentRef.current || isPausedRef.current || audio.ended) {
        return;
      }

      const totalSeconds =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : chapterTimingRef.current.totalSeconds;

      persistPlaybackSnapshotIfNeeded("audio-timeupdate", {
        phase: "playing",
        audioMode: "ai-audio",
        pauseOrigin: "none",
        userPaused: false,
        shouldResume: true,
        audioCacheKey: String(audio.dataset.cacheKey || ""),
        audioStreamUrl: getPersistableAudioStreamUrl(
          audio,
          String(audio.dataset.cacheKey || "")
        ),
        elapsedSeconds: clampNumber(audio.currentTime || 0, 0, totalSeconds || 0),
        totalSeconds
      });
    };

    const handleSeeked = () => {
      if (isManualSeekActive()) {
        void settleManualSeek("audio-seeked");
      }
    };

    const handleEnded = () => {
      stopProgressTicker();
      clearRecoveryTimeout();
      clearManualSeekGuard();
      shouldRecoverPlaybackRef.current = false;
      recoveryAttemptRef.current = 0;
      setIsPlaying(false);
      setIsPaused(false);
      queuedOffsetRef.current = 0;
      releaseWakeLock();

      const totalSeconds =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : chapterTimingRef.current.totalSeconds;

      updateProgressSnapshot(totalSeconds, totalSeconds);
      debugLog("chapter-ended", {
        chapterIndex: currentChapterIndexRef.current,
        totalSeconds
      });

      if (sleepModeRef.current === "chapter") {
        playbackIntentRef.current = false;
        pauseOriginRef.current = "none";
        setPlaybackPhaseState("ended", {
          source: "sleep-chapter-end",
          chapterIndex: currentChapterIndexRef.current
        });
        cancelSleepTimer();
        setStatus(content.statusSleepChapterTriggered);
        return;
      }

      if (
        playbackIntentRef.current &&
        currentChapterIndexRef.current < chaptersRef.current.length - 1
      ) {
        const nextChapterIndex = currentChapterIndexRef.current + 1;
        const nextChapterKey = getAudioCacheKeyForChapter(nextChapterIndex);
        const nextChapterTiming = chapterAudioCacheRef.current.get(nextChapterKey);

        setPlaybackPhaseState("transitioning", {
          source: "chapter-ended",
          chapterIndex: nextChapterIndex
        });
        persistPlaybackSnapshotIfNeeded(
          "chapter-ended-transition",
          {
            phase: "transitioning",
            audioMode: "ai-audio",
            pauseOrigin: "system",
            shouldResume: true,
            chapterIndex: nextChapterIndex,
            blockIndex: 0,
            offsetChars: 0,
            elapsedSeconds: 0,
            totalSeconds: nextChapterTiming?.totalSeconds || 0
          },
          { force: true }
        );
        transitionToChapterPlayback(nextChapterIndex, "chapter-ended").catch(() => {
          jumpToChapter(nextChapterIndex, content.statusBuffering);
        });
        return;
      }

      playbackIntentRef.current = false;
      pauseOriginRef.current = "none";
      setPlaybackPhaseState("ended", {
        source: "chapter-ended-final",
        chapterIndex: currentChapterIndexRef.current
      });
      setStatus(content.statusFinished);
      persistPlaybackSnapshotIfNeeded(
        "chapter-ended-final",
        {
          phase: "ended",
          audioMode: "ai-audio",
          pauseOrigin: "none",
          shouldResume: false
        },
        { force: true }
      );
    };

    const handleError = () => {
      freezeProgressTracking();
      releaseWakeLock();

      if (audio.dataset.directStream === "1") {
        const playbackModel = getCurrentPlaybackModel();
        const currentTarget = findSeekTarget(
          playbackModel,
          blocksRef.current,
          clampNumber(audio.currentTime || getCurrentEstimatedElapsedSeconds(), 0, playbackModel.totalSeconds)
        );

        queuedOffsetRef.current = currentTarget.offsetChars;
        currentIndexRef.current = currentTarget.blockIndex;
        setCurrentIndex(currentTarget.blockIndex);

        runWithAudioPauseGuard(() => {
          audio.pause();
          audio.removeAttribute("src");
          delete audio.dataset.cacheKey;
          delete audio.dataset.directStream;
          audio.load();
        });
      }

      if (playbackIntentRef.current && !isPausedRef.current) {
        pauseOriginRef.current = "system";
        shouldRecoverPlaybackRef.current = true;
        setPlaybackPhaseState("pausedBySystem", {
          source: "audio-error",
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current
        });
        setStatus(content.statusBuffering);
        persistPlaybackSnapshotIfNeeded(
          "audio-error",
          {
            phase: "pausedBySystem",
            audioMode: "ai-audio",
            pauseOrigin: "system",
            userPaused: false,
            shouldResume: true
          },
          { force: true }
        );
        schedulePlaybackRecovery(RECOVERY_RETRY_DELAYS_MS[1]);
        return;
      }

      setIsPlaying(false);
      setIsPaused(false);
      setPlaybackPhaseState("idle", {
        source: "audio-error-idle",
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current
      });
      setStatus(content.statusError);
    };

    audio.addEventListener("playing", handlePlaying);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("canplaythrough", handleCanPlay);
    audio.addEventListener("seeked", handleSeeked);
    audio.addEventListener("stalled", handleStalled);
    audio.addEventListener("suspend", handleSuspend);
    audio.addEventListener("emptied", handleEmptied);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("playing", handlePlaying);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("canplaythrough", handleCanPlay);
      audio.removeEventListener("seeked", handleSeeked);
      audio.removeEventListener("stalled", handleStalled);
      audio.removeEventListener("suspend", handleSuspend);
      audio.removeEventListener("emptied", handleEmptied);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [
    content.statusChapterChanged,
    content.statusBuffering,
    content.statusError,
    content.statusFinished,
    content.statusSleepChapterTriggered
  ]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
      return;
    }

    if (typeof window !== "undefined" && "MediaMetadata" in window) {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: bookTitle || content.workspaceTitle,
        artist: currentChapterDisplayTitle || content.importedTextTitle,
        album: "PageTone",
        artwork: resolvedBookCoverImage
          ? [
              {
                src: resolvedBookCoverImage
              }
            ]
          : undefined
      });
      debugLog("media-session-metadata", {
        title: bookTitle || content.workspaceTitle,
        chapterTitle: currentChapterDisplayTitle || content.importedTextTitle
      });
    }

    navigator.mediaSession.playbackState =
      isPlaying && !isPaused ? "playing" : "paused";

    const setHandler = (action, handler) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch (_error) {
        // Some platforms do not support all action handlers.
      }
    };

    setHandler("play", () => {
      if (!isPlaying || isPaused) {
        handlePlayToggle();
      }
    });
    setHandler("pause", () => {
      if (isPlaying && !isPaused) {
        handlePlayToggle();
      }
    });
    setHandler("seekto", (details) => {
      if (details && Number.isFinite(details.seekTime)) {
        seekToElapsedSeconds(details.seekTime);
      }
    });
    setHandler("seekbackward", () => handleSkipBySeconds(-15));
    setHandler("seekforward", () => handleSkipBySeconds(15));
    setHandler(
      "previoustrack",
      aiAudioAvailable && hasPreviousChapter ? handlePreviousChapter : null
    );
    setHandler(
      "nexttrack",
      aiAudioAvailable && hasNextChapter ? handleNextChapter : null
    );

    return () => {
      setHandler("play", null);
      setHandler("pause", null);
      setHandler("seekto", null);
      setHandler("seekbackward", null);
      setHandler("seekforward", null);
      setHandler("previoustrack", null);
      setHandler("nexttrack", null);
    };
  }, [
    bookTitle,
    content.importedTextTitle,
    content.workspaceTitle,
    currentChapterDisplayTitle,
    resolvedBookCoverImage,
    handleNextChapter,
    handlePreviousChapter,
    hasNextChapter,
    hasPreviousChapter,
    isPaused,
    isPlaying,
    aiAudioAvailable
  ]);

  useEffect(() => {
    if (
      typeof navigator === "undefined" ||
      !("mediaSession" in navigator) ||
      typeof navigator.mediaSession.setPositionState !== "function" ||
      !progressState.totalSeconds
    ) {
      return;
    }

    try {
      navigator.mediaSession.setPositionState({
        duration: progressState.totalSeconds,
        playbackRate: rate,
        position: clampNumber(
          progressState.elapsedSeconds,
          0,
          progressState.totalSeconds
        )
      });
    } catch (_error) {
      // Some platforms reject position metadata during startup or on unsupported media stacks.
    }
  }, [
    isPaused,
    isPlaying,
    progressState.elapsedSeconds,
    progressState.totalSeconds,
    rate
  ]);

  useEffect(() => {
    if (isPlaying && !isPaused) {
      requestWakeLock();
      return undefined;
    }

    releaseWakeLock();
    return undefined;
  }, [isPaused, isPlaying]);

  useEffect(() => {
    nearEndPrefetchKeyRef.current = "";
    chapterStartPrefetchKeyRef.current = "";
  }, [currentChapterAudioKey]);

  useEffect(() => {
    const shouldWarmCurrentChapter =
      isVisible ||
      playbackIntentRef.current ||
      isPlayingRef.current ||
      Boolean(restorePlaybackSnapshotRef.current);

    if (!shouldWarmCurrentChapter || !aiAudioAvailable || !supportsAudio || !blocks.length) {
      return undefined;
    }

    let cancelled = false;
    let idleHandle = null;

    const warmCurrentChapter = () => {
      if (cancelled || chapterAudioCacheRef.current.has(currentChapterAudioKey)) {
        return;
      }

      ensureChapterAudioLoaded(currentChapterIndex, {
        prefetch: true,
        source: "warm-current"
      }).catch(() => {});
    };

    if (typeof window.requestIdleCallback === "function") {
      idleHandle = window.requestIdleCallback(warmCurrentChapter, { timeout: 1200 });
    } else {
      idleHandle = window.setTimeout(warmCurrentChapter, 400);
    }

    return () => {
      cancelled = true;

      if (typeof window.cancelIdleCallback === "function" && typeof idleHandle === "number") {
        window.cancelIdleCallback(idleHandle);
        return;
      }

      if (idleHandle) {
        window.clearTimeout(idleHandle);
      }
    };
  }, [
    aiAudioAvailable,
    blocks.length,
    currentChapterAudioKey,
    currentChapterIndex,
    isVisible,
    supportsAudio
  ]);

  useEffect(() => {
    const snapshot = restorePlaybackSnapshotRef.current;

    if (
      restoreWarmupDoneRef.current ||
      !snapshot ||
      snapshot.audioMode !== "ai-audio" ||
      !aiAudioAvailable ||
      !supportsAudio ||
      !blocks.length
    ) {
      return undefined;
    }

    restoreWarmupDoneRef.current = true;
    let cancelled = false;
    const snapshotEntry = hydrateSnapshotAudioEntry(snapshot, "restore-warm");

    const restorePromise = snapshotEntry
      ? Promise.resolve(snapshotEntry).catch(() => null)
      : ensureChapterAudioLoaded(currentChapterIndexRef.current, {
          prefetch: true,
          source: "restore-warm"
        });

    restorePromise
      .then((entry) => {
        if (cancelled) {
          return null;
        }

        if (!entry) {
          return ensureChapterAudioLoaded(currentChapterIndexRef.current, {
            prefetch: true,
            source: "restore-warm-refresh"
          });
        }

        if (audioRef.current?.dataset.cacheKey === entry.key) {
          return entry;
        }

        return attachChapterAudio(entry, snapshot.elapsedSeconds).catch(() =>
          ensureChapterAudioLoaded(currentChapterIndexRef.current, {
            prefetch: true,
            source: "restore-warm-refresh"
          }).then((refreshedEntry) => attachChapterAudio(refreshedEntry, snapshot.elapsedSeconds))
        );
      })
      .then(() => {
        if (cancelled) {
          return;
        }

        freezeProgressTracking();
        restorePlaybackSnapshotRef.current = null;
        debugLog("restore-warm-ready", {
          chapterIndex: currentChapterIndexRef.current,
          elapsedSeconds: snapshot.elapsedSeconds,
          restoredFromSnapshot: Boolean(snapshotEntry)
        });
        warmUpcomingChapterAudio(currentChapterIndexRef.current, "restore-warm-ahead");
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        if (canUseBrowserSpeechFallback(error)) {
          suppressAiPlaybackForSession(error, "restore-warm");
        }
        restoreWarmupDoneRef.current = false;
        restorePlaybackSnapshotRef.current = null;
        debugLog("restore-warm-failed", {
          chapterIndex: currentChapterIndexRef.current,
          message: error instanceof Error ? error.message : "Unknown restore warmup error."
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    aiAudioAvailable,
    blocks.length,
    currentChapterAudioKey,
    supportsAudio
  ]);

  useEffect(() => {
    if (
      !aiAudioAvailable ||
      !supportsAudio ||
      !isPlaying ||
      isPaused ||
      !progressState.totalSeconds
    ) {
      return;
    }

    const remainingSeconds = progressState.totalSeconds - progressState.elapsedSeconds;
    const prefetchKey = `${currentChapterAudioKey}:${currentChapterIndex}`;
    const prefetchThreshold = getNearEndPrefetchThreshold(progressState.totalSeconds);

    if (
      remainingSeconds <= prefetchThreshold &&
      nearEndPrefetchKeyRef.current !== prefetchKey
    ) {
      nearEndPrefetchKeyRef.current = prefetchKey;
      prefetchUpcomingChapters(currentChapterIndex, BACKGROUND_PREFETCH_AHEAD, {
        source: "near-end-prefetch"
      })
        .then((didPrefetchAll) => {
          if (!didPrefetchAll && nearEndPrefetchKeyRef.current === prefetchKey) {
            nearEndPrefetchKeyRef.current = "";
            debugLog("chapter-prefetch-retry-armed", {
              source: "near-end-prefetch",
              chapterIndex: currentChapterIndex
            });
          }
        })
        .catch(() => {
          if (nearEndPrefetchKeyRef.current === prefetchKey) {
            nearEndPrefetchKeyRef.current = "";
          }
        });
    }
  }, [
    aiAudioAvailable,
    currentChapterAudioKey,
    currentChapterIndex,
    isPaused,
    isPlaying,
    progressState.elapsedSeconds,
    progressState.totalSeconds,
    supportsAudio
  ]);

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    const persistForLifecycle = (source) => {
      const browserSpeechSnapshotActive =
        supportsSpeechFallback &&
        !aiAudioAvailable &&
        playbackIntentRef.current &&
        !isPausedRef.current;
      const speechChunkPosition = findSpeechChunkPosition(
        browserSpeechChunksRef.current,
        currentIndexRef.current,
        queuedOffsetRef.current
      );

      if (playbackIntentRef.current && !isPausedRef.current) {
        freezeProgressTrackingRef.current();
      }

      const snapshot = persistPlaybackSnapshotIfNeeded(
        `lifecycle-${source}`,
        {
          phase:
            playbackIntentRef.current && !isPausedRef.current
              ? "pausedBySystem"
              : playbackPhaseRef.current,
          pauseOrigin:
            playbackIntentRef.current && !isPausedRef.current
              ? "system"
              : pauseOriginRef.current,
          shouldResume: playbackIntentRef.current && !isPausedRef.current,
          backgroundInterrupted: browserSpeechSnapshotActive,
          speechChunkIndex: speechChunkPosition.chunkIndex,
          visibleExcerptContext: buildVisibleExcerptContext(
            blocksRef.current,
            currentIndexRef.current
          )
        },
        { force: true }
      );

      debugLog("lifecycle-persist", {
        source,
        snapshot
      });
    };

    const handleForegroundReturn = (source) => {
      reconcileForegroundPlayback(source);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        const usingBrowserSpeech = supportsSpeechFallback && !aiAudioAvailable && playbackIntentRef.current;

        debugLog("background-hidden", {
          chapterIndex: currentChapterIndexRef.current,
          blockIndex: currentIndexRef.current,
          audioLoaded: hasCurrentChapterLoadedAudio(),
          elapsedSeconds: getCurrentEstimatedElapsedSeconds(),
          usingBrowserSpeech
        });
        if (playbackIntentRef.current && aiAudioAvailable) {
          warmUpcomingChapterAudio(currentChapterIndexRef.current, "visibility-hidden");
        }
        if (usingBrowserSpeech) {
          browserSpeechWasHiddenRef.current = true;
          setIsPlaying(false);
          setPlaybackPhaseState("pausedBySystem", {
            source: "visibility-hidden-speech",
            chapterIndex: currentChapterIndexRef.current,
            blockIndex: currentIndexRef.current
          });
        }

        persistForLifecycle("visibility-hidden");
        releaseWakeLock();
        return;
      }

      debugLog("background-visible", {
        chapterIndex: currentChapterIndexRef.current,
        blockIndex: currentIndexRef.current
      });
      handleForegroundReturn("visibility-visible");
    };

    const handlePageHide = () => {
      if (supportsSpeechFallback && !aiAudioAvailable && playbackIntentRef.current) {
        browserSpeechWasHiddenRef.current = true;
      }
      persistForLifecycle("pagehide");
      releaseWakeLock();
    };

    const handlePageShow = () => {
      handleForegroundReturn("pageshow");
    };

    const handleFocus = () => {
      handleForegroundReturn("focus");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("focus", handleFocus);
    };
  }, [
    aiAudioAvailable,
    currentChapterAudioKey,
    supportsAudio
  ]);

  useEffect(() => {
    if (!onPlayerStateChange) {
      return;
    }

    onPlayerStateChange({
      hasPlayableContent: blocks.length > 0,
      isPlaying,
      isPaused,
      playbackPhase,
      playButtonLabel,
      bookTitle: bookTitle || content.workspaceTitle,
      coverImage: resolvedBookCoverImage,
      chapterTitle: currentChapterDisplayTitle || content.importedTextTitle,
      status,
      progressPercent: progressState.progressPercent,
      progressText: progressState.progressText,
      elapsedSeconds: progressState.elapsedSeconds,
      totalSeconds: progressState.totalSeconds
    });
  }, [
    onPlayerStateChange,
    blocks.length,
    isPlaying,
    isPaused,
    playbackPhase,
    playButtonLabel,
    bookTitle,
    resolvedBookCoverImage,
    content.workspaceTitle,
    content.importedTextTitle,
    currentChapterDisplayTitle,
    status,
    progressState
  ]);

  useEffect(() => {
    if (!isSeekInteracting) {
      setSeekPreviewPercent(progressPct || 0);
    }
  }, [isSeekInteracting, progressPct]);

  useEffect(() => {
    if (!onUiStateChange) {
      return;
    }

    onUiStateChange({
      book: currentBook || null,
      shelfBooks: orderedSavedBooks.slice(0, MAX_SAVED_BOOKS).map((book) => ({
        id: book.id,
        title: book.bookTitle || "",
        coverImage: book.coverImage || ""
      })),
      chapterTitle: currentChapterDisplayTitle || "",
      progress: progressPct || 0,
      currentTime: miniPlayerCurrentTime || 0,
      duration: miniPlayerDuration || 0,
      isPlaying: !!isPlaying,
      canShowMiniPlayer: !!currentBook,
      resumePending: false,
      resumeLabel: "",
      resumeNote: ""
    });
  }, [
    onUiStateChange,
    currentBook,
    orderedSavedBooks,
    currentChapterDisplayTitle,
    progressPct,
    miniPlayerCurrentTime,
    miniPlayerDuration,
    isPlaying
  ]);

  useEffect(() => {
    if (!onRegisterActions) {
      return;
    }

    onRegisterActions({
      togglePlay: () => handlePlayToggleRef.current(),
      skipBack: () => skipBackActionRef.current(),
      skipForward: () => skipForwardActionRef.current(),
      seekToPercent: (nextPercent) => seekToPercentRef.current(nextPercent),
      openSavedBookAndPlay: (bookId) => handleOpenSavedBook(bookId, { shouldAutoplay: true }),
      openEcho: () => {}
    });
  }, [onRegisterActions]);

  useImperativeHandle(
    ref,
    () => ({
      togglePlayback: () => handlePlayToggleRef.current(),
      hasPlayableContent: blocks.length > 0,
      seekToPercent: (nextPercent) => seekToPercentRef.current(nextPercent),
      skipBackBySeconds: (seconds = 15) => handleSkipBySeconds(-Math.abs(seconds)),
      skipForwardBySeconds: (seconds = 15) => handleSkipBySeconds(Math.abs(seconds)),
      openSavedBookAndPlay: (bookId) => handleOpenSavedBook(bookId, { shouldAutoplay: true }),
      importExternalText: (payload) => importExternalSelection(payload),
      exportCurrentChapterMp3: (options = {}) => handleExportMp3(options),
      exportBookMp3: (options = {}) => handleExportMp3(options)
    }),
    [
      blocks.length,
      content.fileTypeTxt,
      content.importedTextTitle,
      content.statusAutoStarted,
      content.statusUnsupported,
      currentChapterDisplayTitle,
      preferredReadingLanguage
    ]
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <main
      className={`readerLabPage ${isVisible ? "" : "readerLabPageHidden"} ${
        isPureReadingMode ? "has-pure-reading" : ""
      }`}
      aria-hidden={!isVisible}
    >
      <section className="readerLabTopBar echo-topbar">
        <div className="echo-topbar__row echo-topbar__row--meta">
          {onClose ? (
            <button className="echo-back-btn" type="button" onClick={onClose}>
              {content.back_home}
            </button>
          ) : (
            <div className="echo-topbar__back-spacer" aria-hidden="true" />
          )}

          <div className="echo-topbar__hero">
            <div className="echo-topbar__cover-slot">
              {currentBook?.cover ? (
                <img
                  className="echo-book-meta__cover echo-book-meta__cover--hero"
                  src={currentBook.cover}
                  alt={currentBook.title || content.workspaceTitle}
                />
              ) : (
                <div className="echo-book-meta__cover echo-book-meta__cover--fallback echo-book-meta__cover--hero">
                  E
                </div>
              )}
            </div>

            <div className="echo-topbar__hero-main">
              <div className="echo-book-meta echo-book-meta--hero">
                <div className="echo-book-meta__text">
                  <div className="echo-book-meta__title">{currentBook?.title || content.workspaceTitle}</div>
                  <div className="echo-book-meta__chapter">
                    {currentChapterDisplayTitle || content.importedTextTitle}
                  </div>
                </div>
              </div>

              <div className="echo-topbar__meta-actions">
                {shouldShowAudioStatusPill ? (
                  <AudioStatusPill
                    mode={audioStatusMode}
                    hasServerKey={!audioConfig.personalKeyRequired && audioConfig.hasServerKey}
                    hasPersonalKey={Boolean(personalApiKey)}
                    onOpenSettings={handleOpenAudioSettings}
                  />
                ) : null}

                {renderUnderstandLanguageControl()}
                {renderTimerControl()}
                <button
                  className={`echoTopbarImportShortcut ${
                    isTopbarImportDragActive ? "is-drag-active" : ""
                  }`}
                  type="button"
                  onClick={handleTopbarImportShortcut}
                  onDragOver={handleTopbarImportDragOver}
                  onDragEnter={handleTopbarImportDragOver}
                  onDragLeave={handleTopbarImportDragLeave}
                  onDrop={handleTopbarImportDrop}
                  aria-label={content.importTxt}
                  title={content.importTxt}
                >
                  <span className="echoTopbarImportShortcut__icon" aria-hidden="true">
                    ➕
                  </span>
                </button>
                {currentBook && !isCompactLayout ? renderChapterListPanel({ placement: "topbar" }) : null}
              </div>
            </div>
          </div>
        </div>

        <section className="echoFeatureIntroCard" aria-label={content.featureIntroTitle}>
          <div className="echoFeatureIntroCard__eyebrow">{content.workspaceTitle}</div>
          <h3 className="echoFeatureIntroCard__title">{content.featureIntroTitle}</h3>
          <p className="echoFeatureIntroCard__body">{content.featureIntroBody}</p>
        </section>

        <div className="echo-topbar__row echo-topbar__row--controls">
          <div className="echo-player-main-controls">
            <div className="echo-player-main-controls__row">
              <button
                className="echo-player-secondary"
                onClick={() => handleSkipBySeconds(-15)}
                disabled={!blocks.length}
                aria-label={content.skipBackAria}
                title={content.skipBackAria}
              >
                {content.skipBackFifteen}
              </button>

              <button
                className="echo-player-primary"
                onClick={handlePlayToggle}
                disabled={!canTogglePlayback}
              >
                {playButtonLabel}
              </button>

              <button
                className="echo-player-secondary"
                onClick={() => handleSkipBySeconds(15)}
                disabled={!blocks.length}
                aria-label={content.skipForwardAria}
                title={content.skipForwardAria}
              >
                {content.skipForwardFifteen}
              </button>
            </div>

            <div className="echo-player-main-controls__aux">
              <button
                className={`echo-player-pure-reading ${isPureReadingMode ? "is-active" : ""}`}
                type="button"
                onClick={handlePureReadingToggle}
                disabled={!isPaused && !isPureReadingMode}
                title={
                  isPureReadingMode
                    ? content.pureReadingActiveHint
                    : content.pureReadingHint
                }
              >
                {isPureReadingMode ? content.pureReadingExit : content.pureReadingLabel}
              </button>

            </div>
          </div>

          <div
            className="echo-topbar__progress-wrap"
            ref={progressWrapRef}
          >
            <button
              className="echo-player-secondary echo-topbar__progress-navButton"
              type="button"
              onClick={handlePreviousChapter}
              disabled={!hasPreviousChapter}
              aria-label={content.previousChapter}
              title={content.previousChapter}
            >
              ‹
            </button>
            <div className="echo-topbar__progress-core">
              <div
                ref={progressInputRef}
                className={`echo-progress ${
                  !blocks.length || !seekableDuration ? "is-disabled" : ""
                } ${isSeekInteracting ? "is-seeking" : ""}`}
                style={{ "--echo-progress-percent": `${displayedProgressPct || 0}%` }}
                role="slider"
                tabIndex={!blocks.length || !seekableDuration ? -1 : 0}
                onPointerDown={handleSeekTrackPointerDown}
                onPointerMove={handleSeekTrackPointerMove}
                onPointerUp={handleSeekInteractionEnd}
                onPointerCancel={handleSeekInteractionEnd}
                onKeyDown={handleSeekTrackKeyDown}
                aria-label={content.progressLabel}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(displayedProgressPct || 0)}
                aria-valuetext={`${formatClock(
                  (displayedProgressPct / 100) * seekableDuration
                )} / ${formatClock(seekableDuration)}`}
              >
                <span
                  className="echo-progress__fill"
                  style={{ width: `${displayedProgressPct || 0}%` }}
                />
              </div>
              <div className="echo-topbar__progress-meta">
                <span>{formatClock(liveCurrentTime)}</span>
                <span>{formatClock(seekableDuration)}</span>
              </div>
            </div>
            <button
              className="echo-player-secondary echo-topbar__progress-navButton"
              type="button"
              onClick={handleNextChapter}
              disabled={!hasNextChapter}
              aria-label={hasNextChapter ? content.nextChapter : content.noNextChapter}
              title={hasNextChapter ? content.nextChapter : content.noNextChapter}
            >
              ›
            </button>
          </div>
        </div>
      </section>

      {isCompactLayout ? (
        <div className="echoQuickControlRow">
          {renderConversionQuickPanel()}
        </div>
      ) : null}

      {isCompactLayout ? (
        <div className="echoUtilityShortcutRow" aria-label="PageTone utility shortcuts">
          {currentBook ? (
            <button
              className="echoUtilityShortcut"
              type="button"
              onClick={() => openUtilitySheet("chapters")}
            >
              {content.chapterListTitle}
            </button>
          ) : null}
          <button
            className="echoUtilityShortcut echoUtilityShortcut--featured"
            type="button"
            onClick={() => openUtilitySheet("shelf")}
          >
            {content.shelfTitle}
          </button>
          <button
            className="echoUtilityShortcut"
            type="button"
            onClick={() => openUtilitySheet("audio")}
          >
            {content.audioSettingsTitle}
          </button>
          {blocks.length ? (
            <button
              className="echoUtilityShortcut"
              type="button"
              onClick={() => openUtilitySheet("queue")}
            >
              {content.queueTitle}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className={`echoWorkspaceLayout ${currentBook ? "has-book" : "is-empty"}`}>
        <section className={`echoReadingStage ${isPureReadingMode ? "is-pure-reading" : ""}`}>
          {currentBook ? (
            <div className="echoStagePanel echoStagePanelPrimary">
              <div className="echoStageHeader">
                <div>
                  <div className="echoStageEyebrow">{content.readingWindow}</div>
                  <h2 className="echoStageTitle">{currentChapterDisplayTitle || content.importedTextTitle}</h2>
                </div>
                <div className="echoStageMeta">
                  <span className="readerLabMetaChip">{currentChapterCountLabel}</span>
                  <span className="readerLabMetaChip">{currentBlockCountLabel}</span>
                  <span className="readerLabMetaChip">
                    {content.detectedLanguage}: {detectedLanguageLabel}
                  </span>
                  <span className="readerLabMetaChip">{understandingChipLabel}</span>
                  <span className="readerLabMetaChip">
                    {content.currentVoice}: {activeVoiceName || content.voiceAuto}
                  </span>
                </div>
              </div>

              <div className="readerLabStatusRow">
                <span className={`readerLabStatusDot ${isPlaying ? "playing" : ""}`} />
                <span>{status}</span>
              </div>

              {isPureReadingMode ? (
                <div className="echoPureReadingTopBar">
                  <button
                    className="echoPureReadingTopBar__exit"
                    type="button"
                    onClick={handlePureReadingToggle}
                  >
                    {content.pureReadingExit}
                  </button>
                </div>
              ) : null}

              <div className="echoStageWindowShell">
                <div
                  ref={readingWindowRef}
                  className={`echo-reading-window ${
                    isCompactLayout ? "is-native-scroll" : ""
                  } ${isReadingWindowDragging ? "is-dragging" : ""} ${
                    isPureReadingMode ? "is-pure-reading" : ""
                  }`}
                  onPointerDown={handleReadingWindowDragStart}
                  onPointerMove={handleReadingWindowDragMove}
                  onPointerUp={handleReadingWindowDragEnd}
                  onPointerCancel={handleReadingWindowDragEnd}
                  onWheel={handlePureReadingWheel}
                  onClick={handlePureReadingClick}
                >
                  <div
                    className="echo-reading-window__track"
                    style={
                      isCompactLayout || isPureReadingMode
                        ? undefined
                        : { transform: `translateY(${readingWindowTrackOffsetY}px)` }
                    }
                  >
                    {isPureReadingMode
                      ? null
                      : renderReadingWindowPane({
                          sourceText: readingWindowPreviousExcerpt,
                          sourceLanguage:
                            readingWindowPreviousBlock?.language ||
                            readingWindowCurrentBlock?.language ||
                            "en-US",
                          translatedText: previousTranslatedExcerpt,
                          translatedLanguage:
                            previousTranslatedBlock?.targetLanguage ||
                            readingWindowCurrentTranslatedBlock?.targetLanguage ||
                            "zh-CN",
                          preferTranslatedFirst: previousReadingWindowPrefersTranslatedTop,
                          tone: "faded"
                        })}

                    {renderReadingWindowPane({
                      sourceText: readingWindowCurrentExcerpt,
                      sourceLanguage: readingWindowCurrentBlock?.language || "en-US",
                      translatedText:
                        (!shouldLockPureReadingToSource && currentTranslatedExcerpt) ||
                        (!isPureReadingMode && currentTranslationPending
                          ? content.translationPlaybackPending
                          : ""),
                      translatedLanguage:
                        currentTranslationStatus?.targetLanguage || "zh-CN",
                      fallbackText: "Start reading or listening to begin.",
                      preferTranslatedFirst:
                        isPureReadingMode
                          ? !shouldLockPureReadingToSource && currentReadingWindowPrefersTranslatedTop
                          : currentReadingWindowPrefersTranslatedTop,
                      primaryLanguageLabel:
                        (isPureReadingMode
                          ? !shouldLockPureReadingToSource && currentReadingWindowPrefersTranslatedTop
                          : currentReadingWindowPrefersTranslatedTop)
                          ? getLanguageLabel(currentTranslationStatus?.targetLanguage || "zh-CN")
                          : getLanguageLabel(readingWindowCurrentBlock?.language || "en-US"),
                      hidePrimarySectionLabel: true,
                      primarySectionOffsetChars:
                        !isPureReadingMode &&
                        currentReadingWindowPrefersTranslatedTop &&
                        currentTranslatedExcerpt
                          ? currentTranslatedOffsetChars
                          : !isPureReadingMode
                          ? currentReadingWindowOffsetChars
                          : null,
                      paneRef: readingWindowCurrentPaneRef,
                      tone: "current"
                    })}

                    {isPureReadingMode
                      ? null
                      : renderReadingWindowPane({
                          sourceText: readingWindowNextExcerpt,
                          sourceLanguage:
                            readingWindowNextBlock?.language ||
                            readingWindowCurrentBlock?.language ||
                            "en-US",
                          translatedText: nextTranslatedExcerpt,
                          translatedLanguage:
                            nextTranslatedBlock?.targetLanguage ||
                            readingWindowCurrentTranslatedBlock?.targetLanguage ||
                            "zh-CN",
                          preferTranslatedFirst: nextReadingWindowPrefersTranslatedTop,
                          tone: "faded"
                        })}
                  </div>
                </div>
              </div>

              <div className="echoStageFooter">
                <div className="echoStageFooterProgress">
                  <span>{content.progressLabel}</span>
                  <strong>{progressState.progressText}</strong>
                </div>
                <p className="readerLabProgressNote">{content.progressNote}</p>
              </div>
            </div>
          ) : (
            <div className="echoStagePanel echoStagePanelEmpty">
              <div className="echoStageHeader">
                <div>
                  <h2 className="echoStageTitle">{content.dropzoneTitle}</h2>
                </div>
                <p className="echoStageLead">{content.sandboxIntro}</p>
              </div>

              {renderImportPanel({ prominent: true })}
            </div>
          )}

          {renderSourceEditor()}
        </section>

        {!isCompactLayout ? (
          <aside className="echoUtilityRail">
            <div className="echoConversionQuickDock">
              {renderConversionQuickPanel()}
            </div>
            {renderCurrentBookPanel()}
            {renderShelfPanel()}
            {currentBook ? renderImportPanel() : null}
            {renderApiKeyPanel()}
            {renderAudioSettingsPanel()}
            {renderQueuePanel()}
          </aside>
        ) : null}
      </div>

      <EchoBottomSheet
        open={Boolean(activeUtilitySheet)}
        title={utilitySheetTitle}
        closeLabel={content.closePanel}
        onClose={closeUtilitySheet}
      >
        {activeUtilitySheet === "chapters" ? renderChapterListPanel({ inSheet: true }) : null}
        {activeUtilitySheet === "audio" ? (
          <>
            {renderApiKeyPanel({ inSheet: true })}
            {renderAudioSettingsPanel({ inSheet: true })}
          </>
        ) : null}
        {activeUtilitySheet === "shelf" ? renderShelfPanel({ inSheet: true }) : null}
        {activeUtilitySheet === "queue" ? renderQueuePanel({ inSheet: true }) : null}
        {activeUtilitySheet === "import" ? renderImportPanel({ inSheet: true }) : null}
      </EchoBottomSheet>

      <input
        ref={fileInputRef}
        className="readerLabHiddenInput"
        type="file"
        accept={IMPORTABLE_FILE_ACCEPT}
        onChange={handleFileChange}
      />

      <audio ref={audioRef} hidden playsInline preload="auto" />
    </main>
  );
});

export default ReaderLab;
